/* global PowerPoint, Office, console, fetch */

import {
  ParagraphContent,
  ParagraphOp,
  ParagraphSet,
  ShapeContent,
  SlideContent,
  SlideRef,
  PresentationContent,
  PresentationModification,
  SlideModification,
  ShapeModification,
  TableData,
  TableCellData,
  TableCellProperties,
  TableClearOp,
  TableLayoutSpec,
  TableMergedArea,
  TableStructureOp,
  TableStyleSettingsData,
  TableStyleTarget,
  TableStyleUpdate,
  ApplyResultSummary,
  CitationManifest,
  SlideCitationBinding,
  ApplyReasonCode,
  ApplyDiagnostic,
  CompositeLayout,
  CompositeLayoutType,
  CompositeLayoutItem,
  Snippet,
  DeckLayoutCatalog,
  DeckLayoutCatalogEntry,
  DeckLayoutPlaceholder,
} from "../types";
import { generateCompositeLayoutShapes } from "./slideLayoutBlueprints";
import { resolveSnippetPolicy } from "./ai/snippetPolicy";
import { CITI_SNIPPET_STYLE_PROFILE, DEFAULT_SNIPPET_STYLE_PROFILE } from "./ai/snippetStyle";
import type { BrandId } from "./ai/brandRegistry";
import { expandInSlideChartsForOfficeJs } from "./chartTwoPassTranslator";
import { buildApiUrl, getApiBaseUrl } from "../config";
import { hasAnyImagePayload, normalizeBase64Payload, parseBase64DataUri } from "../utils/imagePayload";
import {
  buildEmptyTableShellRows,
  buildTableTextUpdatesFromRows,
  mergeTableTextUpdates,
} from "../utils/tableData";
import { guardedPowerPointRun, isOfficeJsBypassed } from "./officeJsGuard";
import {
  classifyApplyResult,
  countSuccessfulOps,
  createActionCounts,
  recordError,
  recordSkipped,
} from "./applyResultSummary";
import { persistCitationRefs } from "../citations/be/persistCitationRefs";
import {
  stripModificationsInPlace,
  type SlideCitationRefs,
} from "../citations/stripAndExtract";
import {
  addImageFromBase64ViaIntents,
  addSlideViaIntents,
  addSlideTagsViaIntents,
  applyModificationsViaIntents,
  createBlankSlideAtEndViaIntents,
  deleteSlidesViaIntents,
  exportPresentationAsBase64ViaIntents,
  exportSlideAsImageViaIntents,
  extractDeckLayoutCatalogViaIntents,
  extractDesignMetadataViaIntents,
  extractPromptDesignContextViaIntents,
  extractPresentationContentViaIntents,
  getResolvedSlideBoundsViaIntents,
  getActivePresentationRefViaIntents,
  getSelectedShapesViaIntents,
  getSelectedSlideRefViaIntents,
  getSlideCountViaIntents,
  insertSlidesFromBase64ViaIntents,
  listSlidesViaIntents,
  resolveSlideIndexByIdViaIntents,
  setSelectedSlidesViaIntents,
  type FactSetPresentationRef,
  type RunOfficeExtractorFilters,
  type RunOfficeExtractorShapeFilter,
  type RunOfficeExtractorSlideRangeFilter,
  type SlideBounds,
  streamSlideThumbnailsViaIntents,
  extractRawDesignJsonViaIntents,
  extractSlideBackgroundsViaIntents,
  openPresentationFromBase64ViaIntents,
} from "./factset/intentBridgeService";
import { shouldUseFactSetBridge } from "./presentationHost";
import { CITI_COLORS, CITI_POSITIONS, CITI_THEME_COLORS } from "./ai/citiStyleConfig";
import { computeContextSlideWindow } from "../utils/contextWindow";

export { classifyApplyResult, countSuccessfulOps };

const MARGIN = 20;
const DEFAULT_SLIDE_WIDTH = 960;
const DEFAULT_SLIDE_HEIGHT = 540;

let inferredSlideWidth = DEFAULT_SLIDE_WIDTH;
let inferredSlideHeight = DEFAULT_SLIDE_HEIGHT;
let dimensionsResolved = false;
let lastKnownSelectedSlideId: string | null = null;

let lastKnownPresentationContent: PresentationContent = {
  slides: [],
  selectedSlideIndex: 0,
  slideWidth: DEFAULT_SLIDE_WIDTH,
  slideHeight: DEFAULT_SLIDE_HEIGHT,
};

interface ApplyModificationsOptions {
  onPreparedIntentBatch?: (operations: PresentationModification) => void | Promise<void>;
  presentationRef?: FactSetPresentationRef | null;
  // Wave 4 fix 3: when the caller already has fresh slide dimensions (e.g.
  // the chat extract pulled them via `pageSetup: true`), pass them through
  // so the FactSet branch can skip the `getResolvedSlideBoundsViaIntents`
  // round-trip — which otherwise can fall through to an unfiltered
  // `RunOfficeExtractor` fallback on hosts that don't surface bounds via
  // Office pageSetup or `GetActivePresentationContext`.
  preferredBounds?: SlideBounds;
  /** Active brand for snippet expansion policy/profile. */
  brandId?: BrandId | null;
}

function isPropertyNotLoadedError(error: unknown): boolean {
  const msg =
    error instanceof Error
      ? error.message || ""
      : typeof (error as { message?: unknown })?.message === "string"
        ? ((error as { message?: string }).message ?? "")
        : "";
  return (
    msg.includes("is not available") ||
    msg.includes("PropertyNotLoaded") ||
    msg.includes("not been loaded")
  );
}

async function readSlideDimensionsInIsolatedContext(
  logPrefix = "[SLIDE]"
): Promise<{ width: number; height: number }> {
  if (dimensionsResolved) {
    return { width: inferredSlideWidth, height: inferredSlideHeight };
  }
  try {
    return await guardedPowerPointRun(async (context) => {
      const pageSetup = context.presentation.pageSetup;
      pageSetup.load("slideWidth,slideHeight");
      await context.sync();
      if (pageSetup.slideWidth && pageSetup.slideHeight) {
        inferredSlideWidth = pageSetup.slideWidth;
        inferredSlideHeight = pageSetup.slideHeight;
        dimensionsResolved = true;
        console.log(
          `${logPrefix} Actual slide dimensions: ${inferredSlideWidth}×${inferredSlideHeight} points`
        );
        return { width: inferredSlideWidth, height: inferredSlideHeight };
      }
      return { width: inferredSlideWidth, height: inferredSlideHeight };
    }, { width: inferredSlideWidth, height: inferredSlideHeight });
  } catch {
    console.log(
      `${logPrefix} Could not read pageSetup (requires PowerPointApi 1.10), using cached ${inferredSlideWidth}×${inferredSlideHeight}`
    );
    return { width: inferredSlideWidth, height: inferredSlideHeight };
  }
}

async function readSelectedSlideIdInIsolatedContext(
  failureLogMessage = "Could not get selected slide, defaulting to first slide"
): Promise<string | null> {
  try {
    const result = await guardedPowerPointRun(async (context) => {
      const selectedSlides = context.presentation.getSelectedSlides();
      selectedSlides.load("items/id");
      await context.sync();
      if (selectedSlides.items.length > 0) {
        return selectedSlides.items[0].id;
      }
      return null;
    }, null);
    if (result !== null) {
      lastKnownSelectedSlideId = result;
    }
    return result ?? lastKnownSelectedSlideId;
  } catch {
    if (lastKnownSelectedSlideId) {
      return lastKnownSelectedSlideId;
    }
    console.log(failureLogMessage);
    return null;
  }
}

function roundPt(value: number): number {
  return Math.round(value * 10) / 10;
}

interface ParagraphRange {
  index: number;
  start: number;
  length: number;
  applyLength: number;
  text: string;
}

function getParagraphRanges(text: string): ParagraphRange[] {
  if (!text) {
    return [{ index: 0, start: 0, length: 0, applyLength: 0, text: "" }];
  }

  const ranges: ParagraphRange[] = [];
  const lineBreakRegex = /\r\n|\r|\n/g;
  let start = 0;
  let index = 0;
  let match: RegExpExecArray | null;

  while ((match = lineBreakRegex.exec(text)) !== null) {
    const end = match.index;
    const delimiterLength = match[0].length;
    ranges.push({
      index,
      start,
      length: end - start,
      applyLength: end - start + delimiterLength,
      text: text.slice(start, end),
    });
    start = match.index + match[0].length;
    index += 1;
  }

  ranges.push({
    index,
    start,
    length: text.length - start,
    applyLength: text.length - start,
    text: text.slice(start),
  });

  return ranges;
}

function buildParagraphContentSnapshot(text: string | undefined): ParagraphContent[] | undefined {
  if (text === undefined) {
    return undefined;
  }
  return getParagraphRanges(text).map((paragraph) => ({
    index: paragraph.index,
    text: paragraph.text,
  }));
}

function applyBoundsToShape(
  shapeMod: { left?: number; top?: number; width?: number; height?: number },
  _slideWidth: number = inferredSlideWidth,
  _slideHeight: number = inferredSlideHeight
): { left?: number; top?: number; width?: number; height?: number } {
  const result = { ...shapeMod };

  if (result.left !== undefined) result.left = roundPt(result.left);
  if (result.top !== undefined) result.top = roundPt(result.top);
  if (result.width !== undefined) result.width = roundPt(result.width);
  if (result.height !== undefined) result.height = roundPt(result.height);

  return result;
}

// ============================================================================

/**
 * Get selected table data using the Common Office API
 * This works when user has a table selected
 */
export async function getSelectedTableData(): Promise<TableCellData | null> {
  if (shouldUseFactSetBridge()) {
    try {
      const selectedShapes = await getSelectedShapesViaIntents({ timeoutMs: 12_000 });
      if (selectedShapes.shapes.length === 0) {
        return null;
      }

      const selectedSlideRef = await getSelectedSlideRefViaIntents();
      const normalizeSlideId = (raw: string): string => String(raw || "").split("#")[0];
      const includeShapes: RunOfficeExtractorShapeFilter[] = [];
      for (const shape of selectedShapes.shapes) {
        const id = String(shape.id || "").trim();
        const slideId = String(shape.slideId || selectedSlideRef.id || "").trim();
        if (!id || !slideId) {
          continue;
        }
        const normalizedName = String(shape.name || "").trim();
        includeShapes.push(
          normalizedName ? { id, slideId, name: normalizedName } : { id, slideId }
        );
      }

      if (includeShapes.length === 0) {
        return null;
      }

      // Wave 2 site 4: ask the host for table-cell content plus every
      // round-trippable style field. This fallback can replace the selected
      // tableData sent to the model, so it must retain borders, solid fills,
      // text-frame/paragraph alignment, and run-level supported fonts.
      const scopedContent = await extractPresentationContentViaIntents(undefined, {
        filters: {
          slides: {
            shapes: {
              refs: includeShapes.map((s) => ({
                slideId: s.slideId,
                shapeId: s.id,
                ...(s.name ? { name: s.name } : {}),
              })),
              textFrame: false,
              fill: false,
              border: false,
              table: {
                cell: {
                  borders: true,
                  shape: {
                    fill: true,
                    textFrame: {
                      text: true,
                      textFrameFormat: true,
                      paragraphs: {
                        text: true,
                        paragraphFormat: true,
                        runs: {
                          text: true,
                          runFormat: true,
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
      for (const requestedShape of includeShapes) {
        const matchingSlide = scopedContent.slides.find(
          (slide) => normalizeSlideId(slide.id) === normalizeSlideId(requestedShape.slideId)
        );
        const matchingShape = matchingSlide?.shapes.find(
          (shape) => String(shape.id || "").trim() === requestedShape.id
        );
        if (matchingShape?.tableData && matchingShape.tableData.rows.length > 0) {
          return matchingShape.tableData;
        }
      }

      for (const slide of scopedContent.slides) {
        const firstTable = slide.shapes.find(
          (shape) => shape.tableData && shape.tableData.rows.length > 0
        );
        if (firstTable?.tableData) {
          return firstTable.tableData;
        }
      }

      return null;
    } catch (error) {
      console.warn("[FACTSET] getSelectedTableData scoped extractor fallback failed:", error);
      return null;
    }
  }

  if (isOfficeJsBypassed()) {
    console.log("[OfficeJS NOP] getSelectedTableData bypassed");
    return null;
  }
  return new Promise((resolve) => {
    // Try Matrix coercion first (works for tables)
    Office.context.document.getSelectedDataAsync(Office.CoercionType.Matrix, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        const data = result.value as string[][];
        if (data && data.length > 0) {
          console.log("Got table data via Common API (Matrix):", data);
          const semanticHints = buildTableSemanticHints(data);
          resolve({
            rowCount: data.length,
            colCount: data[0]?.length || 0,
            rows: data,
            semanticHints,
          });
          return;
        }
      }
      console.log("Matrix coercion failed:", result.error?.message);

      // Try Table coercion as fallback
      Office.context.document.getSelectedDataAsync(Office.CoercionType.Table, (tableResult) => {
        if (tableResult.status === Office.AsyncResultStatus.Succeeded) {
          const tableData = tableResult.value as Office.TableData;
          if (tableData && tableData.rows && tableData.rows.length > 0) {
            console.log("Got table data via Common API (Table):", tableData);
            const rows = tableData.rows.map((row) =>
              (row as unknown[]).map((cell) => String(cell ?? ""))
            );
            const semanticHints = buildTableSemanticHints(rows);
            resolve({
              rowCount: rows.length,
              colCount: rows[0]?.length || 0,
              rows,
              semanticHints,
            });
            return;
          }
        }
        console.log("Table coercion also failed:", tableResult.error?.message);
        resolve(null);
      });
    });
  });
}

interface FetchedImagePayload {
  base64: string;
  /** Present when the response reported a usable Content-Type. */
  mimeType?: string;
}

async function imageUrlToBase64(url: string): Promise<FetchedImagePayload | null> {
  try {
    const apiBase = getApiBaseUrl().replace(/\/+$/, "");
    const isHttp = url.startsWith("http://") || url.startsWith("https://");
    const isOurApiAbsolute = Boolean(apiBase && isHttp && (url === apiBase || url.startsWith(`${apiBase}/`)));
    const isOurApiRelative = url.startsWith("/api/");

    let fetchUrl: string;
    let fetchInit: RequestInit | undefined;

    if (isOurApiRelative) {
      fetchUrl = buildApiUrl(url);
      fetchInit = { credentials: "include" };
    } else if (isOurApiAbsolute) {
      fetchUrl = url;
      fetchInit = { credentials: "include" };
    } else if (isHttp) {
      fetchUrl = buildApiUrl(`/api/image-proxy?url=${encodeURIComponent(url)}`);
      fetchInit = { credentials: "include" };
    } else {
      fetchUrl = url;
      fetchInit = undefined;
    }

    const isProxiedExternal = isHttp && !isOurApiAbsolute;

    let response: Response;
    const t0 = Date.now();
    try {
      console.log(`[IMAGE] Fetching${isProxiedExternal ? " (proxied)" : ""}: ${url.slice(0, 80)}`);
      response = await fetch(fetchUrl, fetchInit);
    } catch (fetchErr) {
      const msg = fetchErr instanceof Error ? fetchErr.message : String(fetchErr);
      if (msg === "Load failed" || msg.includes("network")) {
        console.log(`[IMAGE] Retry after ${msg}`);
        await new Promise((r) => setTimeout(r, 1000));
        response = await fetch(fetchUrl, fetchInit);
      } else {
        throw fetchErr;
      }
    }
    const elapsed = Date.now() - t0;
    if (!response.ok) {
      console.error(`[IMAGE] HTTP ${response.status} (${elapsed}ms): ${url.slice(0, 80)}`);
      return null;
    }

    const contentType = response.headers.get("content-type");
    const arrayBuf = await response.arrayBuffer();
    const bytes = new Uint8Array(arrayBuf);
    console.log(
      `[IMAGE] OK: ${contentType || "unknown"}, ${(bytes.length / 1024).toFixed(1)}KB, ${elapsed}ms`
    );

    if (bytes.length === 0) {
      console.error(`[IMAGE] Empty response body`);
      return null;
    }

    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64Data = btoa(binary);
    console.log(`[IMAGE] Base64: ${(base64Data.length / 1024).toFixed(1)}KB chars`);
    const normalizedContentType = contentType ? normalizeImageMimeType(contentType) : undefined;
    return {
      base64: base64Data,
      ...(normalizedContentType?.startsWith("image/")
        ? { mimeType: normalizedContentType }
        : {}),
    };
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    console.error(`[IMAGE] Failed: ${msg} — ${url.slice(0, 80)}`);
    return null;
  }
}

const DEFAULT_IMAGE_MIME_TYPE = "image/png";
const OFFICEJS_DISABLE_IMAGE_URL_FALLBACK = true;
const INLINE_IMAGE_PROBE_TIMEOUT_MS = 3000;

interface InlineImagePayload {
  base64: string;
  mimeType: string;
}

function normalizeImageMimeType(maybeMimeType?: string): string {
  const raw = String(maybeMimeType || "")
    .trim()
    .toLowerCase()
    .split(";")[0];
  if (!raw) return DEFAULT_IMAGE_MIME_TYPE;
  if (raw.includes("/")) return raw;
  if (raw === "jpg" || raw === "jpeg") return "image/jpeg";
  if (raw === "png" || raw === "gif" || raw === "webp" || raw === "bmp" || raw === "svg+xml") {
    return `image/${raw}`;
  }
  return DEFAULT_IMAGE_MIME_TYPE;
}

function parseImageDataUri(rawDataUri: string | undefined): InlineImagePayload | null {
  const parsed = parseBase64DataUri(rawDataUri);
  if (!parsed) return null;
  return {
    base64: parsed.base64,
    mimeType: normalizeImageMimeType(parsed.mimeType),
  };
}

function buildImageDataUri(base64: string, mimeType: string): string {
  return `data:${normalizeImageMimeType(mimeType)};base64,${base64}`;
}

export function isLogoImageUrl(imageUrl?: string): boolean {
  const normalized = String(imageUrl || "").toLowerCase();
  if (!normalized) return false;
  return (
    normalized.includes("/api/logo/") ||
    normalized.includes("/api/finster/company/logo/") ||
    normalized.includes("logos.logointern.com/") ||
    normalized.includes("logo.dev/")
  );
}

async function probeInlineImageDimensions(
  base64: string,
  mimeType?: string
): Promise<{ width: number; height: number } | null> {
  try {
    if (!base64 || typeof Image === "undefined") return null;
    const dataUri = buildImageDataUri(base64, normalizeImageMimeType(mimeType));
    return await new Promise<{ width: number; height: number }>((resolve, reject) => {
      const image = new Image();
      let settled = false;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      const cleanup = () => {
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        image.onload = null;
        image.onerror = null;
      };
      const settleSuccess = (dimensions: { width: number; height: number }) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(dimensions);
      };
      const settleFailure = (error: Error) => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(error);
      };

      image.onload = () => {
        const width = image.naturalWidth || image.width || 0;
        const height = image.naturalHeight || image.height || 0;
        if (width > 0 && height > 0) settleSuccess({ width, height });
        else settleFailure(new Error("image dimensions unavailable"));
      };
      image.onerror = () => settleFailure(new Error("image decode failed"));
      timeoutId = setTimeout(() => {
        settleFailure(new Error("image decode timed out"));
      }, INLINE_IMAGE_PROBE_TIMEOUT_MS);
      image.src = dataUri;
    });
  } catch {
    return null;
  }
}

/**
 * Fit a logo shape's box to the image's intrinsic aspect ratio (contain).
 * The upstream logo service returns non-square marks (e.g. the Tesla wordmark
 * is 400x52). The model emits a width/height box blind to the true ratio, and
 * the runtime otherwise applies that box verbatim — stretching the logo. This
 * shrinks the requested box to preserve the intrinsic ratio without distortion.
 */
function fitShapeToImageAspectRatio(
  shapeMod: ShapeModification,
  intrinsic: { width: number; height: number }
): { width: number; height: number } | null {
  const requestedWidth = typeof shapeMod.width === "number" ? shapeMod.width : null;
  const requestedHeight = typeof shapeMod.height === "number" ? shapeMod.height : null;
  if (
    !requestedWidth ||
    !requestedHeight ||
    requestedWidth <= 0 ||
    requestedHeight <= 0 ||
    intrinsic.width <= 0 ||
    intrinsic.height <= 0
  ) {
    return null;
  }
  const intrinsicRatio = intrinsic.width / intrinsic.height;
  const heightIfFitWidth = requestedWidth / intrinsicRatio;
  let width: number;
  let height: number;
  if (heightIfFitWidth <= requestedHeight) {
    width = requestedWidth;
    height = heightIfFitWidth;
  } else {
    height = requestedHeight;
    width = requestedHeight * intrinsicRatio;
  }
  return { width, height };
}

function isImageShapeModification(shapeMod: ShapeModification): boolean {
  return hasAnyImagePayload(shapeMod);
}

function shouldForceSquareLogoImageUrl(imageUrl?: string): boolean {
  const normalized = String(imageUrl || "").toLowerCase();
  if (!normalized) return false;
  return normalized.includes("logo.dev/");
}

function shouldFitLogoImageAspectRatio(imageUrl?: string): boolean {
  return isLogoImageUrl(imageUrl) && !shouldForceSquareLogoImageUrl(imageUrl);
}

async function fitLogoShapeToInlineImageAspectRatio(
  shapeMod: ShapeModification,
  base64: string,
  mimeType?: string
): Promise<void> {
  if (!shouldFitLogoImageAspectRatio(shapeMod.imageUrl)) return;
  const intrinsic = await probeInlineImageDimensions(base64, mimeType);
  const fitted = intrinsic ? fitShapeToImageAspectRatio(shapeMod, intrinsic) : null;
  if (fitted) {
    shapeMod.width = fitted.width;
    shapeMod.height = fitted.height;
  }
}

function getInlineImagePayload(shapeMod: ShapeModification): InlineImagePayload | null {
  const fromDataUri = parseImageDataUri(shapeMod.imageDataUri);
  if (fromDataUri) return fromDataUri;
  const base64 = normalizeBase64Payload(shapeMod.imageBase64);
  if (!base64) return null;
  return {
    base64,
    mimeType: normalizeImageMimeType(shapeMod.imageMimeType),
  };
}

function setInlineImagePayload(shapeMod: ShapeModification, payload: InlineImagePayload): void {
  shapeMod.imageBase64 = payload.base64;
  shapeMod.imageMimeType = payload.mimeType;
  shapeMod.imageDataUri = buildImageDataUri(payload.base64, payload.mimeType);
}

const IMAGE_HYDRATE_CONCURRENCY = 12;

async function mapWithConcurrency<TItem, TResult>(
  items: TItem[],
  concurrency: number,
  mapper: (item: TItem, index: number) => Promise<TResult>
): Promise<TResult[]> {
  const results: TResult[] = new Array(items.length);
  let nextIndex = 0;
  const workerCount = Math.min(Math.max(1, concurrency), Math.max(items.length, 1));
  if (items.length === 0) return results;

  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      while (nextIndex < items.length) {
        const currentIndex = nextIndex;
        nextIndex += 1;
        results[currentIndex] = await mapper(items[currentIndex], currentIndex);
      }
    })
  );

  return results;
}

async function hydrateImagePayloads(
  modifications: PresentationModification,
  logMsg: Logger
): Promise<void> {
  const shapesNeedingUrl: Array<{ shapeMod: ShapeModification; imageUrl: string }> = [];

  for (const slideMod of modifications.slides || []) {
    for (const shapeMod of slideMod.shapes || []) {
      if (shapeMod.action !== "create" || !isImageShapeModification(shapeMod)) {
        continue;
      }

      const inlinePayload = getInlineImagePayload(shapeMod);
      if (inlinePayload) {
        setInlineImagePayload(shapeMod, inlinePayload);
        continue;
      }

      const imageUrl = typeof shapeMod.imageUrl === "string" ? shapeMod.imageUrl.trim() : "";
      if (!imageUrl) {
        continue;
      }
      shapesNeedingUrl.push({ shapeMod, imageUrl });
    }
  }

  if (shapesNeedingUrl.length === 0) {
    return;
  }

  const uniqueUrls = Array.from(new Set(shapesNeedingUrl.map((entry) => entry.imageUrl)));
  const prefetchedByUrl: Map<string, FetchedImagePayload | null> = new Map();
  const mimeTypeByUrl: Map<string, string> = new Map();
  for (const { shapeMod, imageUrl } of shapesNeedingUrl) {
    if (!mimeTypeByUrl.has(imageUrl)) {
      mimeTypeByUrl.set(imageUrl, normalizeImageMimeType(shapeMod.imageMimeType));
    }
  }

  logMsg(
    `🖼️ Pre-fetching ${uniqueUrls.length} unique image URL(s) (concurrency ${IMAGE_HYDRATE_CONCURRENCY})...`
  );
  await mapWithConcurrency(uniqueUrls, IMAGE_HYDRATE_CONCURRENCY, async (imageUrl) => {
    const fetched = await imageUrlToBase64(imageUrl);
    prefetchedByUrl.set(imageUrl, fetched);
    if (fetched) {
      logMsg(
        `🖼️ Got base64 for ${imageUrl.slice(0, 80)} (${(fetched.base64.length / 1024).toFixed(1)}KB)`
      );
    } else {
      logMsg(`⚠️ Image pre-fetch failed: ${imageUrl.slice(0, 80)}`);
    }
    return fetched;
  });

  const logoUrlsToProbe = uniqueUrls.filter(
    (imageUrl) => Boolean(prefetchedByUrl.get(imageUrl)) && shouldFitLogoImageAspectRatio(imageUrl)
  );
  const dimensionsByUrl: Map<string, { width: number; height: number } | null> = new Map();
  await mapWithConcurrency(logoUrlsToProbe, IMAGE_HYDRATE_CONCURRENCY, async (imageUrl) => {
    const fetched = prefetchedByUrl.get(imageUrl) ?? null;
    if (!fetched) {
      dimensionsByUrl.set(imageUrl, null);
      return null;
    }
    // Prefer the response MIME (actual bytes) over a hardcoded PNG default; fall
    // back to the per-shape MIME when the fetch did not report a usable type.
    const mimeType = fetched.mimeType || mimeTypeByUrl.get(imageUrl) || DEFAULT_IMAGE_MIME_TYPE;
    const intrinsic = await probeInlineImageDimensions(fetched.base64, mimeType);
    dimensionsByUrl.set(imageUrl, intrinsic);
    return intrinsic;
  });

  for (const { shapeMod, imageUrl } of shapesNeedingUrl) {
    const fetched = prefetchedByUrl.get(imageUrl) ?? null;
    if (!fetched) {
      continue;
    }
    const payload = {
      base64: fetched.base64,
      mimeType:
        fetched.mimeType ||
        normalizeImageMimeType(shapeMod.imageMimeType),
    };
    setInlineImagePayload(shapeMod, payload);

    // Preserve logo aspect ratio: fit the requested box to the image's
    // intrinsic ratio so non-square wordmarks are not stretched.
    if (shouldFitLogoImageAspectRatio(imageUrl)) {
      const intrinsic = dimensionsByUrl.get(imageUrl) ?? null;
      const fitted = intrinsic ? fitShapeToImageAspectRatio(shapeMod, intrinsic) : null;
      if (fitted) {
        shapeMod.width = fitted.width;
        shapeMod.height = fitted.height;
      }
    }
  }
}

/** Test-only helpers for image hydrate concurrency. */
export const _imageHydrateTest = {
  mapWithConcurrency,
  IMAGE_HYDRATE_CONCURRENCY,
};

type Logger = (message: string) => void;

const defaultLogger: Logger = (msg) => console.log(msg);

const shapeActionErrorCode: Record<ShapeModification["action"], ApplyReasonCode> = {
  create: "SHAPE_CREATE_FAILED",
  modify: "SHAPE_MODIFY_FAILED",
  delete: "SHAPE_DELETE_FAILED",
};

function hasInsertSlideActions(modifications: PresentationModification): boolean {
  return (modifications.slides || []).some((slide) => slide.action === "insert");
}

function hasDuplicateSlideActions(modifications: PresentationModification): boolean {
  return (modifications.slides || []).some((slide) => slide.action === "duplicate");
}

function withInsertSlidesUnsupportedDiagnostics(
  modifications: PresentationModification
): PresentationModification {
  const diagnostics: ApplyDiagnostic[] = [...(modifications.diagnostics || [])];
  const supportedSlides: PresentationModification["slides"] = [];

  for (const slide of modifications.slides || []) {
    if (slide.action !== "insert") {
      supportedSlides.push(slide);
      continue;
    }
    diagnostics.push({
      code: "SLIDE_INSERT_UNSUPPORTED",
      category: "apply_error",
      message: "Slide insertion via action=\"insert\" is only supported in FactSet host mode.",
      slideIndex: slide.index,
      slideId: slide.id,
      action: "insert",
    });
  }

  return {
    ...modifications,
    slides: supportedSlides,
    diagnostics,
  };
}

/** Normalize a placeholder/shape name for deterministic matching. */
function normalizePlaceholderMatchKey(value: string | null | undefined): string {
  if (typeof value !== "string") return "";
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Digit-insensitive fallback key: "content placeholder 3" -> "content placeholder".
 * PowerPoint placeholder names sometimes differ only by a trailing ordinal
 * between a layout definition and a materialized slide instance.
 */
function stripTrailingPlaceholderOrdinal(value: string): string {
  return value.replace(/\s*\d+$/, "").trim();
}

function normalizePlaceholderTypeToken(value: string | null | undefined): string {
  if (typeof value !== "string") return "";
  return value
    .trim()
    .toLowerCase()
    .replace(/^placeholder/, "")
    .replace(/[^a-z0-9]+/g, "");
}

function placeholderTypeGroup(value: string | null | undefined): "title" | "body" | "object" | "other" {
  const token = normalizePlaceholderTypeToken(value);
  if (!token) return "other";
  if (token.includes("title")) return "title";
  if (
    token.includes("object") ||
    token.includes("content") ||
    token.includes("chart") ||
    token.includes("table") ||
    token.includes("picture") ||
    token.includes("media")
  ) {
    return "object";
  }
  if (token.includes("body") || token.includes("subtitle") || token.includes("text")) {
    return "body";
  }
  return "other";
}

function arePlaceholderTypesCompatible(
  catalogType: string | undefined,
  realType: string | undefined
): boolean {
  const catalogGroup = placeholderTypeGroup(catalogType);
  const realGroup = placeholderTypeGroup(realType);
  if (catalogGroup === "other" || realGroup === "other") return true;
  if (catalogGroup === realGroup) return true;
  // Text and object/content placeholders often overlap in PowerPoint layouts.
  return (
    (catalogGroup === "body" && realGroup === "object") ||
    (catalogGroup === "object" && realGroup === "body")
  );
}

function isRealPlaceholderShape(shape: ShapeContent): boolean {
  if (typeof shape.placeholderType === "string" && shape.placeholderType.trim().length > 0) {
    return true;
  }
  const name = normalizePlaceholderMatchKey(shape.name);
  return name.includes("placeholder") || name.includes("title");
}

function hasUsableGeometry<T extends {
  left?: number;
  top?: number;
  width?: number;
  height?: number;
}>(shape: T): shape is T & { left: number; top: number; width: number; height: number } {
  return (
    typeof shape.left === "number" &&
    Number.isFinite(shape.left) &&
    typeof shape.top === "number" &&
    Number.isFinite(shape.top) &&
    typeof shape.width === "number" &&
    Number.isFinite(shape.width) &&
    typeof shape.height === "number" &&
    Number.isFinite(shape.height)
  );
}

function normalizeSlideIdForMatch(id: string | number | null | undefined): string {
  return String(id ?? "")
    .trim()
    .replace(/^#/, "");
}

function findDeckLayoutEntryForInsertedSlide(
  catalog: DeckLayoutCatalog | null | undefined,
  slide: SlideModification
): DeckLayoutCatalogEntry | undefined {
  const layoutName = normalizePlaceholderMatchKey(slide.layoutName);
  const slideMasterName = normalizePlaceholderMatchKey(slide.slideMasterName);
  if (!catalog?.layouts?.length || !layoutName) return undefined;
  const matches = catalog.layouts.filter((entry) => {
    const names = [
      normalizePlaceholderMatchKey(entry.layoutName),
      normalizePlaceholderMatchKey(entry.matchingName),
    ].filter(Boolean);
    return names.includes(layoutName);
  });
  if (matches.length <= 1 || !slideMasterName) return matches[0];
  return (
    matches.find(
      (entry) => normalizePlaceholderMatchKey(entry.slideMasterName) === slideMasterName
    ) ?? matches[0]
  );
}

/**
 * A shape carries a "structured create payload" when it builds a non-text
 * element (table/chart/image/geometric). Such shapes can't be applied as an
 * in-place text fill ("modify"); instead they keep their action but are
 * positioned into the resolved placeholder's bounds.
 */
function shapeHasStructuredCreatePayload(shape: ShapeModification): boolean {
  return Boolean(
    shape.table ||
      shape.chart ||
      shape.chartEdit ||
      shape.geometricType ||
      shape.imageUrl ||
      shape.imageDataUri ||
      shape.imageBase64
  );
}

function sortPlaceholdersByVisualOrder<T extends { top?: number; left?: number; name?: string }>(
  placeholders: T[]
): T[] {
  return [...placeholders].sort((a, b) => {
    const topA = typeof a.top === "number" ? a.top : Number.POSITIVE_INFINITY;
    const topB = typeof b.top === "number" ? b.top : Number.POSITIVE_INFINITY;
    if (Math.abs(topA - topB) > 1) return topA - topB;
    const leftA = typeof a.left === "number" ? a.left : Number.POSITIVE_INFINITY;
    const leftB = typeof b.left === "number" ? b.left : Number.POSITIVE_INFINITY;
    if (Math.abs(leftA - leftB) > 1) return leftA - leftB;
    return normalizePlaceholderMatchKey(a.name).localeCompare(normalizePlaceholderMatchKey(b.name));
  });
}

function resolveCatalogPlaceholderForFill(
  placeholderName: string,
  layoutEntry: DeckLayoutCatalogEntry | undefined,
  occurrenceByName: Map<string, number>
): DeckLayoutPlaceholder | undefined {
  if (!layoutEntry?.placeholders?.length) return undefined;
  const exactKey = normalizePlaceholderMatchKey(placeholderName);
  const baseKey = stripTrailingPlaceholderOrdinal(exactKey);
  const exactCandidates = layoutEntry.placeholders.filter(
    (placeholder) => normalizePlaceholderMatchKey(placeholder.name) === exactKey
  );
  const candidates =
    exactCandidates.length > 0
      ? exactCandidates
      : layoutEntry.placeholders.filter(
          (placeholder) =>
            stripTrailingPlaceholderOrdinal(normalizePlaceholderMatchKey(placeholder.name)) === baseKey
        );
  if (candidates.length === 0) return undefined;
  const occurrence = occurrenceByName.get(exactKey) ?? 0;
  occurrenceByName.set(exactKey, occurrence + 1);
  const ordered = sortPlaceholdersByVisualOrder(candidates);
  return ordered[Math.min(occurrence, ordered.length - 1)];
}

function resolveRealShapeByGeneratedName(
  placeholderName: string,
  realShapes: ShapeContent[],
  usedRealIds: Set<string>
): ShapeContent | undefined {
  const byExactName = new Map<string, ShapeContent>();
  const byBaseName = new Map<string, ShapeContent[]>();
  for (const real of realShapes) {
    if (!real.id || usedRealIds.has(real.id)) continue;
    const exact = normalizePlaceholderMatchKey(real.name);
    if (exact && !byExactName.has(exact)) {
      byExactName.set(exact, real);
    }
    const base = stripTrailingPlaceholderOrdinal(exact);
    if (base) {
      const list = byBaseName.get(base) ?? [];
      list.push(real);
      byBaseName.set(base, list);
    }
  }
  const exactKey = normalizePlaceholderMatchKey(placeholderName);
  const exactMatch = byExactName.get(exactKey);
  if (exactMatch?.id && !usedRealIds.has(exactMatch.id)) return exactMatch;
  const baseKey = stripTrailingPlaceholderOrdinal(exactKey);
  return (byBaseName.get(baseKey) ?? []).find(
    (candidate) => candidate.id && !usedRealIds.has(candidate.id)
  );
}

function geometryMatchScore(
  catalogPlaceholder: DeckLayoutPlaceholder,
  realShape: ShapeContent,
  layoutEntry: DeckLayoutCatalogEntry | undefined
): number {
  if (!hasUsableGeometry(catalogPlaceholder) || !hasUsableGeometry(realShape)) {
    return Number.POSITIVE_INFINITY;
  }
  const slideWidth = Math.max(layoutEntry?.dimensions?.width ?? inferredSlideWidth, 1);
  const slideHeight = Math.max(layoutEntry?.dimensions?.height ?? inferredSlideHeight, 1);
  const catalogCenterX = catalogPlaceholder.left + catalogPlaceholder.width / 2;
  const catalogCenterY = catalogPlaceholder.top + catalogPlaceholder.height / 2;
  const realCenterX = realShape.left + realShape.width / 2;
  const realCenterY = realShape.top + realShape.height / 2;
  const centerDistance =
    Math.abs(catalogCenterX - realCenterX) / slideWidth +
    Math.abs(catalogCenterY - realCenterY) / slideHeight;
  const sizeDelta =
    Math.abs(catalogPlaceholder.width - realShape.width) / slideWidth +
    Math.abs(catalogPlaceholder.height - realShape.height) / slideHeight;
  const nameTieBreaker =
    normalizePlaceholderMatchKey(catalogPlaceholder.name) === normalizePlaceholderMatchKey(realShape.name)
      ? -0.001
      : 0;
  return centerDistance + sizeDelta * 0.5 + nameTieBreaker;
}

function resolveRealShapeByCatalogPlaceholder(
  catalogPlaceholder: DeckLayoutPlaceholder,
  realShapes: ShapeContent[],
  usedRealIds: Set<string>,
  layoutEntry: DeckLayoutCatalogEntry | undefined
): ShapeContent | undefined {
  const catalogType = catalogPlaceholder.placeholderFormat?.type;
  const candidates = realShapes
    .filter((shape) => shape.id && !usedRealIds.has(shape.id) && isRealPlaceholderShape(shape))
    .filter((shape) => arePlaceholderTypesCompatible(catalogType, shape.placeholderType));
  const scored = candidates
    .map((shape) => ({
      shape,
      score: geometryMatchScore(catalogPlaceholder, shape, layoutEntry),
    }))
    .filter((entry) => Number.isFinite(entry.score))
    .sort((a, b) => a.score - b.score);
  if (scored.length === 0) return undefined;
  const [best, second] = scored;
  const absoluteThreshold = 0.18;
  const ambiguityMargin = 0.01;
  if (best.score > absoluteThreshold) return undefined;
  if (second && second.score - best.score < ambiguityMargin) return undefined;
  return best.shape;
}

/**
 * Rewrite the shapes of a single freshly inserted slide so any shape carrying a
 * `placeholderName` is mapped onto the real placeholder generated for that
 * layout. Text fills become `modify` on the real shape id (geometry inherited
 * from the placeholder); structured payloads keep their action but are
 * repositioned into the placeholder bounds. Unresolved names degrade to their
 * original action and record a PLACEHOLDER_FILL_UNRESOLVED diagnostic.
 */
function remapPlaceholderFillShapes(
  shapes: ShapeModification[],
  realShapes: ShapeContent[],
  context: {
    slideId: string;
    slideIndex: number;
    layoutEntry?: DeckLayoutCatalogEntry;
    diagnostics: ApplyDiagnostic[];
    logMsg: Logger;
  }
): ShapeModification[] {
  const usedRealIds = new Set<string>();
  const occurrenceByName = new Map<string, number>();

  return shapes.map((shape) => {
    const placeholderName =
      typeof shape.placeholderName === "string" ? shape.placeholderName.trim() : "";
    if (!placeholderName) {
      return shape;
    }

    const catalogPlaceholder = resolveCatalogPlaceholderForFill(
      placeholderName,
      context.layoutEntry,
      occurrenceByName
    );
    let match = catalogPlaceholder
      ? resolveRealShapeByCatalogPlaceholder(
          catalogPlaceholder,
          realShapes,
          usedRealIds,
          context.layoutEntry
        )
      : undefined;
    if (!match && !catalogPlaceholder) {
      context.logMsg(
        `[APPLY] Placeholder "${placeholderName}" on slide ${context.slideId} ` +
          "had no catalog placeholder; falling back to generated shape-name matching"
      );
      match = resolveRealShapeByGeneratedName(placeholderName, realShapes, usedRealIds);
    }
    if (!match || !match.id) {
      context.diagnostics.push({
        code: "PLACEHOLDER_FILL_UNRESOLVED",
        category: "target_mismatch",
        message: `Could not resolve placeholder "${placeholderName}" on inserted slide ${context.slideId}; applied as a new shape instead.`,
        slideIndex: context.slideIndex,
        slideId: context.slideId,
        shapeId: shape.id,
        action: shape.action,
      });
      context.logMsg(
        `[APPLY] Placeholder "${placeholderName}" unresolved on slide ${context.slideId}; keeping shape as ${shape.action}`
      );
      const unresolved: ShapeModification = { ...shape };
      delete unresolved.placeholderName;
      return unresolved;
    }

    usedRealIds.add(match.id);

    if (shapeHasStructuredCreatePayload(shape)) {
      // Chart/table/image/geometric: land it inside the placeholder region but
      // keep it as a real create (cannot "modify" a placeholder into a chart).
      context.logMsg(
        `[APPLY] Positioned "${placeholderName}" payload into placeholder ${match.id} bounds on slide ${context.slideId}`
      );
      const positioned: ShapeModification = { ...shape };
      delete positioned.placeholderName;
      positioned.left = match.left;
      positioned.top = match.top;
      positioned.width = match.width;
      positioned.height = match.height;
      return positioned;
    }

    context.logMsg(
      `[APPLY] Filled placeholder "${placeholderName}" -> shape ${match.id} on slide ${context.slideId}`
    );
    const filled: ShapeModification = { ...shape };
    delete filled.placeholderName;
    delete filled.left;
    delete filled.top;
    delete filled.width;
    delete filled.height;
    filled.id = match.id;
    filled.action = "modify";
    return filled;
  });
}

/**
 * After insert actions are materialized, re-extract the freshly created slides
 * once (scoped by id) and resolve any placeholderName fill shapes against the
 * real generated placeholder ids. Mutates the materialized slide objects in
 * place. No-op when no inserted shape references a placeholderName.
 */
async function resolveInsertedPlaceholderFills(
  insertedRefs: Array<{ slideId: string; slide: SlideModification }>,
  diagnostics: ApplyDiagnostic[],
  logMsg: Logger,
  presentationRef?: FactSetPresentationRef | null
): Promise<void> {
  const refsNeedingFill = insertedRefs.filter(({ slide }) =>
    (slide.shapes || []).some(
      (shape) =>
        typeof shape.placeholderName === "string" && shape.placeholderName.trim().length > 0
    )
  );
  if (refsNeedingFill.length === 0) {
    return;
  }

  const slideIds = Array.from(
    new Set(refsNeedingFill.map((ref) => ref.slideId).filter((id) => id.length > 0))
  );

  const dropPlaceholderNames = (reason: string): void => {
    for (const ref of refsNeedingFill) {
      for (const shape of ref.slide.shapes || []) {
        const placeholderName =
          typeof shape.placeholderName === "string" ? shape.placeholderName.trim() : "";
        if (!placeholderName) continue;
        diagnostics.push({
          code: "PLACEHOLDER_FILL_UNRESOLVED",
          category: "target_mismatch",
          message: `Could not resolve placeholder "${placeholderName}" on inserted slide ${ref.slideId}: ${reason}`,
          slideIndex: ref.slide.index,
          slideId: ref.slideId,
          shapeId: shape.id,
          action: shape.action,
        });
        delete shape.placeholderName;
      }
    }
  };

  let extracted: PresentationContent;
  try {
    extracted = await extractPresentationContentViaIntents(logMsg, {
      forceRefresh: true,
      presentationRef: presentationRef ?? undefined,
      filters: { slides: { ids: slideIds } },
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    logMsg(`[APPLY] Failed to re-extract inserted slides for placeholder mapping: ${message}`);
    dropPlaceholderNames(message);
    return;
  }

  const realSlidesById = new Map<string, SlideContent>();
  for (const real of extracted.slides || []) {
    realSlidesById.set(normalizeSlideIdForMatch(real.id), real);
  }

  let deckLayoutCatalog: DeckLayoutCatalog | null = null;
  try {
    deckLayoutCatalog = await extractDeckLayoutCatalogViaIntents(logMsg, {
      presentationRef: presentationRef ?? undefined,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    logMsg(
      `[APPLY] Failed to extract deck layout catalog for placeholder geometry mapping; ` +
        `falling back to generated shape-name matching: ${message}`
    );
  }

  for (const ref of refsNeedingFill) {
    const realSlide = realSlidesById.get(normalizeSlideIdForMatch(ref.slideId));
    if (!realSlide) {
      logMsg(
        `[APPLY] Inserted slide ${ref.slideId} not returned by re-extract; keeping placeholder shapes as-is`
      );
      for (const shape of ref.slide.shapes || []) {
        const placeholderName =
          typeof shape.placeholderName === "string" ? shape.placeholderName.trim() : "";
        if (!placeholderName) continue;
        diagnostics.push({
          code: "PLACEHOLDER_FILL_UNRESOLVED",
          category: "target_mismatch",
          message: `Inserted slide ${ref.slideId} was not returned by re-extract; placeholder "${placeholderName}" applied as a new shape.`,
          slideIndex: ref.slide.index,
          slideId: ref.slideId,
          shapeId: shape.id,
          action: shape.action,
        });
        delete shape.placeholderName;
      }
      continue;
    }
    const layoutEntry = findDeckLayoutEntryForInsertedSlide(deckLayoutCatalog, ref.slide);
    ref.slide.shapes = remapPlaceholderFillShapes(ref.slide.shapes || [], realSlide.shapes || [], {
      slideId: ref.slideId,
      slideIndex: ref.slide.index,
      layoutEntry,
      diagnostics,
      logMsg,
    });
  }
}

async function materializeStructuralSlideActionsViaIntents(
  modifications: PresentationModification,
  logMsg: Logger,
  presentationRef?: FactSetPresentationRef | null
): Promise<{ modifications: PresentationModification; duplicateCount: number }> {
  const hasInserts = hasInsertSlideActions(modifications);
  const hasDuplicates = hasDuplicateSlideActions(modifications);
  if (!hasInserts && !hasDuplicates) {
    return { modifications, duplicateCount: 0 };
  }

  const materializedSlides: PresentationModification["slides"] = [];
  const diagnostics: ApplyDiagnostic[] = [...(modifications.diagnostics || [])];
  const insertedRefs: Array<{ slideId: string; slide: SlideModification }> = [];
  const lastInsertedSlideIdBySourceId = new Map<string, string>();
  let insertionTargetSlideId: string | undefined;
  let structuralTargetSlideId: string | undefined;
  let duplicateCount = 0;
  const shouldChainAcrossStructuralActions = hasInserts && hasDuplicates;
  const sourceSlideIdByOriginalIndex = new Map<number, string>();
  let indexSourceSnapshotError: string | undefined;
  const needsIndexSourceSnapshot = (modifications.slides || []).some(
    (slide) => slide.action === "duplicate" && !slide.id && typeof slide.index === "number"
  );
  if (needsIndexSourceSnapshot) {
    try {
      const originalSlides = await listSlidesViaIntents(logMsg, { forceRefresh: true, presentationRef });
      for (const originalSlide of originalSlides) {
        sourceSlideIdByOriginalIndex.set(originalSlide.index, originalSlide.slideId);
      }
    } catch (error) {
      indexSourceSnapshotError = error instanceof Error ? error.message : String(error);
      logMsg(
        `[APPLY] Could not snapshot original slide ids for duplicate index resolution: ${indexSourceSnapshotError}`
      );
    }
  }

  for (const slide of modifications.slides || []) {
    if (slide.action !== "insert" && slide.action !== "duplicate") {
      materializedSlides.push(slide);
      continue;
    }

    if (slide.action === "insert") {
      const anchorSlideId =
        (shouldChainAcrossStructuralActions ? structuralTargetSlideId : undefined) ||
        insertionTargetSlideId ||
        slide.id;
      try {
        logMsg(
          `[APPLY] Creating slide for insert action after ${anchorSlideId || `index ${slide.index}`}`
        );
        const created = await addSlideViaIntents({
          targetSlideId: anchorSlideId,
          layoutName: slide.layoutName,
          slideMasterName: slide.slideMasterName,
          log: logMsg,
          presentationRef,
        });
        const createdIndex = await resolveSlideIndexByIdViaIntents(created.slideId, logMsg, {
          presentationRef,
        });
        if (createdIndex === null) {
          throw new Error(`Could not resolve index for inserted slide ${created.slideId}`);
        }
        insertionTargetSlideId = created.slideId;
        structuralTargetSlideId = created.slideId;
        const materialized: SlideModification = {
          ...slide,
          id: created.slideId,
          index: createdIndex,
          action: "modify",
          layoutName: slide.layoutName,
          slideMasterName: slide.slideMasterName,
          shapes: slide.shapes || [],
        };
        materializedSlides.push(materialized);
        insertedRefs.push({ slideId: created.slideId, slide: materialized });
        logMsg(`[APPLY] Insert action materialized as slide index ${createdIndex}`);
      } catch (insertError) {
        const message = insertError instanceof Error ? insertError.message : String(insertError);
        diagnostics.push({
          code: "SLIDE_INSERT_FAILED",
          category: "apply_error",
          message: `Failed to insert slide after ${slide.id || `index ${slide.index}`}: ${message}`,
          slideIndex: slide.index,
          slideId: slide.id,
          action: "insert",
        });
        logMsg(`[APPLY] Insert failed for slide ${slide.index}: ${message}`);
      }
      continue;
    }

    try {
      const sourceSlideId =
        slide.id ||
        (typeof slide.index === "number" ? sourceSlideIdByOriginalIndex.get(slide.index) : undefined);
      if (!slide.id && typeof slide.index === "number" && !sourceSlideId) {
        const reason = indexSourceSnapshotError
          ? `original slide id snapshot failed: ${indexSourceSnapshotError}`
          : `original slide index ${slide.index} was not present in the source snapshot`;
        throw new Error(
          `Could not resolve source slide id for duplicate at index ${slide.index}; ${reason}`
        );
      }
      const sourceKey = sourceSlideId || `index:${slide.index}`;
      const duplicateTargetSlideId =
        lastInsertedSlideIdBySourceId.get(sourceKey) ||
        (shouldChainAcrossStructuralActions ? structuralTargetSlideId : undefined) ||
        slide.afterSlideId;
      // In mixed structural batches, the cross-action cursor preserves the
      // declared operation order. Duplicate-only batches keep the historical
      // per-source chaining behavior.
      const duplicated = await duplicateSlide(slide.index, {
        sourceSlideId,
        log: logMsg,
        presentationRef,
        targetSlideId: duplicateTargetSlideId,
      });
      let duplicatedSlideId = duplicated.newId;
      if (!duplicatedSlideId) {
        const slides = await listSlidesViaIntents(logMsg, { forceRefresh: true, presentationRef });
        duplicatedSlideId = slides.find((candidate) => candidate.index === duplicated.newIndex)?.slideId;
      }
      if (!duplicatedSlideId) {
        throw new Error(`Could not resolve duplicated slide id at index ${duplicated.newIndex}`);
      }

      duplicateCount += 1;
      lastInsertedSlideIdBySourceId.set(sourceKey, duplicatedSlideId);
      structuralTargetSlideId = duplicatedSlideId;
      materializedSlides.push({
        ...slide,
        id: duplicatedSlideId,
        index: duplicated.newIndex,
        action: "modify",
        shapes: slide.shapes || [],
      });
    } catch (duplicateError) {
      const message =
        duplicateError instanceof Error ? duplicateError.message : String(duplicateError);
      diagnostics.push({
        code: "SLIDE_DUPLICATE_FAILED",
        category: "apply_error",
        message: `Failed to duplicate slide ${slide.index}: ${message}`,
        slideIndex: slide.index,
        slideId: slide.id,
        action: "duplicate",
      });
      logMsg(`[APPLY] Duplicate failed for slide ${slide.index}: ${message}`);
    }
  }

  // Map placeholderName fill shapes on the freshly inserted slides onto their
  // real generated placeholder ids (one scoped re-extract for all new slides).
  await resolveInsertedPlaceholderFills(insertedRefs, diagnostics, logMsg, presentationRef);

  const nextModifications: PresentationModification = {
    ...modifications,
    slides: materializedSlides,
  };
  if (diagnostics.length > 0) {
    nextModifications.diagnostics = diagnostics;
  }
  return { modifications: nextModifications, duplicateCount };
}

async function materializeInsertSlideActionsViaIntents(
  modifications: PresentationModification,
  logMsg: Logger,
  presentationRef?: FactSetPresentationRef | null
): Promise<PresentationModification> {
  return (await materializeStructuralSlideActionsViaIntents(modifications, logMsg, presentationRef))
    .modifications;
}

export const remapPlaceholderFillShapesForTesting = remapPlaceholderFillShapes;
export const buildCitationManifestForTesting = buildCitationManifest;
export const materializeInsertSlideActionsViaIntentsForTesting =
  materializeInsertSlideActionsViaIntents;
export const materializeDuplicateSlideActionsViaIntentsForTesting =
  materializeDuplicateSlideActionsViaIntents;

async function materializeDuplicateSlideActionsViaIntents(
  modifications: PresentationModification,
  logMsg: Logger,
  presentationRef?: FactSetPresentationRef | null
): Promise<{ modifications: PresentationModification; duplicateCount: number }> {
  return materializeStructuralSlideActionsViaIntents(modifications, logMsg, presentationRef);
}

/**
 * Build the authoritative per-slide citation manifest from the POST-materialization
 * modifications. By this point insert/duplicate ops have been rewritten to
 * `{ ...slide, id: created.slideId, action:"modify" }`, so every cited slide op
 * carries its REAL host id - no createdSlides diff or positional pairing needed.
 * Merges slide-level declared `citationIds[]` with the inline `<u id>` markup refs
 * already extracted by `stripModificationsInPlace`, keyed by real slide id.
 */
function buildCitationManifest(
  modifications: PresentationModification,
  stripBySlide: SlideCitationRefs[]
): CitationManifest {
  const byId = new Map<string, SlideCitationBinding>();
  const allIds = new Set<string>();
  const ensure = (slideId: string, slideIndex: number, slideNumber?: number): SlideCitationBinding => {
    let binding = byId.get(slideId);
    if (!binding) {
      binding = { slideId, slideIndex, slideNumber, declaredCitationIds: [], shapeRefs: {} };
      byId.set(slideId, binding);
    }
    return binding;
  };

  for (const slide of modifications.slides || []) {
    const slideId = typeof slide.id === "string" ? slide.id : "";
    if (!slideId) continue;
    const declared = Array.isArray(slide.citationIds)
      ? slide.citationIds.filter((id): id is string => typeof id === "string" && id.trim().length > 0)
      : [];
    if (declared.length === 0) continue;
    const slideNumber = (slide as { slideNumber?: number }).slideNumber;
    const binding = ensure(slideId, typeof slide.index === "number" ? slide.index : 0, slideNumber);
    for (const id of declared) {
      if (!binding.declaredCitationIds.includes(id)) binding.declaredCitationIds.push(id);
      allIds.add(id);
    }
  }

  for (const bucket of stripBySlide) {
    if (!bucket.slideId) continue;
    const binding = ensure(bucket.slideId, bucket.slideIndex);
    for (const [shapeId, ids] of Object.entries(bucket.refsByShape)) {
      const list = binding.shapeRefs[shapeId] || (binding.shapeRefs[shapeId] = []);
      for (const id of ids) {
        if (!list.includes(id)) list.push(id);
        allIds.add(id);
      }
    }
  }

  return { bindings: Array.from(byId.values()), allCitationIds: Array.from(allIds) };
}

function createApplyResultSummary(modifications: PresentationModification): ApplyResultSummary {
  const requestedByAction = createActionCounts();
  let requestedShapes = 0;

  for (const slideMod of modifications.slides || []) {
    if ((slideMod.action || "modify") === "duplicate") {
      requestedByAction.duplicate += 1;
    }
    for (const shapeMod of slideMod.shapes || []) {
      requestedShapes += 1;
      requestedByAction[shapeMod.action] += 1;
    }
  }

  return {
    requested: {
      slides: modifications.slides?.length || 0,
      shapes: requestedShapes,
      byAction: requestedByAction,
    },
    applied: {
      slides: 0,
      shapes: 0,
      byAction: createActionCounts(),
    },
    skipped: {
      count: 0,
      byReason: {},
      issues: [],
    },
    errors: {
      count: 0,
      byReason: {},
      issues: [],
    },
    classification: "true_no_op",
  };
}

function hasModifyMutation(
  shapeMod: ShapeModification,
  boundedMod: { left?: number; top?: number; width?: number; height?: number }
): boolean {
  const tableTextUpdates = inferTableTextUpdates(shapeMod);
  return (
    boundedMod.left !== undefined ||
    boundedMod.top !== undefined ||
    boundedMod.width !== undefined ||
    boundedMod.height !== undefined ||
    Boolean(shapeMod.table) ||
    tableTextUpdates.length > 0 ||
    Boolean(shapeMod.textReplacements && shapeMod.textReplacements.length > 0) ||
    Boolean(shapeMod.paragraphOps && shapeMod.paragraphOps.length > 0) ||
    shapeMod.text !== undefined ||
    Boolean(shapeMod.fillColor) ||
    Boolean(shapeMod.lineColor) ||
    shapeMod.lineWidth !== undefined ||
    Boolean(shapeMod.textColor) ||
    shapeMod.fontSize !== undefined ||
    Boolean(shapeMod.fontFamily) ||
    shapeMod.bold !== undefined ||
    shapeMod.italic !== undefined
  );
}

function logApplySummary(summary: ApplyResultSummary, logMsg: Logger): void {
  const truncateLogText = (value: string, maxLen: number = 200): string =>
    value.length > maxLen ? `${value.slice(0, maxLen)}...` : value;

  const successfulOps = countSuccessfulOps(summary);
  logMsg(
    `[APPLY_RESULT] requested=${summary.requested.shapes + summary.requested.byAction.duplicate} ` +
    `applied=${successfulOps} skipped=${summary.skipped.count} errors=${summary.errors.count} ` +
    `classification=${summary.classification}`
  );

  if (summary.skipped.count > 0) {
    const topSkipReasons = Object.entries(summary.skipped.byReason)
      .sort((a, b) => (b[1] || 0) - (a[1] || 0))
      .slice(0, 3)
      .map(([reason, count]) => `${reason}:${count}`)
      .join(", ");
    logMsg(`[APPLY_RESULT] skipped_by_reason=${topSkipReasons}`);
  }

  if (summary.errors.count > 0) {
    const topErrorReasons = Object.entries(summary.errors.byReason)
      .sort((a, b) => (b[1] || 0) - (a[1] || 0))
      .slice(0, 3)
      .map(([reason, count]) => `${reason}:${count}`)
      .join(", ");
    logMsg(`[APPLY_RESULT] errors_by_reason=${topErrorReasons}`);

    const sampledIssues = summary.errors.issues
      .slice(0, 3)
      .map((issue, idx) => `#${idx + 1} ${truncateLogText(issue.message)}`)
      .join(" | ");
    if (sampledIssues) {
      logMsg(`[APPLY_RESULT] error_samples=${sampledIssues}`);
    }
  }
}

/**
 * Map string geometric shape type to PowerPoint.GeometricShapeType
 */
function mapGeometricShapeType(type: string): PowerPoint.GeometricShapeType {
  const typeMap: Record<string, PowerPoint.GeometricShapeType> = {
    rectangle: PowerPoint.GeometricShapeType.rectangle,
    ellipse: PowerPoint.GeometricShapeType.ellipse,
    triangle: PowerPoint.GeometricShapeType.triangle,
    diamond: PowerPoint.GeometricShapeType.diamond,
    pentagon: PowerPoint.GeometricShapeType.pentagon,
    hexagon: PowerPoint.GeometricShapeType.hexagon,
    star4: PowerPoint.GeometricShapeType.star4,
    star5: PowerPoint.GeometricShapeType.star5,
    star6: PowerPoint.GeometricShapeType.star6,
    roundedrectangle: PowerPoint.GeometricShapeType.roundRectangle,
    parallelogram: PowerPoint.GeometricShapeType.parallelogram,
    trapezoid: PowerPoint.GeometricShapeType.trapezoid,
    arrow: PowerPoint.GeometricShapeType.rightArrow,
    arrowright: PowerPoint.GeometricShapeType.rightArrow,
    arrowleft: PowerPoint.GeometricShapeType.leftArrow,
    arrowup: PowerPoint.GeometricShapeType.upArrow,
    arrowdown: PowerPoint.GeometricShapeType.downArrow,
    cloud: PowerPoint.GeometricShapeType.cloud,
    heart: PowerPoint.GeometricShapeType.heart,
    lightning: PowerPoint.GeometricShapeType.lightningBolt,
    bracepair: PowerPoint.GeometricShapeType.bracePair,
    bracketpair: PowerPoint.GeometricShapeType.bracketPair,
    plus: PowerPoint.GeometricShapeType.plus,
    // Default to rectangle if unknown
  };

  return typeMap[type.toLowerCase()] || PowerPoint.GeometricShapeType.rectangle;
}

/**
 * Extract theme colors from the first slide master.
 * Uses the ThemeColorScheme preview API (PowerPointApi 1.10).
 * Returns null if the API is unavailable (e.g. PowerPoint Online).
 */
export async function extractThemeColors(
  log: Logger = defaultLogger,
  options?: { presentationRef?: FactSetPresentationRef | null }
): Promise<import("../types").ThemeColors | null> {
  if (shouldUseFactSetBridge()) {
    try {
      return (await extractDesignMetadataViaIntents(log, {
        presentationRef: options?.presentationRef,
      })).themeColors;
    } catch (err) {
      log(`⚠️ Theme color extraction via intents failed: ${err instanceof Error ? err.message : String(err)}`);
      return null;
    }
  }
  if (isOfficeJsBypassed()) return null;

  try {
    return await guardedPowerPointRun(async (context) => {
      const presentation = context.presentation as any;
      const masters = presentation.slideMasters;
      if (!masters) return null;

      masters.load("items");
      await context.sync();

      if (!masters.items?.length) return null;

      const scheme = masters.items[0].themeColorScheme;
      if (!scheme || typeof scheme.getThemeColor !== "function") return null;

      const colorNames = [
        "Dark1", "Dark2", "Light1", "Light2",
        "Accent1", "Accent2", "Accent3", "Accent4", "Accent5", "Accent6",
        "Hyperlink", "FollowedHyperlink",
      ];

      const results: Record<string, any> = {};
      for (const name of colorNames) {
        results[name] = scheme.getThemeColor(name);
      }
      await context.sync();

      const readColor = (name: string, fallback: string): string => {
        try {
          const val = results[name]?.toJSON?.()?.color ?? results[name]?.color;
          return typeof val === "string" && val.startsWith("#") ? val : fallback;
        } catch {
          return fallback;
        }
      };

      let majorFont: string | undefined;
      let minorFont: string | undefined;
      try {
        const fontScheme = masters.items[0].themeFontScheme;
        if (fontScheme) {
          fontScheme.load("majorFont,minorFont");
          await context.sync();
          const majorObj = (fontScheme as any).majorFont;
          const minorObj = (fontScheme as any).minorFont;
          if (majorObj) {
            if (typeof majorObj === "string") {
              majorFont = majorObj;
            } else if (majorObj.latin) {
              majorFont = typeof majorObj.latin === "string" ? majorObj.latin : majorObj.latin.typeface;
            }
          }
          if (minorObj) {
            if (typeof minorObj === "string") {
              minorFont = minorObj;
            } else if (minorObj.latin) {
              minorFont = typeof minorObj.latin === "string" ? minorObj.latin : minorObj.latin.typeface;
            }
          }
        }
      } catch {
        // themeFontScheme may not be available in all hosts
      }

      return {
        dark1: readColor("Dark1", "#000000"),
        dark2: readColor("Dark2", "#333333"),
        light1: readColor("Light1", "#FFFFFF"),
        light2: readColor("Light2", "#F5F5F5"),
        accent1: readColor("Accent1", "#4472C4"),
        accent2: readColor("Accent2", "#ED7D31"),
        accent3: readColor("Accent3", "#A5A5A5"),
        accent4: readColor("Accent4", "#FFC000"),
        accent5: readColor("Accent5", "#5B9BD5"),
        accent6: readColor("Accent6", "#70AD47"),
        hyperlink: readColor("Hyperlink", "#0563C1"),
        followedHyperlink: readColor("FollowedHyperlink", "#954F72"),
        majorFont,
        minorFont,
      };
    }, null);
  } catch (err) {
    log(`⚠️ Theme color extraction failed: ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }
}

/**
 * Discover the "Blank" slide layout from the first slide master and extract
 * the title placeholder position from the first layout that has one.
 * Returns { blankLayoutId, titlePlaceholder } or null if the API is unavailable.
 */
export async function discoverSlideLayoutInfo(
  log: Logger = defaultLogger,
  options?: { presentationRef?: FactSetPresentationRef | null }
): Promise<import("../types").SlideLayoutInfo | null> {
  if (shouldUseFactSetBridge()) {
    try {
      return (await extractDesignMetadataViaIntents(log, {
        presentationRef: options?.presentationRef,
      })).layoutInfo;
    } catch (err) {
      log(`⚠️ Layout discovery via intents failed: ${err instanceof Error ? err.message : String(err)}`);
      return null;
    }
  }
  if (isOfficeJsBypassed()) return null;

  try {
    return await guardedPowerPointRun(async (context) => {
      const presentation = context.presentation as any;
      const masters = presentation.slideMasters;
      if (!masters) return null;

      masters.load("items");
      await context.sync();
      if (!masters.items?.length) return null;

      const master = masters.items[0];
      const layouts = master.slideLayouts;
      if (!layouts) return null;

      layouts.load("items/name,items/id");
      await context.sync();

      if (!layouts.items?.length) return null;

      let blankLayoutId: string | undefined;
      for (const layout of layouts.items) {
        const name = (layout.name ?? "").toLowerCase().trim();
        if (name === "blank" || name === "leer" || name === "vide" || name === "en blanco") {
          blankLayoutId = layout.id;
          break;
        }
      }

      let titlePlaceholder: import("../types").TitlePlaceholderHint | undefined;
      for (const layout of layouts.items) {
        try {
          const shapes = layout.shapes;
          if (!shapes) continue;
          shapes.load("items/name,items/left,items/top,items/width,items/height,items/type");
          await context.sync();

          for (const shape of shapes.items) {
            const shapeName = (shape.name ?? "").toLowerCase();
            const isTitle =
              shapeName.includes("title") ||
              shape.type === "Placeholder" ||
              shapeName.includes("titel");
            if (isTitle && shape.left != null && shape.top != null) {
              titlePlaceholder = {
                left: Math.round(shape.left),
                top: Math.round(shape.top),
                width: Math.round(shape.width),
                height: Math.round(shape.height),
              };
              break;
            }
          }
          if (titlePlaceholder) break;
        } catch {
          continue;
        }
      }

      if (blankLayoutId) {
        log(`✓ Found "Blank" layout (id=${blankLayoutId})`);
      }
      if (titlePlaceholder) {
        log(`✓ Title placeholder hint: left=${titlePlaceholder.left}, top=${titlePlaceholder.top}, w=${titlePlaceholder.width}, h=${titlePlaceholder.height}`);
      }

      return { blankLayoutId, titlePlaceholder };
    }, null);
  } catch (err) {
    log(`⚠️ Layout discovery failed: ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }
}

/**
 * Extract all content from the current presentation.
 *
 * pageSetup and getSelectedSlides are read in their own isolated
 * PowerPoint.run() contexts so that failures in those optional APIs
 * (common on SharePoint / PowerPoint Online) never poison the main
 * context that loads slides.items.
 */
export interface ExtractPresentationContentOptions {
  forceRefresh?: boolean;
  includeSlideRanges?: RunOfficeExtractorSlideRangeFilter[];
  includeShapes?: RunOfficeExtractorShapeFilter[];
  presentationRef?: FactSetPresentationRef | null;
  // f2_docs RunOfficeExtractor filter schema. When set, the underlying intent
  // is sent with `{ filters }` and the legacy fields above must be omitted.
  // See `intentBridgeService.RunOfficeExtractorFilters` for the shape.
  filters?: RunOfficeExtractorFilters;
}

export interface ExtractWindowedPresentationContentOptions extends ExtractPresentationContentOptions {
  /**
   * Total number of slides the resulting payload should contain, centered on
   * the currently selected slide. See `utils/contextWindow` for the exact
   * window semantics (symmetric, shifts toward deck interior near edges).
   */
  windowSize: number;
  /**
   * Optional stable window center for long-running flows that already captured
   * their target presentation/slide. When provided, windowing does not consult
   * the currently active presentation, which may have changed meanwhile.
   */
  windowAnchor?: {
    selectedSlideIndex: number;
    totalSlideCount: number;
  };
}

function attachUsedSlidesToDeckLayoutCatalog(
  catalog: DeckLayoutCatalog | undefined,
  slides: SlideContent[]
): DeckLayoutCatalog | undefined {
  if (!catalog || catalog.layouts.length === 0) return catalog;
  return {
    layouts: catalog.layouts.map((layout) => {
      const names = new Set(
        [layout.layoutName, layout.matchingName]
          .map((value) => (typeof value === "string" ? value.trim().toLowerCase() : ""))
          .filter(Boolean)
      );
      if (names.size === 0) return layout;
      const usedBySlides = slides
        .filter((slide) => {
          const candidates = [slide.customLayoutName, slide.layout]
            .map((value) => (typeof value === "string" ? value.trim().toLowerCase() : ""))
            .filter(Boolean);
          return candidates.some((candidate) => names.has(candidate));
        })
        .slice(0, 5)
        .map((slide) => ({
          id: slide.id,
          index: slide.index,
          ...(typeof slide.slideNumber === "number" ? { slideNumber: slide.slideNumber } : {}),
        }));
      const layoutType =
        layout.layoutType ??
        slides.find((slide) => {
          const customLayoutName = slide.customLayoutName?.trim().toLowerCase();
          return !!customLayoutName && names.has(customLayoutName);
        })?.layout;
      return {
        ...layout,
        ...(layoutType ? { layoutType } : {}),
        ...(usedBySlides.length > 0 ? { usedBySlides } : {}),
      };
    }),
  };
}

async function attachPromptDesignContext(
  presentation: PresentationContent,
  log: Logger,
  options?: { presentationRef?: FactSetPresentationRef | null }
): Promise<PresentationContent> {
  try {
    if (shouldUseFactSetBridge()) {
      const context = await extractPromptDesignContextViaIntents(log, {
        forceRefresh: false,
        presentationRef: options?.presentationRef,
      });
      let catalog = context.deckLayoutCatalog;
      if (!catalog) {
        try {
          catalog = await extractDeckLayoutCatalogViaIntents(log, {
            forceRefresh: false,
            presentationRef: options?.presentationRef,
          });
        } catch (catalogError) {
          log(
            `extractWindowedPresentationContent: layout catalog fallback unavailable (${
              catalogError instanceof Error ? catalogError.message : String(catalogError)
            })`
          );
        }
      }
      if (catalog) {
        presentation.deckLayoutCatalog = attachUsedSlidesToDeckLayoutCatalog(
          catalog,
          presentation.slides
        );
      }
      if (context.themeColors) {
        presentation.themeColors = context.themeColors;
      }
    } else {
      const themeColors = await extractThemeColors(log, {
        presentationRef: options?.presentationRef,
      });
      if (themeColors) {
        presentation.themeColors = themeColors;
      }
    }
  } catch (err) {
    log(
      `extractWindowedPresentationContent: prompt design context unavailable (${
        err instanceof Error ? err.message : String(err)
      })`
    );
  }
  return presentation;
}

/**
 * Extract presentation content for the chat-edit path, restricted to a
 * window of `windowSize` slides centered on the currently selected slide.
 *
 * Runs the two cheap, poll-friendly intents
 * (`factset.Presentation.GetActivePresentationContext` and
 * `factset.Presentation.GetSlideCount`) in parallel to size the window,
 * then forwards the resulting `{ start, end }` to the underlying
 * `RunOfficeExtractor` call via `filters.slides.ranges`. The host walks
 * only those slides instead of the whole deck.
 *
 * Falls back to the unscoped `extractPresentationContent` path when:
 *   - the FactSet bridge is not active (Office.js host),
 *   - resolving the active slide / slide count fails,
 *   - the computed window is empty (defensive: should not happen).
 *
 * Any caller-supplied `filters.slides.ranges` is overridden by the
 * window. Other `filters.slides.*` leaves (e.g. shape filters,
 * `notesPage: false`) are preserved.
 */
export async function extractWindowedPresentationContent(
  log: Logger = defaultLogger,
  options: ExtractWindowedPresentationContentOptions
): Promise<PresentationContent> {
  const { windowSize, filters, windowAnchor, ...rest } = options;

  if (!shouldUseFactSetBridge()) {
    return attachPromptDesignContext(
      await extractPresentationContent(log, { ...rest, filters }),
      log,
      rest
    );
  }

  let selectedSlideId: string | null = null;
  const hasValidAnchor =
    windowAnchor != null &&
    Number.isFinite(windowAnchor.selectedSlideIndex) &&
    windowAnchor.selectedSlideIndex >= 0 &&
    Number.isFinite(windowAnchor.totalSlideCount) &&
    windowAnchor.totalSlideCount > 0;
  let selectedIndex: number | null = hasValidAnchor
    ? Math.floor(windowAnchor.selectedSlideIndex)
    : null;
  let slideCount: number | null = hasValidAnchor ? Math.floor(windowAnchor.totalSlideCount) : null;
  if (!hasValidAnchor) {
    try {
      const [selectedSlideRef, count] = await Promise.all([
        getSelectedSlideRefViaIntents(log),
        getSlideCountViaIntents(log),
      ]);
      selectedSlideId = selectedSlideRef.id || null;
      selectedIndex = selectedSlideRef.index;
      slideCount = count;
    } catch (err) {
      log(
        `extractWindowedPresentationContent: failed to resolve active slide / slide count (${
          err instanceof Error ? err.message : String(err)
        }); falling back to unscoped chat extract`
      );
      return attachPromptDesignContext(
        await extractPresentationContent(log, { ...rest, filters }),
        log,
        rest
      );
    }
  }

  if (selectedIndex == null || slideCount == null) {
    return attachPromptDesignContext(
      await extractPresentationContent(log, { ...rest, filters }),
      log,
      rest
    );
  }
  const { start, end } = computeContextSlideWindow(selectedIndex, slideCount, windowSize);
  if (end < start) {
    log(
      `extractWindowedPresentationContent: empty window for selectedIndex=${selectedIndex}, slideCount=${slideCount}, windowSize=${windowSize}; falling back to unscoped chat extract`
    );
    return attachPromptDesignContext(
      await extractPresentationContent(log, { ...rest, filters }),
      log,
      rest
    );
  }

  log(
    `Chat extract window: slides ${start}..${end} (selected=${selectedIndex}, total=${slideCount}, size=${end - start + 1})`
  );

  const callerSlides =
    filters?.slides && typeof filters.slides === "object" ? filters.slides : undefined;
  const windowedFilters: RunOfficeExtractorFilters = {
    ...(filters ?? {}),
    slides: {
      ...(callerSlides ?? {}),
      ranges: [{ start, end }],
    },
  };

  const presentation = await extractPresentationContent(log, { ...rest, filters: windowedFilters });

  // The underlying `extractPresentationContentViaIntents` resolves
  // `selectedSlideIndex` via `resolveSelectedSlideIndexFromContext` which
  // returns the ARRAY POSITION of the selected slide within the returned
  // slides (e.g. `4` when the selected slide is the 5th in the window),
  // not the slide's actual DECK index. That used to be the same number
  // for unscoped extracts (because positions matched deck indices), but
  // for a windowed extract the two diverge: the window covers deck indices
  // 140..144, the selected slide is at deck index 144, but the array
  // position is 4. Downstream consumers — `buildRequestContextEnvelope`'s
  // `find(slide.index === selectedSlideIndex)`, `exportSlideAsImage`, the
  // tab session's `slideIndex` field — all expect a deck index and silently
  // pick the wrong slide otherwise (e.g. the LLM ends up editing the first
  // slide in the window instead of the user's actual selected slide).
  //
  // Resolve this by overriding `selectedSlideIndex` with the deck index we
  // already resolved before the extract. Match by slide id first (most
  // robust against host renumbering); fall back to the raw resolved index
  // if the selected slide id isn't in the windowed result for any reason
  // (e.g. user navigated away mid-flight).
  if (typeof selectedIndex === "number") {
    let deckSelectedIndex = selectedIndex;
    if (selectedSlideId) {
      const match = presentation.slides.find((slide) => slide.id === selectedSlideId);
      if (match && typeof match.index === "number") {
        deckSelectedIndex = match.index;
      }
    }
    presentation.selectedSlideIndex = deckSelectedIndex;
  }

  // Plumb the true deck size through so the LLM context envelope can show
  // `deckContext.totalSlides = 200` (real deck) instead of `5` (window
  // size). The system prompt promises the model that `totalSlides` reflects
  // the true deck size; without this, the model would think the deck only
  // has `windowSize` slides and misinterpret cross-slide references.
  presentation.totalDeckSlides = slideCount;

  return attachPromptDesignContext(presentation, log, rest);
}

export async function extractPresentationContent(
  log: Logger = defaultLogger,
  options?: ExtractPresentationContentOptions
): Promise<PresentationContent> {
  if (shouldUseFactSetBridge()) {
    try {
      const presentation = await extractPresentationContentViaIntents(log, options);
      lastKnownPresentationContent = presentation;
      return presentation;
    } catch (intentError) {
      const details = intentError instanceof Error ? intentError.message : String(intentError);
      console.error("[FACTSET] Intent extraction failed:", intentError);
      throw new Error(`FactSet intent extraction failed: ${details}`);
    }
  }

  if (isOfficeJsBypassed()) {
    console.log("[OfficeJS NOP] extractPresentationContent bypassed");
    return lastKnownPresentationContent;
  }

  const { width: actualSlideWidth, height: actualSlideHeight } =
    await readSlideDimensionsInIsolatedContext("[SLIDE]");

  const selectedSlideId = await readSelectedSlideIdInIsolatedContext(
    "Could not get selected slide, defaulting to first slide"
  );

  const extractWithFreshContext = async (): Promise<PresentationContent> =>
    guardedPowerPointRun(async (context) => {
      const slides = context.presentation.slides;
      slides.load("items");
      await context.sync();
      const loadedSlides = slides.items.slice();

      let selectedSlideIndex = 0;
      if (selectedSlideId) {
        for (let i = 0; i < loadedSlides.length; i++) {
          if (loadedSlides[i].id === selectedSlideId) {
            selectedSlideIndex = i;
            break;
          }
        }
      }

      const slideContents: SlideContent[] = [];

      for (let i = 0; i < loadedSlides.length; i++) {
        const slide = loadedSlides[i];

        slide.load("id");
        await context.sync();

        slide.shapes.load("items");
        await context.sync();
        const loadedShapes = slide.shapes.items.slice();

        const shapes: ShapeContent[] = [];

        for (let j = 0; j < loadedShapes.length; j++) {
          const shape = loadedShapes[j];

          try {
            shape.load(["id", "name", "left", "top", "width", "height", "type"]);
            await context.sync();

            let text: string | undefined;
            let paragraphs: ParagraphContent[] | undefined;
            let shapeType: ShapeContent["type"] = "unknown";
            let tableData: TableCellData | undefined;
            let geometricType: string | undefined;
            let fillColor: string | undefined;
            let lineColor: string | undefined;
            let lineWidth: number | undefined;

            const shapeTypeStr = String(shape.type || "").toLowerCase();

            if (shapeTypeStr.includes("table")) {
              shapeType = "table";
              log(`  Attempting table extraction for shape ${j}...`);
              tableData = await extractTableData(shape, context, log);
            } else if (shapeTypeStr.includes("line") || shapeTypeStr.includes("connector")) {
              shapeType = "line";
            } else if (shapeTypeStr.includes("group")) {
              shapeType = "group";
            } else if (shapeTypeStr.includes("ole")) {
              shapeType = "oleObject";
            } else if (shapeTypeStr.includes("geometric") || shapeTypeStr.includes("autoshape")) {
              shapeType = "geometricShape";
            }

            // Try to read geometricShapeType for geometric and unknown shapes
            if (shapeType === "geometricShape" || shapeType === "unknown") {
              try {
                const shapeAny = shape as any;
                if (shapeAny.geometricShapeType !== undefined) {
                  shapeAny.load("geometricShapeType");
                  await context.sync();
                  const geoStr = String(shapeAny.geometricShapeType).toLowerCase();
                  if (geoStr && geoStr !== "undefined") {
                    geometricType = geoStr;
                    if (shapeType === "unknown") shapeType = "geometricShape";
                  }
                }
              } catch {
                // Geometric type not available
              }
            }

            // Extract fill and line color for ALL shape types (not just geometric)
            if (shapeType !== "table") {
              try {
                shape.fill.load("foregroundColor");
                await context.sync();
                fillColor = shape.fill.foregroundColor;
              } catch {
                // Fill color not available
              }
              try {
                const lineFormat = (shape as any).lineFormat || (shape as any).line;
                if (lineFormat) {
                  lineFormat.load("color,weight");
                  await context.sync();
                  lineColor = lineFormat.color;
                  lineWidth = typeof lineFormat.weight === "number" ? lineFormat.weight : undefined;
                }
              } catch {
                // Line format not available
              }
            }

            let fontSize: number | undefined;
            let fontFamily: string | undefined;
            let textColor: string | undefined;
            let bold: boolean | undefined;
            let italic: boolean | undefined;
            let horizontalAlignment: string | undefined;
            let verticalAlignment: string | undefined;

            if (shapeType !== "table") {
              try {
                const textFrame = shape.textFrame;
                const textRange = textFrame.textRange;
                textRange.load("text");
                await context.sync();
                text = textRange.text;
                paragraphs = buildParagraphContentSnapshot(text);
                if (shapeType === "unknown" && text) {
                  shapeType = "textBox";
                }

                // Extract per-paragraph bullet + font info
                try {
                  const paras = (textFrame.textRange as any).paragraphs;
                  if (!paras || typeof paras.load !== "function") throw new Error("no paragraphs API");
                  paras.load("items");
                  await context.sync();
                  for (let pi = 0; pi < paras.items.length && paragraphs && pi < paragraphs.length; pi++) {
                    const paraItem = paras.items[pi] as any;
                    try {
                      const paraFont = paraItem.font;
                      if (paraFont && typeof paraFont.load === "function") {
                        paraFont.load("size,name,color,bold,italic");
                        await context.sync();
                        if (paraFont.size) paragraphs[pi].fontSize = paraFont.size;
                        if (paraFont.name) paragraphs[pi].fontFamily = paraFont.name;
                        if (paraFont.color) paragraphs[pi].textColor = paraFont.color;
                        if (paraFont.bold !== undefined) paragraphs[pi].bold = paraFont.bold;
                        if (paraFont.italic !== undefined) paragraphs[pi].italic = paraFont.italic;
                      }
                    } catch {
                      // Per-paragraph font not available
                    }
                    try {
                      const paraFormat = (paraItem as any).paragraphFormat;
                      if (paraFormat) {
                        const bulletFormat = paraFormat.bulletFormat;
                        if (bulletFormat && typeof bulletFormat.load === "function") {
                          bulletFormat.load("visible,type,style");
                          await context.sync();
                          if (bulletFormat.visible === true) {
                            paragraphs[pi].bulletVisible = true;
                            if (bulletFormat.type !== undefined) {
                              paragraphs[pi].bulletType = String(bulletFormat.type);
                            }
                            if (bulletFormat.style !== undefined) {
                              paragraphs[pi].bulletStyle = String(bulletFormat.style);
                            }
                          }
                        }
                      }
                    } catch {
                      // Bullet info not available for this paragraph
                    }
                  }
                } catch {
                  // Paragraph-level extraction not available
                }

                try {
                  const font = textRange.font;
                  font.load(["size", "name", "color", "bold", "italic"]);
                  await context.sync();
                  fontSize = font.size;
                  fontFamily = font.name;
                  textColor = font.color;
                  bold = font.bold;
                  italic = font.italic;
                } catch {
                  // Font properties not available
                }

                try {
                  const tfAny = textFrame as any;
                  if (tfAny.hasText !== false) {
                    tfAny.load("verticalAlignment");
                    await context.sync();
                    if (tfAny.verticalAlignment) {
                      verticalAlignment = String(tfAny.verticalAlignment);
                    }
                  }
                } catch {
                  // Vertical alignment not available
                }

                try {
                  const firstPara = (textFrame.textRange as any).paragraphs;
                  if (firstPara && typeof firstPara.load === "function") {
                    firstPara.load("items");
                    await context.sync();
                    if (firstPara.items?.length > 0) {
                      const pf = (firstPara.items[0] as any).paragraphFormat;
                      if (pf) {
                        pf.load("alignment");
                        await context.sync();
                        if (pf.alignment) {
                          horizontalAlignment = String(pf.alignment);
                        }
                      }
                    }
                  }
                } catch {
                  // Horizontal alignment not available
                }
              } catch {
                // Shape doesn't have text frame
              }
            }

            shapes.push({
              id: shape.id,
              name: shape.name || `Shape_${j}`,
              type: shapeType,
              geometricType: geometricType as ShapeContent["geometricType"],
              text,
              paragraphs,
              tableData,
              fillColor,
              lineColor,
              lineWidth,
              fontSize,
              fontFamily,
              textColor,
              bold,
              italic,
              horizontalAlignment,
              verticalAlignment,
              left: shape.left !== undefined ? roundPt(shape.left) : undefined,
              top: shape.top !== undefined ? roundPt(shape.top) : undefined,
              width: shape.width !== undefined ? roundPt(shape.width) : undefined,
              height: shape.height !== undefined ? roundPt(shape.height) : undefined,
            });
          } catch (shapeError) {
            console.log(`Skipping shape ${j} due to error:`, shapeError);
          }
        }

        slideContents.push({
          id: slide.id,
          index: i,
          slideNumber: i + 1,
          shapes,
        });
      }

      const result: PresentationContent = {
        slides: slideContents,
        selectedSlideIndex,
        slideWidth: actualSlideWidth,
        slideHeight: actualSlideHeight,
      };
      lastKnownPresentationContent = result;
      return result;
    }, lastKnownPresentationContent);

  try {
    return await extractWithFreshContext();
  } catch (error) {
    if (isPropertyNotLoadedError(error)) {
      console.warn("[SLIDE] PropertyNotLoaded on first attempt, retrying with fresh context…");
      try {
        return await extractWithFreshContext();
      } catch (retryError) {
        console.warn("[SLIDE] Retry also failed, returning last known content", retryError);
        return lastKnownPresentationContent;
      }
    }
    throw error;
  }
}

/**
 * Apply text replacements to a shape (preserves formatting)
 */
async function applyTextReplacements(
  shape: PowerPoint.Shape,
  replacements: { find: string; replace: string }[],
  context: PowerPoint.RequestContext
): Promise<void> {
  const textRange = shape.textFrame.textRange;
  textRange.load("text");
  await context.sync();

  let currentText = textRange.text || "";

  // Track positions and apply replacements
  for (const replacement of replacements) {
    const findIndex = currentText.indexOf(replacement.find);
    if (findIndex === -1) {
      console.log(`Text "${replacement.find}" not found in shape`);
      continue;
    }

    try {
      // Get substring for the found text and replace it
      const targetRange = textRange.getSubstring(findIndex, replacement.find.length);
      targetRange.text = replacement.replace;
      await context.sync();

      // Update our tracking of current text
      currentText =
        currentText.substring(0, findIndex) +
        replacement.replace +
        currentText.substring(findIndex + replacement.find.length);

      // Reload to get updated positions for next replacement
      textRange.load("text");
      await context.sync();
      currentText = textRange.text || "";
    } catch (e) {
      console.log(`Could not replace "${replacement.find}":`, e);
    }
  }
}

interface ParagraphApplyMeta {
  summary: ApplyResultSummary;
  slideIndex: number;
  slideId?: string;
  shapeId: string;
  action: "create" | "modify";
}

const BULLET_TYPE_ALIAS_TOKENS: Readonly<Record<string, readonly string[]>> = {
  None: ["None", "none"],
  Unnumbered: ["Unnumbered", "unnumbered", "UnNumbered", "unNumbered"],
  Numbered: ["Numbered", "numbered"],
};

const NATIVE_NUMBERED_STYLE_FALLBACKS: readonly string[] = [
  "ArabicNumeralPeriod",
  "ArabicNumeralParenthesisRight",
  "ArabicNumeralPlain",
  "RomanUppercasePeriod",
  "RomanLowercasePeriod",
  "AlphabetUppercasePeriod",
  "AlphabetLowercasePeriod",
];

function formatParagraphPropertyValue(value: unknown): string {
  if (typeof value === "string") {
    return `"${value}"`;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function getPowerPointApiSupportDebug(requiredApiVersion?: string): string {
  if (!requiredApiVersion) {
    return "";
  }
  try {
    if (typeof Office === "undefined") {
      return ` (PowerPointApi ${requiredApiVersion} support=unknown)`;
    }
    const requirements = Office.context?.requirements;
    if (!requirements || typeof requirements.isSetSupported !== "function") {
      return ` (PowerPointApi ${requiredApiVersion} support=unknown)`;
    }
    const supported = requirements.isSetSupported("PowerPointApi", requiredApiVersion);
    return ` (PowerPointApi ${requiredApiVersion} supported=${supported ? "yes" : "no"})`;
  } catch {
    return ` (PowerPointApi ${requiredApiVersion} support=unknown)`;
  }
}

function getEnumCandidateValues(enumObject: unknown, requestedValue: string): unknown[] {
  const candidates: unknown[] = [requestedValue];
  if (!enumObject || typeof enumObject !== "object") {
    return candidates;
  }

  const enumRecord = enumObject as Record<string, unknown>;
  const direct = enumRecord[requestedValue];
  if (direct !== undefined) {
    candidates.unshift(direct);
  }

  const caseInsensitiveKey = Object.keys(enumRecord).find(
    (key) => key.toLowerCase() === requestedValue.toLowerCase()
  );
  if (caseInsensitiveKey) {
    const caseInsensitiveValue = enumRecord[caseInsensitiveKey];
    if (caseInsensitiveValue !== undefined) {
      candidates.unshift(caseInsensitiveValue);
    }
  }

  const deduped: unknown[] = [];
  for (const candidate of candidates) {
    if (!deduped.includes(candidate)) {
      deduped.push(candidate);
    }
  }
  return deduped;
}

function getBulletTypeCandidateValues(requestedValue: string): unknown[] {
  const enumObject = (globalThis as unknown as { PowerPoint?: any }).PowerPoint?.BulletType;
  const aliasTokens =
    BULLET_TYPE_ALIAS_TOKENS[requestedValue] || [requestedValue, requestedValue.toLowerCase()];
  const candidates: unknown[] = [];
  for (const token of aliasTokens) {
    for (const candidate of getEnumCandidateValues(enumObject, token)) {
      if (!candidates.includes(candidate)) {
        candidates.push(candidate);
      }
    }
  }
  return candidates;
}

function getNativeNumberedStyleCandidates(): unknown[] {
  const enumObject = (globalThis as unknown as { PowerPoint?: any }).PowerPoint?.BulletStyle;
  const candidates: unknown[] = [];
  for (const styleName of NATIVE_NUMBERED_STYLE_FALLBACKS) {
    for (const candidate of getEnumCandidateValues(enumObject, styleName)) {
      if (!candidates.includes(candidate)) {
        candidates.push(candidate);
      }
    }
  }
  return candidates;
}

function getParagraphApplyLength(paragraphRange: ParagraphRange, paragraphSet: ParagraphSet): number {
  const hasBulletTypeOrStyleRequest = Boolean(
    paragraphSet.bullet?.type !== undefined || paragraphSet.bullet?.style !== undefined
  );
  if (hasBulletTypeOrStyleRequest && paragraphRange.length > 0) {
    return paragraphRange.length;
  }
  if (paragraphRange.applyLength > 0) {
    return paragraphRange.applyLength;
  }
  return paragraphRange.length;
}

async function readBulletRuntimeState(
  paragraphFormat: PowerPoint.ParagraphFormat,
  context: PowerPoint.RequestContext
): Promise<{ type?: unknown; visible?: unknown; style?: unknown } | null> {
  try {
    const bulletFormat = (paragraphFormat as any).bulletFormat;
    if (!bulletFormat || typeof bulletFormat.load !== "function") {
      return null;
    }
    bulletFormat.load("type,visible,style");
    await context.sync();
    return {
      type: bulletFormat.type,
      visible: bulletFormat.visible,
      style: bulletFormat.style,
    };
  } catch {
    return null;
  }
}

async function applyParagraphProperty(
  context: PowerPoint.RequestContext,
  meta: ParagraphApplyMeta,
  propertyName: string,
  propertyValue: unknown,
  targetDescriptor: string,
  applyProperty: () => void,
  requiredApiVersion?: string
): Promise<boolean> {
  try {
    applyProperty();
    await context.sync();
    return true;
  } catch (error) {
    const errMsg = error instanceof Error ? error.message : String(error);
    const propertyValueText = formatParagraphPropertyValue(propertyValue);
    const apiSupportText = getPowerPointApiSupportDebug(requiredApiVersion);
    recordError(meta.summary, {
      code: "PARAGRAPH_OP_APPLY_FAILED",
      category: "apply_error",
      message: `Failed paragraph property ${propertyName}=${propertyValueText} on ${targetDescriptor} for shape ${meta.shapeId}: ${errMsg}${apiSupportText}`,
      slideIndex: meta.slideIndex,
      slideId: meta.slideId,
      shapeId: meta.shapeId,
      action: meta.action,
    });
    return false;
  }
}

async function applyParagraphPropertyWithCandidates(
  context: PowerPoint.RequestContext,
  meta: ParagraphApplyMeta,
  propertyName: string,
  propertyValues: unknown[],
  targetDescriptor: string,
  applyProperty: (propertyValue: unknown) => void,
  requiredApiVersion?: string
): Promise<boolean> {
  const tryApplyCandidates = async (): Promise<{
    success: boolean;
    lastErrMsg: string;
    attemptedValues: string;
  }> => {
    if (propertyValues.length === 0) {
      return { success: false, lastErrMsg: "No candidate values", attemptedValues: "" };
    }

    let lastErrMsg = "";
    for (const propertyValue of propertyValues) {
      try {
        applyProperty(propertyValue);
        await context.sync();
        return {
          success: true,
          lastErrMsg: "",
          attemptedValues: propertyValues
            .map((value) => formatParagraphPropertyValue(value))
            .join(", "),
        };
      } catch (error) {
        lastErrMsg = error instanceof Error ? error.message : String(error);
      }
    }

    return {
      success: false,
      lastErrMsg,
      attemptedValues: propertyValues.map((value) => formatParagraphPropertyValue(value)).join(", "),
    };
  };

  const attempt = await tryApplyCandidates();
  if (attempt.success) {
    return true;
  }

  const apiSupportText = getPowerPointApiSupportDebug(requiredApiVersion);
  recordError(meta.summary, {
    code: "PARAGRAPH_OP_APPLY_FAILED",
    category: "apply_error",
    message: `Failed paragraph property ${propertyName} using candidates [${attempt.attemptedValues}] on ${targetDescriptor} for shape ${meta.shapeId}: ${attempt.lastErrMsg}${apiSupportText}`,
    slideIndex: meta.slideIndex,
    slideId: meta.slideId,
    shapeId: meta.shapeId,
    action: meta.action,
  });
  return false;
}

async function applyParagraphSetToTextRange(
  textRange: PowerPoint.TextRange,
  paragraphSet: ParagraphSet,
  context: PowerPoint.RequestContext,
  meta: ParagraphApplyMeta,
  targetDescriptor: string
): Promise<boolean> {
  let mutated = false;
  const paragraphFormat = textRange.paragraphFormat;

  if (paragraphSet.horizontalAlignment !== undefined) {
    if (
      await applyParagraphProperty(
        context,
        meta,
        "horizontalAlignment",
        paragraphSet.horizontalAlignment,
        targetDescriptor,
        () => {
          paragraphFormat.horizontalAlignment = paragraphSet.horizontalAlignment as any;
        },
        "1.4"
      )
    ) {
      mutated = true;
    }
  }
  if (paragraphSet.indentLevel !== undefined) {
    if (
      await applyParagraphProperty(
        context,
        meta,
        "indentLevel",
        paragraphSet.indentLevel,
        targetDescriptor,
        () => {
          paragraphFormat.indentLevel = paragraphSet.indentLevel;
        },
        "1.10"
      )
    ) {
      mutated = true;
    }
  }

  if (paragraphSet.bullet) {
    const bulletFormat = paragraphFormat.bulletFormat as any;
    const bulletStateBeforeTypeSet =
      paragraphSet.bullet.type !== undefined
        ? await readBulletRuntimeState(paragraphFormat, context)
        : null;
    let bulletTypeApplied = paragraphSet.bullet.type === undefined;
    if (paragraphSet.bullet.visible !== undefined) {
      if (
        await applyParagraphProperty(
          context,
          meta,
          "bullet.visible",
          paragraphSet.bullet.visible,
          targetDescriptor,
          () => {
            bulletFormat.visible = paragraphSet.bullet?.visible;
          },
          "1.4"
        )
      ) {
        mutated = true;
      }
    }
    if (paragraphSet.bullet.type !== undefined) {
      const bulletTypeValue = paragraphSet.bullet.type;
      const currentBulletType = bulletStateBeforeTypeSet?.type;
      if (currentBulletType === "Unsupported") {
        recordError(meta.summary, {
          code: "PARAGRAPH_OP_APPLY_FAILED",
          category: "apply_error",
          message: `Skipped paragraph property bullet.type=${formatParagraphPropertyValue(bulletTypeValue)} on ${targetDescriptor} for shape ${meta.shapeId}: runtime bullet.type is "Unsupported"${getPowerPointApiSupportDebug("1.10")}`,
          slideIndex: meta.slideIndex,
          slideId: meta.slideId,
          shapeId: meta.shapeId,
          action: meta.action,
        });
        bulletTypeApplied = false;
      } else {
        const hostBulletTypeCandidates = getBulletTypeCandidateValues(bulletTypeValue);

        let typeLastErrMsg = "";
        for (const candidateValue of hostBulletTypeCandidates) {
          try {
            bulletFormat.type = candidateValue;
            await context.sync();
            bulletTypeApplied = true;
            break;
          } catch (error) {
            typeLastErrMsg = error instanceof Error ? error.message : String(error);
          }
        }

        let styleFallbackLastErrMsg = "";
        if (!bulletTypeApplied && bulletTypeValue === "Numbered") {
          const numberedStyleCandidates = getNativeNumberedStyleCandidates();
          for (const styleCandidate of numberedStyleCandidates) {
            try {
              bulletFormat.style = styleCandidate;
              await context.sync();
              bulletTypeApplied = true;
              break;
            } catch (error) {
              styleFallbackLastErrMsg = error instanceof Error ? error.message : String(error);
            }
          }
        }

        if (!bulletTypeApplied) {
          const attemptedTypeValues = hostBulletTypeCandidates
            .map((value) => formatParagraphPropertyValue(value))
            .join(", ");
          const nativeNumberedStylesText =
            bulletTypeValue === "Numbered"
              ? `; native numbered style candidates [${NATIVE_NUMBERED_STYLE_FALLBACKS.map((value) => `"${value}"`).join(", ")}] last_error=${styleFallbackLastErrMsg || "n/a"}`
              : "";
          recordError(meta.summary, {
            code: "PARAGRAPH_OP_APPLY_FAILED",
            category: "apply_error",
            message: `Failed paragraph property bullet.type using candidates [${attemptedTypeValues}] on ${targetDescriptor} for shape ${meta.shapeId}: ${typeLastErrMsg}${nativeNumberedStylesText}${getPowerPointApiSupportDebug("1.10")}`,
            slideIndex: meta.slideIndex,
            slideId: meta.slideId,
            shapeId: meta.shapeId,
            action: meta.action,
          });
        }
      }
      if (bulletTypeApplied) {
        mutated = true;
      }
    }
    if (paragraphSet.bullet.style !== undefined && bulletTypeApplied) {
      if (
        await applyParagraphProperty(
          context,
          meta,
          "bullet.style",
          paragraphSet.bullet.style,
          targetDescriptor,
          () => {
            bulletFormat.style = paragraphSet.bullet?.style;
          },
          "1.10"
        )
      ) {
        mutated = true;
      }
    }
  }

  if (paragraphSet.font) {
    const fontObj = textRange.font;
    try {
      if (paragraphSet.font.size !== undefined) fontObj.size = paragraphSet.font.size;
      if (paragraphSet.font.family) fontObj.name = paragraphSet.font.family;
      if (paragraphSet.font.color) fontObj.color = paragraphSet.font.color;
      if (paragraphSet.font.bold !== undefined) fontObj.bold = paragraphSet.font.bold;
      if (paragraphSet.font.italic !== undefined) fontObj.italic = paragraphSet.font.italic;
      await context.sync();
      mutated = true;
    } catch (fontErr) {
      const errMsg = fontErr instanceof Error ? fontErr.message : String(fontErr);
      recordError(meta.summary, {
        code: "PARAGRAPH_OP_APPLY_FAILED",
        category: "apply_error",
        message: `Failed paragraph font on ${targetDescriptor} for shape ${meta.shapeId}: ${errMsg}`,
        slideIndex: meta.slideIndex,
        slideId: meta.slideId,
        shapeId: meta.shapeId,
        action: meta.action,
      });
    }
  }

  return mutated;
}

function shouldApplyAllTargetPerParagraph(paragraphSet: ParagraphSet): boolean {
  // Some paragraph-format members are rejected on mixed text ranges even when valid per paragraph.
  // Font is also per-paragraph since applying to mixed ranges can fail.
  return Boolean(
    paragraphSet.indentLevel !== undefined ||
      paragraphSet.bullet?.type !== undefined ||
      paragraphSet.bullet?.style !== undefined ||
      paragraphSet.font !== undefined
  );
}

async function applyParagraphSetPerParagraph(
  textRange: PowerPoint.TextRange,
  paragraphRanges: ParagraphRange[],
  paragraphSet: ParagraphSet,
  context: PowerPoint.RequestContext,
  meta: ParagraphApplyMeta,
  descriptorSuffix?: string
): Promise<boolean> {
  let anyMutationApplied = false;

  for (const paragraphRange of paragraphRanges) {
    const targetLength = getParagraphApplyLength(paragraphRange, paragraphSet);
    if (targetLength <= 0) {
      continue;
    }
    try {
      const targetRange = textRange.getSubstring(paragraphRange.start, targetLength);
      if (
        await applyParagraphSetToTextRange(
          targetRange,
          paragraphSet,
          context,
          meta,
          `paragraph index ${paragraphRange.index}${descriptorSuffix || ""}`
        )
      ) {
        anyMutationApplied = true;
      }
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : String(error);
      recordError(meta.summary, {
        code: "PARAGRAPH_OP_APPLY_FAILED",
        category: "apply_error",
        message: `Failed paragraph operation for paragraph ${paragraphRange.index} on shape ${meta.shapeId}: ${errMsg}`,
        slideIndex: meta.slideIndex,
        slideId: meta.slideId,
        shapeId: meta.shapeId,
        action: meta.action,
      });
    }
  }

  return anyMutationApplied;
}

async function applyParagraphOps(
  shape: PowerPoint.Shape,
  paragraphOps: ParagraphOp[],
  context: PowerPoint.RequestContext,
  meta: ParagraphApplyMeta
): Promise<boolean> {
  const textRange = shape.textFrame.textRange;
  textRange.load("text");
  await context.sync();

  const text = textRange.text || "";
  const paragraphRanges = getParagraphRanges(text);
  let anyMutationApplied = false;

  for (const paragraphOp of paragraphOps) {
    if (paragraphOp.target.type === "all") {
      if (shouldApplyAllTargetPerParagraph(paragraphOp.set)) {
        if (
          await applyParagraphSetPerParagraph(
            textRange,
            paragraphRanges,
            paragraphOp.set,
            context,
            meta
          )
        ) {
          anyMutationApplied = true;
        }
        continue;
      }

      const errorsBeforeAllApply = meta.summary.errors.count;
      const allParagraphsMutated = await applyParagraphSetToTextRange(
        textRange,
        paragraphOp.set,
        context,
        meta,
        "all paragraphs"
      );
      if (allParagraphsMutated) {
        anyMutationApplied = true;
        continue;
      }

      const shouldRetryPerParagraph =
        meta.summary.errors.count > errorsBeforeAllApply && paragraphRanges.length > 1;
      if (!shouldRetryPerParagraph) {
        continue;
      }

      if (
        await applyParagraphSetPerParagraph(
          textRange,
          paragraphRanges,
          paragraphOp.set,
          context,
          meta,
          " (fallback from all)"
        )
      ) {
        anyMutationApplied = true;
      }
      continue;
    }

    for (const targetIndex of paragraphOp.target.indices) {
      const paragraphRange = paragraphRanges[targetIndex];
      if (!paragraphRange) {
        recordSkipped(meta.summary, {
          code: "PARAGRAPH_TARGET_INDEX_OUT_OF_RANGE",
          category: "target_mismatch",
          message: `Paragraph index ${targetIndex} is out of range for shape ${meta.shapeId}.`,
          slideIndex: meta.slideIndex,
          slideId: meta.slideId,
          shapeId: meta.shapeId,
          action: meta.action,
        });
        continue;
      }

      try {
        const targetLength = getParagraphApplyLength(paragraphRange, paragraphOp.set);
        const targetRange =
          targetLength > 0 ? textRange.getSubstring(paragraphRange.start, targetLength) : textRange;
        if (
          await applyParagraphSetToTextRange(
            targetRange,
            paragraphOp.set,
            context,
            meta,
            `paragraph index ${targetIndex}`
          )
        ) {
          anyMutationApplied = true;
        }
      } catch (error) {
        const errMsg = error instanceof Error ? error.message : String(error);
        recordError(meta.summary, {
          code: "PARAGRAPH_OP_APPLY_FAILED",
          category: "apply_error",
          message: `Failed paragraph operation for paragraph ${targetIndex} on shape ${meta.shapeId}: ${errMsg}`,
          slideIndex: meta.slideIndex,
          slideId: meta.slideId,
          shapeId: meta.shapeId,
          action: meta.action,
        });
      }
    }
  }

  return anyMutationApplied;
}

function recordUnsupportedCreateParagraphOps(
  summary: ApplyResultSummary,
  slideIndex: number,
  slideId: string | undefined,
  shapeId: string,
  createShapeType: "table" | "chart" | "image",
  paragraphOps?: ParagraphOp[]
): void {
  if (!paragraphOps || paragraphOps.length === 0) {
    return;
  }
  recordSkipped(
    summary,
    {
      code: "MODEL_OUTPUT_INVALID_PARAGRAPH_OP",
      category: "model_output_mismatch",
      message: `paragraphOps are not supported when creating ${createShapeType} shape ${shapeId}.`,
      slideIndex,
      slideId,
      shapeId,
      action: "create",
    },
    paragraphOps.length
  );
}

async function applyParagraphOpsWithHandling(
  shape: PowerPoint.Shape,
  paragraphOps: ParagraphOp[] | undefined,
  context: PowerPoint.RequestContext,
  summary: ApplyResultSummary,
  slideIndex: number,
  slideId: string | undefined,
  shapeId: string,
  action: "create" | "modify"
): Promise<boolean> {
  if (!paragraphOps || paragraphOps.length === 0) {
    return false;
  }
  try {
    return await applyParagraphOps(shape, paragraphOps, context, {
      summary,
      slideIndex,
      slideId,
      shapeId,
      action,
    });
  } catch (paragraphError) {
    const errMsg = paragraphError instanceof Error ? paragraphError.message : String(paragraphError);
    recordError(summary, {
      code: "PARAGRAPH_OP_APPLY_FAILED",
      category: "apply_error",
      message: `Failed paragraph operations for shape ${shapeId}: ${errMsg}`,
      slideIndex,
      slideId,
      shapeId,
      action,
    });
    return false;
  }
}

/**
 * Extract table cell contents from a table shape using the official PowerPoint Table API
 * Reference: https://learn.microsoft.com/en-us/office/dev/add-ins/powerpoint/work-with-tables
 */
function normalizePointValue(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function normalizeColorToken(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
}

function normalizeBorderStyle(value: unknown): { color?: string; weight?: number } | undefined {
  if (!value || typeof value !== "object") {
    return undefined;
  }
  const maybeBorder = value as { color?: unknown; weight?: unknown };
  const color = normalizeColorToken(maybeBorder.color);
  const weight = normalizePointValue(maybeBorder.weight);
  if (!color && weight === undefined) {
    return undefined;
  }
  return {
    color,
    weight,
  };
}

function normalizeCellPropertiesForSnapshot(value: TableCellProperties | undefined): TableCellProperties | undefined {
  if (!value) {
    return undefined;
  }

  const normalized: TableCellProperties = {};

  if (value.fill) {
    const fill: NonNullable<TableCellProperties["fill"]> = {};
    const color = normalizeColorToken(value.fill.color);
    if (color) {
      fill.color = color;
    }
    if (typeof value.fill.transparency === "number" && Number.isFinite(value.fill.transparency)) {
      fill.transparency = value.fill.transparency;
    }
    if (Object.keys(fill).length > 0) {
      normalized.fill = fill;
    }
  }

  if (value.font) {
    const font: NonNullable<TableCellProperties["font"]> = {};
    const color = normalizeColorToken(value.font.color);
    if (color) {
      font.color = color;
    }
    if (typeof value.font.name === "string" && value.font.name.trim()) {
      font.name = value.font.name.trim();
    }
    if (typeof value.font.size === "number" && Number.isFinite(value.font.size)) {
      font.size = value.font.size;
    }
    if (typeof value.font.bold === "boolean") {
      font.bold = value.font.bold;
    }
    if (typeof value.font.italic === "boolean") {
      font.italic = value.font.italic;
    }
    if (typeof value.font.allCaps === "boolean") {
      font.allCaps = value.font.allCaps;
    }
    if (Object.keys(font).length > 0) {
      normalized.font = font;
    }
  }

  if (value.borders) {
    const borders: NonNullable<TableCellProperties["borders"]> = {};
    const top = normalizeBorderStyle(value.borders.top);
    const bottom = normalizeBorderStyle(value.borders.bottom);
    const left = normalizeBorderStyle(value.borders.left);
    const right = normalizeBorderStyle(value.borders.right);
    if (top) borders.top = top;
    if (bottom) borders.bottom = bottom;
    if (left) borders.left = left;
    if (right) borders.right = right;
    if (Object.keys(borders).length > 0) {
      normalized.borders = borders;
    }
  }

  if (typeof value.horizontalAlignment === "string") {
    normalized.horizontalAlignment = value.horizontalAlignment;
  }
  if (typeof value.verticalAlignment === "string") {
    normalized.verticalAlignment = value.verticalAlignment;
  }
  if (typeof value.indentLevel === "number" && Number.isFinite(value.indentLevel)) {
    normalized.indentLevel = value.indentLevel;
  }

  if (value.margins) {
    const margins: NonNullable<TableCellProperties["margins"]> = {};
    for (const key of ["left", "right", "top", "bottom"] as const) {
      const marginVal = normalizePointValue(value.margins[key]);
      if (marginVal !== undefined) {
        margins[key] = marginVal;
      }
    }
    if (Object.keys(margins).length > 0) {
      normalized.margins = margins;
    }
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

function extractCellSnapshot(cell: any): TableCellProperties | undefined {
  const snapshot: TableCellProperties = {};

  const fillColor = normalizeColorToken(cell?.fill?.color ?? cell?.fill?.foregroundColor);
  const fillTransparency = normalizePointValue(cell?.fill?.transparency);
  if (fillColor || fillTransparency !== undefined) {
    snapshot.fill = {
      color: fillColor,
      transparency: fillTransparency,
    };
  }

  const fontColor = normalizeColorToken(cell?.font?.color);
  const fontName = typeof cell?.font?.name === "string" ? cell.font.name : undefined;
  const fontSize = normalizePointValue(cell?.font?.size);
  const fontBold = typeof cell?.font?.bold === "boolean" ? cell.font.bold : undefined;
  const fontItalic = typeof cell?.font?.italic === "boolean" ? cell.font.italic : undefined;
  const fontAllCaps = typeof cell?.font?.allCaps === "boolean" ? cell.font.allCaps : undefined;
  if (fontColor || fontName || fontSize !== undefined || fontBold !== undefined || fontItalic !== undefined || fontAllCaps !== undefined) {
    snapshot.font = {
      color: fontColor,
      name: fontName,
      size: fontSize,
      bold: fontBold,
      italic: fontItalic,
      allCaps: fontAllCaps,
    };
  }

  const topBorder = normalizeBorderStyle(cell?.borders?.top);
  const bottomBorder = normalizeBorderStyle(cell?.borders?.bottom);
  const leftBorder = normalizeBorderStyle(cell?.borders?.left);
  const rightBorder = normalizeBorderStyle(cell?.borders?.right);
  if (topBorder || bottomBorder || leftBorder || rightBorder) {
    snapshot.borders = {
      top: topBorder,
      bottom: bottomBorder,
      left: leftBorder,
      right: rightBorder,
    };
  }

  const hAlign = typeof cell?.horizontalAlignment === "string"
    ? cell.horizontalAlignment.toLowerCase()
    : undefined;
  if (hAlign && ["left", "center", "right", "justify", "distributed"].includes(hAlign)) {
    snapshot.horizontalAlignment = hAlign as TableCellProperties["horizontalAlignment"];
  }

  const vAlignRaw = typeof cell?.verticalAlignment === "string"
    ? cell.verticalAlignment.toLowerCase()
    : undefined;
  const vAlignMap: Record<string, TableCellProperties["verticalAlignment"]> = {
    top: "top",
    middle: "middle",
    center: "middle",
    bottom: "bottom",
  };
  if (vAlignRaw && vAlignMap[vAlignRaw]) {
    snapshot.verticalAlignment = vAlignMap[vAlignRaw];
  }

  return normalizeCellPropertiesForSnapshot(snapshot);
}

function isNumericLikeCellValue(value: string): boolean {
  const cleaned = value.replace(/[$,%\s,()]/g, "").replace(/−/g, "-");
  if (!cleaned) {
    return false;
  }
  return Number.isFinite(Number(cleaned));
}

function buildTableSemanticHints(rows: string[][]): TableCellData["semanticHints"] | undefined {
  if (rows.length === 0) {
    return undefined;
  }

  const headerRowIndices: number[] = [];
  if (rows.length > 1) {
    const firstRow = rows[0] || [];
    const textualCells = firstRow.filter((value) => value.trim() && !isNumericLikeCellValue(value));
    if (textualCells.length >= Math.ceil(Math.max(1, firstRow.length) / 2)) {
      headerRowIndices.push(0);
    }
  }

  const startDataRow = headerRowIndices.includes(0) ? 1 : 0;
  const numericColumnIndices: number[] = [];
  const colCount = Math.max(...rows.map((row) => row.length), 0);
  for (let col = 0; col < colCount; col++) {
    let numericCount = 0;
    let nonEmptyCount = 0;
    for (let row = startDataRow; row < rows.length; row++) {
      const cell = rows[row]?.[col] ?? "";
      if (!cell.trim()) {
        continue;
      }
      nonEmptyCount += 1;
      if (isNumericLikeCellValue(cell)) {
        numericCount += 1;
      }
    }
    if (nonEmptyCount > 0 && numericCount / nonEmptyCount >= 0.6) {
      numericColumnIndices.push(col);
    }
  }

  if (headerRowIndices.length === 0 && numericColumnIndices.length === 0) {
    return undefined;
  }

  return {
    headerRowIndices: headerRowIndices.length > 0 ? headerRowIndices : undefined,
    numericColumnIndices: numericColumnIndices.length > 0 ? numericColumnIndices : undefined,
  };
}

function normalizeMergedAreaEntry(area: any): TableMergedArea | undefined {
  const rowIndex = normalizePointValue(
    area?.rowIndex ?? area?.startRowIndex ?? area?.row ?? area?.r
  );
  const columnIndex = normalizePointValue(
    area?.columnIndex ?? area?.startColumnIndex ?? area?.column ?? area?.c
  );
  const rowCount = normalizePointValue(area?.rowCount ?? area?.rowSpan ?? area?.rows);
  const columnCount = normalizePointValue(area?.columnCount ?? area?.columnSpan ?? area?.cols);

  if (
    rowIndex === undefined ||
    columnIndex === undefined ||
    rowCount === undefined ||
    columnCount === undefined
  ) {
    return undefined;
  }
  if (rowCount <= 0 || columnCount <= 0) {
    return undefined;
  }

  return {
    rowIndex: Math.floor(rowIndex),
    columnIndex: Math.floor(columnIndex),
    rowCount: Math.floor(rowCount),
    columnCount: Math.floor(columnCount),
  };
}

async function extractMergedAreas(
  table: any,
  context: PowerPoint.RequestContext,
  log: Logger
): Promise<TableMergedArea[] | undefined> {
  try {
    if (typeof table?.getMergedAreas !== "function") {
      return undefined;
    }
    const mergedAreasObj = table.getMergedAreas();
    if (!mergedAreasObj) {
      return undefined;
    }

    if (typeof mergedAreasObj.load === "function") {
      mergedAreasObj.load("items");
      await context.sync();
    }

    const rawItems: unknown[] = Array.isArray(mergedAreasObj.items)
      ? mergedAreasObj.items
      : Array.isArray(mergedAreasObj.value)
        ? mergedAreasObj.value
        : [];
    const normalized = rawItems
      .map((item) => normalizeMergedAreaEntry(item))
      .filter((item): item is TableMergedArea => Boolean(item));

    return normalized.length > 0 ? normalized : undefined;
  } catch (error) {
    log(`  ⚠ Could not extract merged areas: ${error}`);
    return undefined;
  }
}

async function extractAxisSizes(
  axisCollection: any,
  itemCount: number,
  sizeKeys: string[],
  context: PowerPoint.RequestContext
): Promise<number[] | undefined> {
  if (!axisCollection || itemCount <= 0) {
    return undefined;
  }

  try {
    if (typeof axisCollection.load === "function") {
      axisCollection.load("items");
      await context.sync();
    }
  } catch {
    // Fall through; some hosts still allow direct index access.
  }

  const sizes: Array<number | undefined> = [];
  for (let idx = 0; idx < itemCount; idx++) {
    let axisItem = axisCollection?.items?.[idx];
    if (!axisItem && typeof axisCollection?.getItemAt === "function") {
      axisItem = axisCollection.getItemAt(idx);
    }
    if (!axisItem) {
      sizes.push(undefined);
      continue;
    }

    try {
      if (typeof axisItem.load === "function") {
        axisItem.load(sizeKeys.join(","));
        await context.sync();
      }
    } catch {
      // Ignore load failures and try direct reads.
    }

    let sizeValue: number | undefined;
    for (const key of sizeKeys) {
      sizeValue = normalizePointValue((axisItem as any)[key]);
      if (sizeValue !== undefined) {
        break;
      }
    }
    sizes.push(sizeValue);
  }

  const hasKnownValues = sizes.some((value) => value !== undefined);
  if (!hasKnownValues) {
    return undefined;
  }
  return sizes.map((value) => value ?? 0);
}

async function extractTableStyleSettings(
  table: any,
  context: PowerPoint.RequestContext,
  log: Logger
): Promise<TableStyleSettingsData | undefined> {
  try {
    const settings = table?.styleSettings;
    if (!settings) {
      return undefined;
    }
    if (typeof settings.load === "function") {
      settings.load(
        "style,areRowsBanded,areColumnsBanded,isFirstRowHighlighted,isFirstColumnHighlighted,isLastRowHighlighted,isLastColumnHighlighted"
      );
      await context.sync();
    }

    const snapshot: TableStyleSettingsData = {
      style: typeof settings.style === "string" ? settings.style : undefined,
      areRowsBanded:
        typeof settings.areRowsBanded === "boolean" ? settings.areRowsBanded : undefined,
      areColumnsBanded:
        typeof settings.areColumnsBanded === "boolean" ? settings.areColumnsBanded : undefined,
      isFirstRowHighlighted:
        typeof settings.isFirstRowHighlighted === "boolean"
          ? settings.isFirstRowHighlighted
          : undefined,
      isFirstColumnHighlighted:
        typeof settings.isFirstColumnHighlighted === "boolean"
          ? settings.isFirstColumnHighlighted
          : undefined,
      isLastRowHighlighted:
        typeof settings.isLastRowHighlighted === "boolean" ? settings.isLastRowHighlighted : undefined,
      isLastColumnHighlighted:
        typeof settings.isLastColumnHighlighted === "boolean"
          ? settings.isLastColumnHighlighted
          : undefined,
    };

    return Object.values(snapshot).some((value) => value !== undefined) ? snapshot : undefined;
  } catch (error) {
    log(`  ⚠ Could not extract table style settings: ${error}`);
    return undefined;
  }
}

async function extractTableData(
  shape: PowerPoint.Shape,
  context: PowerPoint.RequestContext,
  log: Logger = defaultLogger
): Promise<TableCellData | undefined> {
  try {
    // Use the official getTable() API
    const shapeAny = shape as any;

    // Check if getTable method exists
    if (typeof shapeAny.getTable !== "function") {
      log("  ✗ getTable() method not available on this shape");
      return undefined;
    }

    const table = shapeAny.getTable();
    table.load("rowCount, columnCount");
    await context.sync();

    const rowCount = table.rowCount as number;
    const colCount = table.columnCount as number;
    log(`  Table dimensions: ${rowCount}x${colCount}`);

    const rows: string[][] = [];
    const cellSnapshots: Array<Array<TableCellProperties | undefined>> = [];

    // Extract each cell's text and style snapshot using getCellOrNullObject.
    for (let row = 0; row < rowCount; row++) {
      const rowData: string[] = [];
      const styleData: Array<TableCellProperties | undefined> = [];
      for (let col = 0; col < colCount; col++) {
        try {
          const cell = table.getCellOrNullObject(row, col);
          cell.load("text,horizontalAlignment,verticalAlignment");
          try {
            cell.font?.load?.("name,size,bold,italic,color,allCaps");
          } catch {
            // Ignore optional property load failures.
          }
          try {
            cell.fill?.load?.("color,foregroundColor,transparency");
          } catch {
            // Ignore optional property load failures.
          }
          try {
            const borderSides = ["top", "bottom", "left", "right"] as const;
            for (const side of borderSides) {
              cell.borders?.[side]?.load?.("color,weight");
            }
          } catch {
            // Ignore optional property load failures.
          }
          await context.sync();

          if (!cell.isNullObject) {
            rowData.push(cell.text || "");
            styleData.push(extractCellSnapshot(cell));
          } else {
            rowData.push("");
            styleData.push(undefined);
          }
        } catch (cellErr) {
          log(`  ✗ Cell ${row},${col} error: ${cellErr}`);
          rowData.push("");
          styleData.push(undefined);
        }
      }
      rows.push(rowData);
      cellSnapshots.push(styleData);
    }

    const columnWidths = await extractAxisSizes((table as any).columns, colCount, ["width", "columnWidth"], context);
    const rowHeights = await extractAxisSizes((table as any).rows, rowCount, ["height", "rowHeight"], context);
    const mergedAreas = await extractMergedAreas(table, context, log);
    const styleSettings = await extractTableStyleSettings(table, context, log);

    let tableStyle: string | undefined;
    try {
      const tableAny = table as any;
      tableStyle =
        (typeof tableAny?.tableStyle === "string" ? tableAny.tableStyle : undefined) ||
        (typeof tableAny?.style === "string" ? tableAny.style : undefined) ||
        styleSettings?.style;
    } catch {
      tableStyle = styleSettings?.style;
    }

    const firstCellStyle = normalizeCellPropertiesForSnapshot(cellSnapshots?.[0]?.[0]);
    const cellOverrides =
      firstCellStyle &&
      cellSnapshots
        .flatMap((rowStyles, row) =>
          rowStyles
            .map((style, col) => ({ row, col, set: normalizeCellPropertiesForSnapshot(style) }))
            .filter((entry) => {
              if (!entry.set) {
                return false;
              }
              return JSON.stringify(entry.set) !== JSON.stringify(firstCellStyle);
            })
        )
        .slice(0, 64);

    const styleSnapshot =
      tableStyle || styleSettings || firstCellStyle || (cellOverrides && cellOverrides.length > 0)
        ? {
            tableStyle,
            styleSettings,
            uniformCellProperties: firstCellStyle,
            cellOverrides: cellOverrides && cellOverrides.length > 0 ? cellOverrides : undefined,
          }
        : undefined;

    const semanticHints = buildTableSemanticHints(rows);

    log(`  ✓ Table extracted: ${rowCount}x${colCount}`);
    log(`  Cells: ${JSON.stringify(rows)}`);

    return {
      rowCount,
      colCount,
      rows,
      columnWidths: columnWidths && columnWidths.length > 0 ? columnWidths : undefined,
      rowHeights: rowHeights && rowHeights.length > 0 ? rowHeights : undefined,
      mergedAreas,
      styleSnapshot,
      semanticHints,
    };
  } catch (e) {
    log(`  ✗ Table extraction failed: ${e}`);
    return undefined;
  }
}

async function applyTableTextUpdatesToTable(
  table: any,
  textUpdates: { row: number; col: number; text: string }[],
  context: PowerPoint.RequestContext,
  log: Logger = defaultLogger
): Promise<void> {
  for (const update of textUpdates) {
    try {
      const cell = table.getCellOrNullObject(update.row, update.col);
      cell.load("isNullObject");
      await context.sync();

      if (!cell.isNullObject) {
        cell.text = update.text;
      } else {
        log(`  ⚠ Table text update skipped for null cell ${update.row},${update.col}`);
      }
    } catch (error) {
      log(`  ⚠ Table text update failed for ${update.row},${update.col}: ${error}`);
    }
  }

  await context.sync();
}

async function snapshotTableTextRows(
  table: any,
  context: PowerPoint.RequestContext
): Promise<string[][]> {
  table.load("rowCount,columnCount");
  await context.sync();

  const rowCount = Math.max(0, Math.floor(Number(table.rowCount || 0)));
  const colCount = Math.max(0, Math.floor(Number(table.columnCount || 0)));
  if (rowCount === 0 || colCount === 0) {
    return [];
  }

  const cellRefs: Array<{ row: number; col: number; cell: any }> = [];
  for (let row = 0; row < rowCount; row++) {
    for (let col = 0; col < colCount; col++) {
      const cell = table.getCellOrNullObject(row, col);
      cell.load("isNullObject,text");
      cellRefs.push({ row, col, cell });
    }
  }
  await context.sync();

  const rows = Array.from({ length: rowCount }, () => Array.from({ length: colCount }, () => ""));
  for (const ref of cellRefs) {
    if (!ref.cell.isNullObject) {
      rows[ref.row][ref.col] = typeof ref.cell.text === "string" ? ref.cell.text : "";
    }
  }
  return rows;
}

function buildProjectedTableRows(
  currentRows: string[][],
  nextRowCount: number,
  nextColCount: number
): string[][] {
  const currentRowCount = currentRows.length;
  const currentColCount = currentRows[0]?.length || 0;
  return Array.from({ length: nextRowCount }, (_, row) =>
    Array.from({ length: nextColCount }, (_, col) => {
      if (row < currentRowCount && col < currentColCount) {
        return currentRows[row]?.[col] ?? "";
      }
      return "";
    })
  );
}

function buildTableEditOpsFromRows(
  currentRows: string[][],
  targetRows: string[][]
): {
  structureOps: NonNullable<TableData["structureOps"]>;
  textUpdates: NonNullable<TableData["textUpdates"]>;
} {
  const currentRowCount = currentRows.length;
  const currentColCount = currentRows[0]?.length || 0;
  const nextRowCount = targetRows.length;
  const nextColCount = targetRows[0]?.length || 0;
  const structureOps: NonNullable<TableData["structureOps"]> = [];

  if (nextRowCount > currentRowCount) {
    structureOps.push({
      op: "addRow",
      index: currentRowCount,
      count: nextRowCount - currentRowCount,
    });
  } else if (nextRowCount < currentRowCount) {
    structureOps.push({
      op: "deleteRow",
      index: nextRowCount,
      count: currentRowCount - nextRowCount,
    });
  }

  if (nextColCount > currentColCount) {
    structureOps.push({
      op: "addColumn",
      index: currentColCount,
      count: nextColCount - currentColCount,
    });
  } else if (nextColCount < currentColCount) {
    structureOps.push({
      op: "deleteColumn",
      index: nextColCount,
      count: currentColCount - nextColCount,
    });
  }

  const projectedRows = buildProjectedTableRows(currentRows, nextRowCount, nextColCount);
  const textUpdates: NonNullable<TableData["textUpdates"]> = [];
  for (let row = 0; row < nextRowCount; row++) {
    for (let col = 0; col < nextColCount; col++) {
      const nextText = targetRows[row]?.[col] ?? "";
      if ((projectedRows[row]?.[col] ?? "") !== nextText) {
        textUpdates.push({ row, col, text: nextText });
      }
    }
  }

  return { structureOps, textUpdates };
}

function hasTableCellPropertyValues(props: TableCellProperties | undefined): boolean {
  if (!props) {
    return false;
  }
  if (props.fill && (props.fill.color !== undefined || props.fill.transparency !== undefined)) {
    return true;
  }
  if (
    props.font &&
    (props.font.color !== undefined ||
      props.font.name !== undefined ||
      props.font.size !== undefined ||
      props.font.bold !== undefined ||
      props.font.italic !== undefined ||
      props.font.allCaps !== undefined)
  ) {
    return true;
  }
  if (
    props.borders &&
    Object.values(props.borders).some(
      (border) => border && (border.color !== undefined || border.weight !== undefined)
    )
  ) {
    return true;
  }
  if (props.horizontalAlignment !== undefined || props.verticalAlignment !== undefined) {
    return true;
  }
  if (props.indentLevel !== undefined) {
    return true;
  }
  if (props.margins && Object.values(props.margins).some((value) => value !== undefined)) {
    return true;
  }
  return false;
}

function buildTableStyleUpdatesFromCreateProperties(
  tableData: TableData
): NonNullable<TableData["styleUpdates"]> {
  const styleUpdates: NonNullable<TableData["styleUpdates"]> = [];
  if (hasTableCellPropertyValues(tableData.uniformCellProperties)) {
    styleUpdates.push({
      target: { type: "all" },
      set: tableData.uniformCellProperties as TableCellProperties,
    });
  }
  for (let row = 0; row < (tableData.specificCellProperties || []).length; row += 1) {
    const rowProps = tableData.specificCellProperties?.[row] || [];
    for (let col = 0; col < rowProps.length; col += 1) {
      const cellProps = rowProps[col];
      if (!hasTableCellPropertyValues(cellProps)) {
        continue;
      }
      styleUpdates.push({
        target: { type: "cell", row, col },
        set: cellProps,
      });
    }
  }
  return styleUpdates;
}

function buildMergedAreaStructureOps(
  mergedAreas: TableMergedArea[] | undefined
): NonNullable<TableData["structureOps"]> {
  return (mergedAreas || [])
    .filter(
      (area) =>
        Number.isFinite(area.rowIndex) &&
        Number.isFinite(area.columnIndex) &&
        Number.isFinite(area.rowCount) &&
        Number.isFinite(area.columnCount) &&
        area.rowCount > 0 &&
        area.columnCount > 0 &&
        (area.rowCount > 1 || area.columnCount > 1)
    )
    .map((area) => ({
      op: "mergeCells" as const,
      rowIndex: area.rowIndex,
      columnIndex: area.columnIndex,
      rowCount: area.rowCount,
      columnCount: area.columnCount,
    }));
}

function buildCreateStyleSettingsUpdate(tableData: TableData): TableStyleSettingsData | undefined {
  const { style: _baselineStyle, ...baselineSettings } = tableData.styleSettings || {};
  const merged: TableStyleSettingsData = {
    ...baselineSettings,
    ...(tableData.styleSettingsUpdate || {}),
  };
  return Object.values(merged).some((value) => value !== undefined) ? merged : undefined;
}

async function createTableWithFollowUp(
  slide: PowerPoint.Slide,
  tableData: TableData,
  shapeMod: { left?: number; top?: number; width?: number; height?: number },
  context: PowerPoint.RequestContext,
  log?: (message: string) => void
): Promise<PowerPoint.Shape | undefined> {
  const createdShape = await insertTable(slide, tableData, shapeMod, context, log);
  const createdTable = (createdShape as any)?.getTable?.();
  if (!createdShape || !createdTable) {
    return createdShape;
  }

  const structureOps = [
    ...(tableData.structureOps || []),
    ...buildMergedAreaStructureOps(tableData.mergedAreas),
  ];
  const clearOps = tableData.clearOps || [];
  const textUpdates = mergeTableTextUpdates(
    buildTableTextUpdatesFromRows(tableData.rows || []),
    tableData.textUpdates
  );
  const styleSettingsUpdate = buildCreateStyleSettingsUpdate(tableData);
  const styleUpdates = [
    ...buildTableStyleUpdatesFromCreateProperties(tableData),
    ...(tableData.styleUpdates || []),
  ];

  if (structureOps.length > 0) {
    await applyTableStructureOps(createdTable, structureOps, context);
  }
  if (clearOps.length > 0) {
    await applyTableClearOps(createdTable, clearOps, context);
  }
  if (textUpdates.length > 0) {
    await applyTableTextUpdatesToTable(createdTable, textUpdates, context);
  }
  if (styleSettingsUpdate) {
    await applyTableStyleSettingsUpdate(createdTable, styleSettingsUpdate, context);
  }
  if (styleUpdates.length > 0) {
    await applyTableStyleUpdates(createdTable, styleUpdates, context);
  }
  if (tableData.layoutSpec) {
    await applyTableRelayout(createdTable, createdShape, tableData.layoutSpec, shapeMod, context);
  }

  return createdShape;
}

/**
 * Insert a host-safe table shell into a slide.
 * Content and dense styling are applied in a follow-up step.
 */
async function insertTable(
  slide: PowerPoint.Slide,
  tableData: TableData,
  shapeMod: { left?: number; top?: number; width?: number; height?: number },
  context: PowerPoint.RequestContext,
  log?: (message: string) => void
): Promise<PowerPoint.Shape | undefined> {
  const logMsg = log || console.log;
  const rows = tableData.rows;
  if (!rows || rows.length === 0 || rows[0]?.length === 0) {
    throw new Error("Table create/replace requires non-empty rows.");
  }

  const rowCount = rows.length;
  const colCount = rows[0]?.length || 1;

  const shapes = slide.shapes as any;

  // Check if addTable exists
  if (typeof shapes.addTable !== "function") {
    // Fallback: create a text box with table-like content
    logMsg("[TABLE] addTable not available, creating text representation");
    const tableText = rows.map((row) => row.join("\t")).join("\n");
    const textBox = slide.shapes.addTextBox(tableText);
    // Use consistent defaults for table-like fallback content
    const left = shapeMod.left ?? 50;
    const top = shapeMod.top ?? 80;
    textBox.left = left;
    textBox.top = top;
    textBox.width = shapeMod.width ?? 500;
    textBox.height = shapeMod.height ?? 200;
    await context.sync();
    return textBox as PowerPoint.Shape;
  }

  // Build TableAddOptions matching the PowerPoint API
  const tableOptions: any = {
    values: buildEmptyTableShellRows(rows),
  };

  const tableStyleToken = tableData.tableStyle || tableData.styleSettings?.style;
  if (tableStyleToken) {
    tableOptions.style = tableStyleToken;
  }

  // Set deterministic column widths to avoid auto-sizing drift.
  const tableLeft = shapeMod.left ?? 50;
  const targetWidth = shapeMod.width ?? 760;
  logMsg(`[TABLE] Target table width: ${targetWidth}pt at left=${tableLeft}`);

  let adjustedWidths: number[];

  if (tableData.columnWidths && tableData.columnWidths.length > 0) {
    // AI specified column widths - scale if needed
    const totalColumnWidth = tableData.columnWidths.reduce((sum, w) => sum + w, 0);

    if (totalColumnWidth > targetWidth) {
      // Scale down proportionally to fit
      const scale = targetWidth / totalColumnWidth;
      adjustedWidths = tableData.columnWidths.map((w) => Math.round(w * scale));
      logMsg(
        `[TABLE] ⚠️ Column widths scaled: ${totalColumnWidth}pt → ${adjustedWidths.reduce((s, w) => s + w, 0)}pt (max: ${targetWidth}pt)`
      );
    } else {
      adjustedWidths = tableData.columnWidths;
    }
  } else {
    // AI didn't specify column widths - calculate even distribution
    const colWidth = Math.floor(targetWidth / colCount);
    adjustedWidths = Array(colCount).fill(colWidth);
  }

  tableOptions.columns = adjustedWidths.map((width) => ({ columnWidth: width }));

  // Add row heights
  if (tableData.rowHeights && tableData.rowHeights.length > 0) {
    tableOptions.rows = tableData.rowHeights.map((height) => ({ rowHeight: height }));
  }

  // Create table with full options
  const tableShape = shapes.addTable(rowCount, colCount, tableOptions);

  // Position the table shape
  if (shapeMod.left !== undefined) tableShape.left = shapeMod.left;
  if (shapeMod.top !== undefined) tableShape.top = shapeMod.top;
  if (shapeMod.width !== undefined) tableShape.width = shapeMod.width;
  if (shapeMod.height !== undefined) tableShape.height = shapeMod.height;

  await context.sync();
  logMsg(`[TABLE] Created table shell successfully`);
  return tableShape as PowerPoint.Shape;
}

/**
 * Convert our TableCellProperties to PowerPoint API format
 */
function buildCellProperties(props: import("../types").TableCellProperties): any {
  if (!props) return {};

  const result: any = {};

  // Fill
  if (props.fill) {
    result.fill = {};
    if (props.fill.color) result.fill.color = props.fill.color;
    if (props.fill.transparency !== undefined) result.fill.transparency = props.fill.transparency;
  }

  // Font
  if (props.font) {
    result.font = {};
    if (props.font.color) result.font.color = props.font.color;
    if (props.font.name) result.font.name = props.font.name;
    if (props.font.size) result.font.size = props.font.size;
    if (props.font.bold !== undefined) result.font.bold = props.font.bold;
    if (props.font.italic !== undefined) result.font.italic = props.font.italic;
    if (props.font.allCaps !== undefined) result.font.allCaps = props.font.allCaps;
  }

  // Borders
  if (props.borders) {
    result.borders = {};
    if (props.borders.top)
      result.borders.top = { color: props.borders.top.color, weight: props.borders.top.weight };
    if (props.borders.bottom)
      result.borders.bottom = {
        color: props.borders.bottom.color,
        weight: props.borders.bottom.weight,
      };
    if (props.borders.left)
      result.borders.left = { color: props.borders.left.color, weight: props.borders.left.weight };
    if (props.borders.right)
      result.borders.right = {
        color: props.borders.right.color,
        weight: props.borders.right.weight,
      };
  }

  // Alignment - convert to PowerPoint enum values
  if (props.horizontalAlignment) {
    // PowerPoint.ParagraphHorizontalAlignment values
    const alignMap: Record<string, string> = {
      left: "Left",
      center: "Center",
      right: "Right",
      justify: "Justify",
      distributed: "Distributed",
    };
    result.horizontalAlignment = alignMap[props.horizontalAlignment] || "Left";
  }

  if (props.verticalAlignment) {
    // PowerPoint.TextVerticalAlignment values
    const valignMap: Record<string, string> = {
      top: "Top",
      middle: "Middle",
      bottom: "Bottom",
    };
    result.verticalAlignment = valignMap[props.verticalAlignment] || "Top";
  }

  return result;
}

function getTableAction(tableData: TableData | undefined, shapeAction: ShapeModification["action"]): TableData["action"] {
  if (!tableData) {
    return undefined;
  }
  if (tableData.action) {
    return tableData.action;
  }
  const hasEditIntent = Boolean(
    (shapeAction === "modify" && tableData.rows && tableData.rows.length > 0) ||
    (tableData.textUpdates && tableData.textUpdates.length > 0) ||
      (tableData.styleUpdates && tableData.styleUpdates.length > 0) ||
      (tableData.structureOps && tableData.structureOps.length > 0) ||
      (tableData.clearOps && tableData.clearOps.length > 0) ||
      tableData.styleSettingsUpdate
  );
  if (hasEditIntent) {
    return "edit";
  }
  if (shapeAction === "modify") {
    if (tableData.layoutSpec) {
      return "relayout";
    }
    return "edit";
  }
  return "create";
}

function inferTableTextUpdates(shapeMod: ShapeModification): NonNullable<TableData["textUpdates"]> {
  if (shapeMod.table?.textUpdates && shapeMod.table.textUpdates.length > 0) {
    return shapeMod.table.textUpdates;
  }
  if (shapeMod.cellUpdates && shapeMod.cellUpdates.length > 0) {
    return shapeMod.cellUpdates;
  }
  return [];
}

function collectTableTargetCells(
  target: TableStyleTarget,
  rowCount: number,
  colCount: number
): Array<{ row: number; col: number }> {
  const cells: Array<{ row: number; col: number }> = [];
  const add = (row: number, col: number) => {
    if (row < 0 || col < 0 || row >= rowCount || col >= colCount) {
      return;
    }
    if (!cells.some((item) => item.row === row && item.col === col)) {
      cells.push({ row, col });
    }
  };

  switch (target.type) {
    case "all":
      for (let row = 0; row < rowCount; row++) {
        for (let col = 0; col < colCount; col++) {
          add(row, col);
        }
      }
      break;
    case "row":
      for (let col = 0; col < colCount; col++) {
        add(target.index, col);
      }
      break;
    case "column":
      for (let row = 0; row < rowCount; row++) {
        add(row, target.index);
      }
      break;
    case "cell":
      add(target.row, target.col);
      break;
    case "range":
      for (let row = target.startRow; row < target.startRow + target.rowCount; row++) {
        for (let col = target.startCol; col < target.startCol + target.colCount; col++) {
          add(row, col);
        }
      }
      break;
    default:
      break;
  }
  return cells;
}

async function applyTableCellPropertySet(
  table: any,
  row: number,
  col: number,
  set: TableCellProperties,
  context: PowerPoint.RequestContext
): Promise<boolean> {
  const cell = table.getCellOrNullObject(row, col);
  cell.load("isNullObject");
  await context.sync();
  if (cell.isNullObject) {
    return false;
  }

  const prepared = buildCellProperties(set);
  if (prepared.fill) {
    if (prepared.fill.color && typeof cell?.fill?.setSolidColor === "function") {
      cell.fill.setSolidColor(String(prepared.fill.color).replace(/^#/, ""));
    } else if (prepared.fill.color && cell?.fill) {
      cell.fill.color = prepared.fill.color;
    }
    if (prepared.fill.transparency !== undefined && cell?.fill) {
      cell.fill.transparency = prepared.fill.transparency;
    }
  }

  if (prepared.font && cell?.font) {
    if (prepared.font.color !== undefined) cell.font.color = prepared.font.color;
    if (prepared.font.name !== undefined) cell.font.name = prepared.font.name;
    if (prepared.font.size !== undefined) cell.font.size = prepared.font.size;
    if (prepared.font.bold !== undefined) cell.font.bold = prepared.font.bold;
    if (prepared.font.italic !== undefined) cell.font.italic = prepared.font.italic;
    if (prepared.font.allCaps !== undefined) cell.font.allCaps = prepared.font.allCaps;
  }

  if (prepared.borders && cell?.borders) {
    const borderSides = ["top", "bottom", "left", "right"] as const;
    for (const side of borderSides) {
      const borderPatch = prepared.borders[side];
      if (!borderPatch) {
        continue;
      }
      const border = cell.borders[side];
      if (!border) {
        continue;
      }
      if (borderPatch.color !== undefined) {
        border.color = borderPatch.color;
      }
      if (borderPatch.weight !== undefined) {
        border.weight = borderPatch.weight;
      }
    }
  }

  if (prepared.horizontalAlignment !== undefined) {
    cell.horizontalAlignment = prepared.horizontalAlignment;
  }
  if (prepared.verticalAlignment !== undefined) {
    cell.verticalAlignment = prepared.verticalAlignment;
  }

  if (set.margins && cell?.margins) {
    if (set.margins.left !== undefined) cell.margins.left = set.margins.left;
    if (set.margins.right !== undefined) cell.margins.right = set.margins.right;
    if (set.margins.top !== undefined) cell.margins.top = set.margins.top;
    if (set.margins.bottom !== undefined) cell.margins.bottom = set.margins.bottom;
  }
  if (set.indentLevel !== undefined) {
    cell.indentLevel = set.indentLevel;
  }
  return true;
}

async function applyTableStyleUpdates(
  table: any,
  styleUpdates: TableStyleUpdate[],
  context: PowerPoint.RequestContext
): Promise<boolean> {
  if (!styleUpdates || styleUpdates.length === 0) {
    return false;
  }
  table.load("rowCount,columnCount");
  await context.sync();
  const rowCount = table.rowCount as number;
  const colCount = table.columnCount as number;

  let applied = false;
  for (const styleUpdate of styleUpdates) {
    const targets = collectTableTargetCells(styleUpdate.target, rowCount, colCount);
    for (const targetCell of targets) {
      const touched = await applyTableCellPropertySet(
        table,
        targetCell.row,
        targetCell.col,
        styleUpdate.set,
        context
      );
      applied = applied || touched;
    }
  }
  await context.sync();
  return applied;
}

async function applyTableStyleSettingsUpdate(
  table: any,
  styleSettingsUpdate: TableStyleSettingsData | undefined,
  context: PowerPoint.RequestContext
): Promise<boolean> {
  if (!styleSettingsUpdate) {
    return false;
  }

  const tableAny = table as any;
  const settings = tableAny.styleSettings;
  let touched = false;

  if (styleSettingsUpdate.style) {
    if (settings && "style" in settings) {
      settings.style = styleSettingsUpdate.style;
      touched = true;
    } else if ("style" in tableAny) {
      tableAny.style = styleSettingsUpdate.style;
      touched = true;
    }
  }

  const boolFields: Array<keyof TableStyleSettingsData> = [
    "areRowsBanded",
    "areColumnsBanded",
    "isFirstRowHighlighted",
    "isFirstColumnHighlighted",
    "isLastRowHighlighted",
    "isLastColumnHighlighted",
  ];
  for (const field of boolFields) {
    const value = styleSettingsUpdate[field];
    if (typeof value !== "boolean") {
      continue;
    }
    if (settings && field in settings) {
      (settings as any)[field] = value;
      touched = true;
    } else if (field in tableAny) {
      tableAny[field] = value;
      touched = true;
    }
  }

  if (touched) {
    await context.sync();
  }
  return touched;
}

async function applySingleStructureOp(
  table: any,
  structureOp: TableStructureOp,
  context: PowerPoint.RequestContext
): Promise<boolean> {
  const tableAny = table as any;
  const rowsCollection = tableAny.rows;
  const colsCollection = tableAny.columns;

  switch (structureOp.op) {
    case "addRow": {
      const count = Math.max(1, Math.floor(structureOp.count || 1));
      if (rowsCollection && typeof rowsCollection.add === "function") {
        for (let i = 0; i < count; i++) {
          rowsCollection.add(Math.max(0, structureOp.index + i));
        }
        await context.sync();
        return true;
      }
      break;
    }
    case "deleteRow": {
      const count = Math.max(1, Math.floor(structureOp.count || 1));
      if (rowsCollection && typeof rowsCollection.deleteRows === "function") {
        const startIndex = Math.max(0, structureOp.index);
        const getRowAt =
          typeof rowsCollection.getItemAt === "function"
            ? (idx: number) => rowsCollection.getItemAt(idx)
            : null;
        if (!getRowAt) {
          break;
        }

        const rowsToDelete: any[] = [];
        for (let i = 0; i < count; i++) {
          rowsToDelete.push(getRowAt(startIndex + i));
        }
        if (rowsToDelete.length === 0) {
          break;
        }

        rowsCollection.deleteRows(rowsToDelete);
        await context.sync();
        return true;
      }
      break;
    }
    case "addColumn": {
      const count = Math.max(1, Math.floor(structureOp.count || 1));
      if (colsCollection && typeof colsCollection.add === "function") {
        for (let i = 0; i < count; i++) {
          colsCollection.add(Math.max(0, structureOp.index + i));
        }
        await context.sync();
        return true;
      }
      break;
    }
    case "deleteColumn": {
      const count = Math.max(1, Math.floor(structureOp.count || 1));
      if (colsCollection && typeof colsCollection.deleteColumns === "function") {
        const startIndex = Math.max(0, structureOp.index);
        const getColumnAt =
          typeof colsCollection.getItemAt === "function"
            ? (idx: number) => colsCollection.getItemAt(idx)
            : null;
        if (!getColumnAt) {
          break;
        }

        const columnsToDelete: any[] = [];
        for (let i = 0; i < count; i++) {
          columnsToDelete.push(getColumnAt(startIndex + i));
        }
        if (columnsToDelete.length === 0) {
          break;
        }

        colsCollection.deleteColumns(columnsToDelete);
        await context.sync();
        return true;
      }
      break;
    }
    case "mergeCells":
      if (typeof tableAny.mergeCells === "function") {
        tableAny.mergeCells(
          structureOp.rowIndex,
          structureOp.columnIndex,
          structureOp.rowCount,
          structureOp.columnCount
        );
        await context.sync();
        return true;
      }
      break;
    case "splitCell":
    case "resizeCellSpan": {
      const cell = tableAny.getCellOrNullObject?.(structureOp.rowIndex, structureOp.columnIndex);
      if (!cell) {
        break;
      }
      cell.load("isNullObject");
      await context.sync();
      if (cell.isNullObject) {
        break;
      }
      if (structureOp.op === "splitCell" && typeof cell.split === "function") {
        cell.split(structureOp.rowCount, structureOp.columnCount);
        await context.sync();
        return true;
      }
      if (structureOp.op === "resizeCellSpan" && typeof cell.resize === "function") {
        cell.resize(structureOp.rowCount, structureOp.columnCount);
        await context.sync();
        return true;
      }
      break;
    }
    default:
      break;
  }

  return false;
}

async function applyTableStructureOps(
  table: any,
  structureOps: TableStructureOp[],
  context: PowerPoint.RequestContext
): Promise<boolean> {
  if (!structureOps || structureOps.length === 0) {
    return false;
  }

  let applied = false;
  for (const structureOp of structureOps) {
    const success = await applySingleStructureOp(table, structureOp, context);
    if (!success) {
      throw new Error(`Unsupported structure operation: ${structureOp.op}`);
    }
    applied = true;
  }
  return applied;
}

async function applyTableClearOps(
  table: any,
  clearOps: TableClearOp[] | undefined,
  context: PowerPoint.RequestContext
): Promise<boolean> {
  if (!clearOps || clearOps.length === 0) {
    return false;
  }

  const tableAny = table as any;
  if (typeof tableAny.clear !== "function") {
    throw new Error("Table.clear is not available for clearOps.");
  }

  for (const clearOp of clearOps) {
    const clearOptions: any = {};
    if (clearOp.mode === "all") {
      clearOptions.all = true;
    } else if (clearOp.mode === "text_only") {
      clearOptions.text = true;
    } else if (clearOp.mode === "format_only") {
      clearOptions.format = true;
    }
    tableAny.clear(clearOptions);
  }
  await context.sync();
  return true;
}

function regionsOverlap(a: TableLayoutSpec["targetRegion"], b: TableLayoutSpec["targetRegion"]): boolean {
  if (!a || !b) {
    return false;
  }
  return !(
    a.left + a.width <= b.left ||
    b.left + b.width <= a.left ||
    a.top + a.height <= b.top ||
    b.top + b.height <= a.top
  );
}

async function applyTableRelayout(
  table: any,
  fallbackShape: PowerPoint.Shape,
  layoutSpec: TableLayoutSpec | undefined,
  boundedMod: { left?: number; top?: number; width?: number; height?: number },
  context: PowerPoint.RequestContext
): Promise<boolean> {
  const hasExplicitBounds =
    boundedMod.left !== undefined ||
    boundedMod.top !== undefined ||
    boundedMod.width !== undefined ||
    boundedMod.height !== undefined;

  if (!layoutSpec && !hasExplicitBounds) {
    return false;
  }

  const tableShape =
    (typeof (table as any).getShape === "function" ? ((table as any).getShape() as PowerPoint.Shape) : null) ||
    fallbackShape;

  const currentBounds = {
    left: normalizePointValue((tableShape as any).left) ?? 0,
    top: normalizePointValue((tableShape as any).top) ?? 0,
    width: normalizePointValue((tableShape as any).width) ?? 0,
    height: normalizePointValue((tableShape as any).height) ?? 0,
  };
  const targetRegion = layoutSpec?.targetRegion;

  const nextBounds = {
    left: targetRegion?.left ?? boundedMod.left ?? currentBounds.left,
    top: targetRegion?.top ?? boundedMod.top ?? currentBounds.top,
    width: targetRegion?.width ?? boundedMod.width ?? currentBounds.width,
    height: targetRegion?.height ?? boundedMod.height ?? currentBounds.height,
  };

  if (layoutSpec?.avoidRegions && layoutSpec.avoidRegions.length > 0) {
    for (const avoidRegion of layoutSpec.avoidRegions) {
      if (!regionsOverlap(nextBounds, avoidRegion)) {
        continue;
      }
      const shiftAbove = avoidRegion.top - nextBounds.height - MARGIN;
      if (shiftAbove >= MARGIN) {
        nextBounds.top = shiftAbove;
      } else {
        nextBounds.top = avoidRegion.top + avoidRegion.height + MARGIN;
      }
    }
  }

  tableShape.left = nextBounds.left;
  tableShape.top = nextBounds.top;
  tableShape.width = nextBounds.width;
  tableShape.height = nextBounds.height;
  await context.sync();

  if (layoutSpec?.fitPolicy?.mode === "fit_with_font_shrink") {
    table.load("rowCount,columnCount");
    await context.sync();
    const rowCount = table.rowCount as number;
    const colCount = table.columnCount as number;
    const areaRatio =
      currentBounds.width > 0 && currentBounds.height > 0
        ? Math.min(1, (nextBounds.width * nextBounds.height) / (currentBounds.width * currentBounds.height))
        : 1;
    const fontScale = Math.sqrt(Math.max(0.2, areaRatio));
    const minFont = Math.max(6, layoutSpec.fitPolicy.minFontSizePt || 9);
    for (let row = 0; row < rowCount; row++) {
      for (let col = 0; col < colCount; col++) {
        const cell = table.getCellOrNullObject(row, col);
        cell.load("isNullObject");
        try {
          cell.font?.load?.("size");
        } catch {
          // Ignore optional load errors.
        }
        await context.sync();
        if (cell.isNullObject || !cell.font) {
          continue;
        }
        const currentSize = normalizePointValue(cell.font.size);
        if (currentSize === undefined) {
          continue;
        }
        const scaled = Math.max(minFont, Math.round(currentSize * fontScale * 10) / 10);
        cell.font.size = scaled;
      }
    }
    await context.sync();
  }

  return true;
}

interface TableOperationApplyResult {
  applied: boolean;
  replacementShape?: PowerPoint.Shape;
}

async function applyTableOperationToExistingShape(
  slide: PowerPoint.Slide,
  shape: PowerPoint.Shape,
  tableData: TableData,
  boundedMod: { left?: number; top?: number; width?: number; height?: number },
  context: PowerPoint.RequestContext
): Promise<TableOperationApplyResult> {
  const shapeAny = shape as any;
  if (typeof shapeAny.getTable !== "function") {
    throw new Error("Target shape does not support getTable().");
  }

  const tableAction = getTableAction(tableData, "modify");
  const table = shapeAny.getTable();
  let textUpdates = tableData.textUpdates || [];
  let structureOps = tableData.structureOps || [];
  const hasExplicitBounds =
    boundedMod.left !== undefined ||
    boundedMod.top !== undefined ||
    boundedMod.width !== undefined ||
    boundedMod.height !== undefined;

  if (
    tableAction === "edit" &&
    tableData.rows &&
    tableData.rows.length > 0 &&
    textUpdates.length === 0
  ) {
    const currentRows = await snapshotTableTextRows(table, context);
    if (structureOps.length === 0) {
      const derivedEditOps = buildTableEditOpsFromRows(currentRows, tableData.rows);
      structureOps = derivedEditOps.structureOps;
      textUpdates = derivedEditOps.textUpdates;
    } else {
      textUpdates = buildTableTextUpdatesFromRows(tableData.rows);
    }
  }

  if (tableAction === "replace" || tableAction === "create") {
    const nextRows = tableData.rows;
    if (!nextRows || nextRows.length === 0) {
      throw new Error("replace/create table operation requires rows.");
    }
    const fallbackBounds = {
      left: boundedMod.left ?? (shape as any).left,
      top: boundedMod.top ?? (shape as any).top,
      width: boundedMod.width ?? (shape as any).width,
      height: boundedMod.height ?? (shape as any).height,
    };

    const existingStyleSettings = await extractTableStyleSettings(table, context, defaultLogger);
    shape.delete();
    await context.sync();

    const createdShape = await createTableWithFollowUp(
      slide,
      {
        ...tableData,
        action: "create",
        styleSettings: tableData.styleSettings || existingStyleSettings,
      },
      fallbackBounds,
      context
    );
    return { applied: true, replacementShape: createdShape };
  }

  let applied = false;

  if (structureOps.length > 0) {
    const structureApplied = await applyTableStructureOps(table, structureOps, context);
    applied = applied || structureApplied;
  }

  if (textUpdates.length > 0) {
    await applyTableTextUpdatesToTable(table, textUpdates, context);
    applied = true;
  }

  if (tableData.styleUpdates && tableData.styleUpdates.length > 0) {
    const styleApplied = await applyTableStyleUpdates(table, tableData.styleUpdates, context);
    applied = applied || styleApplied;
  }

  if (tableData.styleSettingsUpdate) {
    const styleSettingsApplied = await applyTableStyleSettingsUpdate(
      table,
      tableData.styleSettingsUpdate,
      context
    );
    applied = applied || styleSettingsApplied;
  }

  if (tableData.clearOps && tableData.clearOps.length > 0) {
    const clearApplied = await applyTableClearOps(table, tableData.clearOps, context);
    applied = applied || clearApplied;
  }

  if (tableAction === "relayout" || tableData.layoutSpec || hasExplicitBounds) {
    const relayoutApplied = await applyTableRelayout(table, shape, tableData.layoutSpec, boundedMod, context);
    applied = applied || relayoutApplied;
  }

  return { applied };
}

/**
 * Insert a chart into a slide by rendering it as an image
 * PowerPoint JS API doesn't have native addChart, so we use Chart.js to render
 */
/**
 * Duplicate a slide and return info about the new slide
 * Copies all shape types: text boxes, geometric shapes, tables, and images
 */
export async function duplicateSlide(
  sourceIndex: number,
  options?: {
    log?: Logger;
    presentationRef?: FactSetPresentationRef | null;
    sourceSlideId?: string;
    targetSlideId?: string;
  }
): Promise<{ newIndex: number; newId?: string }> {
  if (shouldUseFactSetBridge()) {
    const presentationRef = options?.presentationRef;
    const log = options?.log;
    const preSlides = (await listSlidesViaIntents(log, { forceRefresh: true, presentationRef }))
      .slice()
      .sort((left, right) => left.index - right.index);
    const sourceSlide =
      (options?.sourceSlideId
        ? preSlides.find((slide) => slide.slideId === options.sourceSlideId)
        : undefined) ??
      preSlides.find((slide) => slide.index === sourceIndex) ??
      preSlides[sourceIndex];
    if (!sourceSlide) {
      throw new Error(
        options?.sourceSlideId
          ? `Source slide ${options.sourceSlideId} (index ${sourceIndex}) not found`
          : `Source slide index ${sourceIndex} out of range`
      );
    }

    let originalSelectedSlideId: string | null = null;
    try {
      originalSelectedSlideId = (await getSelectedSlideRefViaIntents(log, { presentationRef })).id;
    } catch (error) {
      log?.(
        `Could not snapshot selected slide before duplicate export; continuing without restore: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }

    try {
      await setSelectedSlidesViaIntents({
        slideIds: [sourceSlide.slideId],
        log,
        presentationRef,
      });
    } catch (error) {
      log?.(
        `Could not preselect source slide ${sourceSlide.slideId} before duplicate export; continuing with slideIds export: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }

    const sourceSlideBase64 = await exportPresentationAsBase64ViaIntents({
      slideIds: [sourceSlide.slideId],
      log,
      presentationRef,
    });
    const insertedSlides = await insertSlidesFromBase64ViaIntents({
      base64Slides: sourceSlideBase64,
      targetSlideId: options?.targetSlideId || sourceSlide.slideId,
      keepSourceFormatting: true,
      log,
      presentationRef,
    });

    if (originalSelectedSlideId) {
      try {
        await setSelectedSlidesViaIntents({
          slideIds: [originalSelectedSlideId],
          log,
          presentationRef,
        });
      } catch (error) {
        log?.(
          `Could not restore selected slide ${originalSelectedSlideId} after duplicate export; duplicate succeeded: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }
    }

    const inserted = insertedSlides[0];
    if (!inserted) {
      throw new Error("FactSet duplicate did not expose a newly inserted slide reference.");
    }
    const insertedIndex =
      inserted.insertedSlideIndex ??
      (await resolveSlideIndexByIdViaIntents(inserted.insertedSlideId, log, { presentationRef }));
    if (insertedIndex === null) {
      throw new Error(`Could not resolve duplicated slide index for ${inserted.insertedSlideId}`);
    }

    return { newIndex: insertedIndex, newId: inserted.insertedSlideId };
  }

  return guardedPowerPointRun(async (context) => {
    const presentation = context.presentation;
    const slides = presentation.slides;
    slides.load("items");
    await context.sync();

    console.log(`[DUPLICATE] Starting duplication of slide ${sourceIndex}`);
    console.log(`[DUPLICATE] Total slides before: ${slides.items.length}`);

    if (sourceIndex >= slides.items.length) {
      throw new Error(`Source slide index ${sourceIndex} out of range`);
    }

    const sourceSlide = slides.items[sourceIndex];
    sourceSlide.load("id");
    await context.sync();

    const sourceSlideId = sourceSlide.id;
    console.log(`[DUPLICATE] Source slide ID: ${sourceSlideId}`);

    const newSlideIndex = sourceIndex + 1;

    // Try to add slide with same layout as source
    try {
      sourceSlide.layout.load("id");
      await context.sync();

      const layoutId = sourceSlide.layout?.id;
      console.log(`[DUPLICATE] Source layout ID: ${layoutId}`);

      const slidesAny = slides as unknown as {
        add(options?: { index?: number; layoutId?: string }): PowerPoint.Slide;
      };
      slidesAny.add({ index: newSlideIndex, layoutId: layoutId });
      await context.sync();
    } catch (layoutError) {
      console.log("[DUPLICATE] Layout copy failed, using basic add:", layoutError);
      const slidesAny = slides as unknown as {
        add(options?: { index?: number }): PowerPoint.Slide;
      };
      slidesAny.add({ index: newSlideIndex });
      await context.sync();
    }

    // Reload slides to get the new slide
    slides.load("items");
    await context.sync();

    const targetSlide = slides.items[newSlideIndex];

    // Load source slide shapes with all properties we need
    const sourceSlideReloaded = slides.items[sourceIndex];
    sourceSlideReloaded.shapes.load("items");
    await context.sync();

    console.log(
      `[DUPLICATE] New slide created at index ${newSlideIndex}, copying ${sourceSlideReloaded.shapes.items.length} shapes`
    );

    let copiedCount = 0;
    let skippedCount = 0;
    const skippedTypes: string[] = [];

    for (const shape of sourceSlideReloaded.shapes.items) {
      try {
        // Load shape properties including type
        shape.load(["id", "name", "left", "top", "width", "height", "type"]);
        await context.sync();

        const shapeTypeStr = String(shape.type || "").toLowerCase();
        console.log(
          `[DUPLICATE] Processing shape: type=${shapeTypeStr}, pos=(${shape.left}, ${shape.top})`
        );

        // Try to get text content (works for text boxes and shapes with text)
        let text = "";
        let fontSize: number | undefined;
        let fontBold: boolean | undefined;
        let fontColor: string | undefined;
        try {
          const textRange = shape.textFrame.textRange;
          textRange.load("text");
          textRange.font.load(["size", "bold", "color"]);
          await context.sync();
          text = textRange.text || "";
          fontSize = textRange.font.size;
          fontBold = textRange.font.bold;
          fontColor = textRange.font.color;
        } catch {
          // Shape doesn't have text frame
        }

        // Try to get fill color for geometric shapes
        let fillColor: string | undefined;
        try {
          shape.fill.load("foregroundColor");
          await context.sync();
          fillColor = shape.fill.foregroundColor;
        } catch {
          // Fill not available
        }

        // Handle different shape types
        if (shapeTypeStr.includes("geometric") || shapeTypeStr.includes("autoshape")) {
          // Geometric shape - recreate with same properties
          let geoType: PowerPoint.GeometricShapeType = PowerPoint.GeometricShapeType.rectangle;
          try {
            const shapeAny = shape as any;
            if (shapeAny.geometricShapeType) {
              shapeAny.load("geometricShapeType");
              await context.sync();
              geoType = shapeAny.geometricShapeType;
            }
          } catch {
            // Use default rectangle
          }

          const newShape = targetSlide.shapes.addGeometricShape(geoType);
          newShape.left = shape.left;
          newShape.top = shape.top;
          newShape.width = shape.width;
          newShape.height = shape.height;

          if (fillColor) {
            try {
              newShape.fill.setSolidColor(fillColor);
            } catch {
              /* ignore */
            }
          }

          if (text) {
            try {
              newShape.textFrame.textRange.text = text;
              if (fontSize) newShape.textFrame.textRange.font.size = fontSize;
              if (fontBold !== undefined) newShape.textFrame.textRange.font.bold = fontBold;
              if (fontColor) newShape.textFrame.textRange.font.color = fontColor;
            } catch {
              /* ignore */
            }
          }

          copiedCount++;
          console.log(`[DUPLICATE] Copied geometric shape with text: "${text.slice(0, 20)}..."`);
        } else if (shapeTypeStr.includes("table")) {
          // Table shape - try to copy using getTable API
          try {
            const shapeAny = shape as any;
            if (typeof shapeAny.getTable === "function") {
              const table = shapeAny.getTable();
              table.load("rowCount, columnCount");
              await context.sync();

              const rowCount = table.rowCount as number;
              const colCount = table.columnCount as number;
              const rows: string[][] = [];

              // Extract cell values
              for (let row = 0; row < rowCount; row++) {
                const rowData: string[] = [];
                for (let col = 0; col < colCount; col++) {
                  try {
                    const cell = table.getCellOrNullObject(row, col);
                    cell.load("text");
                    await context.sync();
                    rowData.push(cell.isNullObject ? "" : cell.text || "");
                  } catch {
                    rowData.push("");
                  }
                }
                rows.push(rowData);
              }

              // Create new table
              const shapesAny = targetSlide.shapes as any;
              if (typeof shapesAny.addTable === "function") {
                const newTable = shapesAny.addTable(rowCount, colCount, { values: rows });
                newTable.left = shape.left;
                newTable.top = shape.top;
                newTable.width = shape.width;
                newTable.height = shape.height;
                await context.sync();
                copiedCount++;
                console.log(`[DUPLICATE] Copied table: ${rowCount}x${colCount}`);
              } else {
                // Fallback: create text representation
                const tableText = rows.map((row) => row.join("\t")).join("\n");
                const textBox = targetSlide.shapes.addTextBox(tableText);
                textBox.left = shape.left;
                textBox.top = shape.top;
                textBox.width = shape.width;
                textBox.height = shape.height;
                copiedCount++;
                console.log(`[DUPLICATE] Copied table as text: ${rowCount}x${colCount}`);
              }
            }
          } catch (tableErr) {
            console.log(`[DUPLICATE] Could not copy table:`, tableErr);
            skippedCount++;
            skippedTypes.push("table");
          }
        } else if (shapeTypeStr.includes("picture") || shapeTypeStr.includes("image")) {
          // Image - cannot directly copy, note it was skipped
          // PowerPoint JS API doesn't expose image data for reading
          console.log(`[DUPLICATE] Skipping image (cannot read image data from PowerPoint API)`);
          skippedCount++;
          skippedTypes.push("image");
        } else if (shapeTypeStr.includes("line") || shapeTypeStr.includes("connector")) {
          // Line/connector - create a simple line
          try {
            const shapesAny = targetSlide.shapes as any;
            if (typeof shapesAny.addLine === "function") {
              const line = shapesAny.addLine(
                PowerPoint.ConnectorType.straight,
                { x: shape.left, y: shape.top },
                { x: shape.left + shape.width, y: shape.top + shape.height }
              );
              await context.sync();
              copiedCount++;
              console.log(`[DUPLICATE] Copied line`);
            } else {
              skippedCount++;
              skippedTypes.push("line");
            }
          } catch {
            skippedCount++;
            skippedTypes.push("line");
          }
        } else if (text) {
          // Default: if it has text, copy as text box
          const newShape = targetSlide.shapes.addTextBox(text);
          newShape.left = shape.left;
          newShape.top = shape.top;
          newShape.width = shape.width;
          newShape.height = shape.height;

          try {
            if (fontSize) newShape.textFrame.textRange.font.size = fontSize;
            if (fontBold !== undefined) newShape.textFrame.textRange.font.bold = fontBold;
            if (fontColor) newShape.textFrame.textRange.font.color = fontColor;
          } catch {
            /* ignore font errors */
          }

          copiedCount++;
          console.log(`[DUPLICATE] Copied text shape: "${text.slice(0, 30)}..."`);
        } else {
          // Shape without text that we couldn't identify
          console.log(`[DUPLICATE] Skipping unknown shape type: ${shapeTypeStr}`);
          skippedCount++;
          skippedTypes.push(shapeTypeStr || "unknown");
        }
      } catch (e) {
        console.log("[DUPLICATE] Could not copy shape:", e);
        skippedCount++;
      }
    }

    await context.sync();
    console.log(
      `[DUPLICATE] Completed: copied ${copiedCount} shapes, skipped ${skippedCount} (${Array.from(new Set(skippedTypes)).join(", ")})`
    );

    return { newIndex: newSlideIndex };
  }, { newIndex: -1 });
}

/**
 * Apply a single shape to a specific slide in its own PowerPoint.run context.
 * Used for streaming deck creation where shapes are applied as they arrive.
 *
 * H5 fix: accepts the same `{ presentationRef, preferredBounds }` targeting
 * that `applyModifications` uses. Without these, the streaming per-shape
 * path landed shapes on whatever PowerPoint had active at the moment the
 * shape arrived (NOT the deck the user kicked off), and sized them against
 * a shared bounds cache that mid-deck doc switches can invalidate. The
 * deck-creation caller in `generateSlideContent.ts` now forwards
 * `targetPresentationRef` + `slideBounds` here.
 */
export async function applySingleShape(
  slideIndex: number,
  shapeMod: ShapeModification,
  log?: (message: string) => void,
  options?: {
    presentationRef?: FactSetPresentationRef | null;
    preferredBounds?: SlideBounds;
  }
): Promise<void> {
  if (shouldUseFactSetBridge()) {
    await applyModifications(
      {
        slides: [
          {
            id: "",
            index: slideIndex,
            action: "modify",
            shapes: [shapeMod],
          },
        ],
      },
      log,
      undefined,
      {
        presentationRef: options?.presentationRef,
        preferredBounds: options?.preferredBounds,
      }
    );
    return;
  }

  if (isOfficeJsBypassed()) {
    console.log("[OfficeJS NOP] applySingleShape bypassed");
    return;
  }
  const logMsg = (msg: string) => {
    console.log(msg);
    if (log) log(msg);
  };

  // Resolve image payload BEFORE entering PowerPoint.run to avoid WebKit timeouts.
  let prefetchedBase64: string | null = null;
  if (shapeMod.action === "create" && isImageShapeModification(shapeMod)) {
    const inlinePayload = getInlineImagePayload(shapeMod);
    if (inlinePayload) {
      setInlineImagePayload(shapeMod, inlinePayload);
      prefetchedBase64 = inlinePayload.base64;
      logMsg(`  🖼️ Using inline image payload (${(prefetchedBase64.length / 1024).toFixed(1)}KB)`);
    } else if (shapeMod.imageUrl) {
      logMsg(`  🖼️ Pre-fetching image: ${shapeMod.imageUrl.slice(0, 80)}...`);
      const fetched = await imageUrlToBase64(shapeMod.imageUrl);
      if (!fetched) {
        logMsg(`  ⚠️ Image fetch failed for ${shapeMod.imageUrl.slice(0, 80)}`);
        return;
      }
      prefetchedBase64 = fetched.base64;
      setInlineImagePayload(shapeMod, {
        base64: fetched.base64,
        mimeType: fetched.mimeType || normalizeImageMimeType(shapeMod.imageMimeType),
      });
      logMsg(`  🖼️ Got base64 (${(prefetchedBase64.length / 1024).toFixed(1)}KB)`);
    } else {
      logMsg(`  ⚠️ Inline image payload missing for shape ${shapeMod.id}`);
      return;
    }

    await fitLogoShapeToInlineImageAspectRatio(
      shapeMod,
      prefetchedBase64,
      normalizeImageMimeType(shapeMod.imageMimeType)
    );
  }

  return guardedPowerPointRun(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items");
    await context.sync();

    const slideWidth = inferredSlideWidth || DEFAULT_SLIDE_WIDTH;
    const slideHeight = inferredSlideHeight || DEFAULT_SLIDE_HEIGHT;

    if (slideIndex >= slides.items.length) {
      logMsg(`[SHAPE] Slide index ${slideIndex} out of range`);
      return;
    }

    const slide = slides.items[slideIndex];
    slide.shapes.load("items");
    await context.sync();

    for (const shape of slide.shapes.items) {
      shape.load("id,left,top,width,height");
    }
    await context.sync();

    const boundedMod = applyBoundsToShape(shapeMod, slideWidth, slideHeight);
    if (boundedMod.left !== undefined) shapeMod.left = boundedMod.left;
    if (boundedMod.top !== undefined) shapeMod.top = boundedMod.top;
    if (boundedMod.width !== undefined) shapeMod.width = boundedMod.width;
    if (boundedMod.height !== undefined) shapeMod.height = boundedMod.height;

    const shapeType = isImageShapeModification(shapeMod)
      ? "image"
      : shapeMod.table
        ? "table"
        : shapeMod.chart
          ? "chart"
          : shapeMod.geometricType || "textBox";

    if (shapeMod.action === "create") {
      if (shapeMod.table) {
        const tableAction = getTableAction(shapeMod.table, "create");
        if (tableAction && tableAction !== "create" && tableAction !== "replace") {
          logMsg(`  ⚠️ Unsupported table action "${tableAction}" for create flow`);
          return;
        }
        await createTableWithFollowUp(slide, shapeMod.table, boundedMod, context, logMsg);
      } else if (shapeMod.chart) {
        // No-op: in Office.js mode chart shapes are expanded into inserted
        // chart slides BEFORE applyModifications iterates (via
        // expandInSlideChartsForOfficeJs in chartTwoPassTranslator.ts), so
        // we should never see a chart shape here. If we do (e.g. the
        // translator failed), log and skip rather than fall through to a
        // legacy image fallback.
        if (log) log(`⚠️ Chart shape ${shapeMod.id} reached slide iteration; translator failed`);
      } else if (isImageShapeModification(shapeMod) && prefetchedBase64) {
        const base64Data = prefetchedBase64;
        const left = boundedMod.left ?? 50;
        const top = boundedMod.top ?? 80;
        let width = boundedMod.width ?? 300;
        let height = boundedMod.height ?? 200;

        // Legacy logo.dev assets are square favicons; LogoIntern assets preserve their own aspect ratio.
        if (shouldForceSquareLogoImageUrl(shapeMod.imageUrl) && width !== height) {
          const side = Math.min(width, height);
          width = side;
          height = side;
        }
        let inserted = false;

        // Method 1: Rectangle with fill.setImage (most reliable)
        try {
          const rectShape = slide.shapes.addGeometricShape(PowerPoint.GeometricShapeType.rectangle);
          rectShape.left = left;
          rectShape.top = top;
          rectShape.width = width;
          rectShape.height = height;
          const lineAny = rectShape.lineFormat as any;
          if (lineAny && typeof lineAny.load === "function") {
            lineAny.visible = false;
          }
          await context.sync();
          const fill = rectShape.fill;
          if (fill && typeof fill.setImage === "function") {
            fill.setImage(base64Data);
            await context.sync();
            inserted = true;
            logMsg(`  🖼️ ✓ Image via rectangle.fill.setImage`);
          }
        } catch (rectErr) {
          logMsg(
            `  🖼️ rect+fill failed: ${rectErr instanceof Error ? rectErr.message : String(rectErr)}`
          );
        }

        // Method 2: addImage fallback
        if (!inserted) {
          const shapesAny = slide.shapes as any;
          if (typeof shapesAny.addImage === "function") {
            try {
              const imageShape = shapesAny.addImage(base64Data) as PowerPoint.Shape;
              imageShape.left = left;
              imageShape.top = top;
              imageShape.width = width;
              imageShape.height = height;
              await context.sync();
              inserted = true;
              logMsg(`  🖼️ ✓ Image via addImage`);
            } catch (e) {
              logMsg(`  🖼️ addImage failed: ${e instanceof Error ? e.message : String(e)}`);
            }
          }
        }

        if (!inserted) {
          logMsg(`  ⚠️ Could not insert image — no API available`);
        }
      } else if (shapeMod.geometricType) {
        const geoType = mapGeometricShapeType(shapeMod.geometricType);
        const geoShape = slide.shapes.addGeometricShape(geoType);
        geoShape.left = boundedMod.left ?? 50;
        geoShape.top = boundedMod.top ?? 50;
        geoShape.width = boundedMod.width ?? 200;
        geoShape.height = boundedMod.height ?? 100;
        if (shapeMod.fillColor) {
          geoShape.fill.setSolidColor(shapeMod.fillColor.replace(/^#/, ""));
        }
        if (shapeMod.text) {
          geoShape.textFrame.textRange.text = shapeMod.text;
          if (shapeMod.fontSize) geoShape.textFrame.textRange.font.size = shapeMod.fontSize;
          if (shapeMod.bold) geoShape.textFrame.textRange.font.bold = shapeMod.bold;
          if (shapeMod.textColor)
            geoShape.textFrame.textRange.font.color = shapeMod.textColor.replace(/^#/, "");
        }
        await context.sync();
      } else {
        const textBox = slide.shapes.addTextBox(shapeMod.text || "");
        textBox.left = boundedMod.left ?? 50;
        textBox.top = boundedMod.top ?? 50;
        textBox.width = boundedMod.width ?? 400;
        textBox.height = boundedMod.height ?? 50;
        if (shapeMod.fontSize) textBox.textFrame.textRange.font.size = shapeMod.fontSize;
        if (shapeMod.bold) textBox.textFrame.textRange.font.bold = shapeMod.bold;
        if (shapeMod.italic) textBox.textFrame.textRange.font.italic = shapeMod.italic;
        if (shapeMod.textColor)
          textBox.textFrame.textRange.font.color = shapeMod.textColor.replace(/^#/, "");
        if (shapeMod.fontFamily) textBox.textFrame.textRange.font.name = shapeMod.fontFamily;
        await context.sync();
      }
      logMsg(`  ✓ Created ${shapeType}: "${(shapeMod.text || shapeMod.id || "").slice(0, 40)}"`);
    }
  });
}

/**
 * Apply modifications to the presentation based on OpenAI response
 */
export async function applyModifications(
  modifications: PresentationModification,
  log?: (message: string) => void,
  themeAccent?: string,
  options?: ApplyModificationsOptions
): Promise<ApplyResultSummary> {
  const useIntentBridge = shouldUseFactSetBridge();
  if (!useIntentBridge && isOfficeJsBypassed()) {
    console.log("[OfficeJS NOP] applyModifications bypassed");
    const nopSummary = createApplyResultSummary(modifications);
    nopSummary.classification = "true_no_op";
    return nopSummary;
  }

  // Helper to log to both console and optional callback
  const logMsg = (msg: string) => {
    console.log(msg);
    if (log) log(msg);
  };

  let materializedDuplicateCount = 0;
  if (
    useIntentBridge &&
    (hasInsertSlideActions(modifications) || hasDuplicateSlideActions(modifications))
  ) {
    const structuralMaterialization = await materializeStructuralSlideActionsViaIntents(
      modifications,
      logMsg,
      options?.presentationRef
    );
    modifications = structuralMaterialization.modifications;
    materializedDuplicateCount = structuralMaterialization.duplicateCount;
  } else if (hasInsertSlideActions(modifications)) {
    modifications = withInsertSlidesUnsupportedDiagnostics(modifications);
  }

  await hydrateImagePayloads(modifications, logMsg);

  // Strip Finster citation markup (`<u id="…" data-…="…">VALUE</u>`) from
  // every text-bearing field on every shape BEFORE downstream apply paths
  // see the modifications. This guarantees PowerPoint never receives the
  // markup (which it would render literally) and gives us a per-shape
  // map of citation uuids to persist as a `finster:citationRefs` slide
  // tag after the apply succeeds. See `citations/stripAndExtract.ts` and
  // `citations/persistCitationRefs.ts`.
  const stripResult = stripModificationsInPlace(modifications);
  if (stripResult.allCitationIds.length > 0) {
    logMsg(
      `[CITATIONS] Stripped ${stripResult.allCitationIds.length} citation uuid(s) across ${stripResult.bySlide.length} slide(s).`
    );
  }
  const citationRefsToPersist: SlideCitationRefs[] = stripResult.bySlide;

  // Authoritative per-slide citation manifest (real ids). Built once here, after
  // materialization + strip, and attached to whichever summary we return so the
  // orchestrator binds SlideCitationsView messages by real id (no createdSlides
  // diff / positional pairing). Root-level citationIds are a model mistake - they
  // belong on the slide op that created the cited content - so warn and ignore.
  if (Array.isArray((modifications as { citationIds?: unknown }).citationIds)) {
    logMsg(
      "[CITATIONS][WARN] modifications.citationIds present at the root; citationIds must sit on the slide op that creates the cited content. Ignoring the root-level array."
    );
  }
  const citationManifest = buildCitationManifest(modifications, stripResult.bySlide);

  // Office.js mode: expand any in-slide chart shapes into newly inserted
  // chart slides BEFORE iteration. FactSet bridge path forwards charts
  // untouched through prepareBatchOperations, so we skip this for the bridge.
  if (!useIntentBridge) {
    modifications = await expandInSlideChartsForOfficeJs(modifications, logMsg);
  }

  const snippetPolicy = resolveSnippetPolicy({ brandId: options?.brandId });
  const hasBrand = options?.brandId != null;
  const expandSnippets =
    snippetPolicy.storageOverride === false
      ? false
      : hasBrand
        ? snippetPolicy.enabled
        : true;
  const snippetProfile = hasBrand ? snippetPolicy.profile : DEFAULT_SNIPPET_STYLE_PROFILE;
  const allowedSnippetTypes = hasBrand ? new Set(snippetPolicy.typeEnum) : null;

  const expandSnippetShapesForSlide = (
    slideMod: PresentationModification["slides"][number],
    slideWidth: number,
    slideHeight: number
  ): ShapeModification[] => {
    if (!expandSnippets) {
      return slideMod.shapes || [];
    }
    const rawSnippets = slideMod.snippets?.length
      ? slideMod.snippets
      : slideMod.compositeLayout?.items?.length
        ? [slideMod.compositeLayout]
        : [];
    const allSnippets = allowedSnippetTypes
      ? rawSnippets.filter((snippet) => allowedSnippetTypes.has(snippet.type))
      : rawSnippets;

    if (allSnippets.length === 0) {
      return slideMod.shapes || [];
    }

    const snippetShapes = allSnippets.flatMap((snippet) =>
      generateCompositeLayoutShapes(snippet, slideWidth, slideHeight, themeAccent, {
        useTextBoxesForStatGrid: useIntentBridge,
        styleProfile: snippetProfile,
      })
    );

    if (snippetShapes.length === 0) {
      return slideMod.shapes || [];
    }

    return [...(slideMod.shapes || []), ...snippetShapes];
  };

  const expandSnippetShapesForModifications = (
    input: PresentationModification,
    slideWidth: number,
    slideHeight: number
  ): PresentationModification => {
    let didExpand = false;
    const expandedSlides = (input.slides || []).map((slideMod) => {
      const expandedShapes = expandSnippetShapesForSlide(slideMod, slideWidth, slideHeight);
      if (expandedShapes.length === (slideMod.shapes || []).length) {
        return slideMod;
      }
      didExpand = true;
      return {
        ...slideMod,
        shapes: expandedShapes,
      };
    });

    return didExpand
      ? {
          ...input,
          slides: expandedSlides,
        }
      : input;
  };

  if (useIntentBridge) {
    const resolvedBounds = await getResolvedSlideBoundsViaIntents(logMsg, {
      preferredBounds: options?.preferredBounds,
      presentationRef: options?.presentationRef,
    });
    const expandedModifications = expandSnippetShapesForModifications(
      modifications,
      resolvedBounds.slideWidth,
      resolvedBounds.slideHeight
    );
    const bridgeSummary = await applyModificationsViaIntents(expandedModifications, log, {
      preferredBounds: {
        slideWidth: resolvedBounds.slideWidth,
        slideHeight: resolvedBounds.slideHeight,
      },
      onPreparedBatch: options?.onPreparedIntentBatch,
      presentationRef: options?.presentationRef,
    });
    if (materializedDuplicateCount > 0) {
      bridgeSummary.requested.byAction.duplicate += materializedDuplicateCount;
      bridgeSummary.applied.byAction.duplicate += materializedDuplicateCount;
      bridgeSummary.applied.slides += materializedDuplicateCount;
      bridgeSummary.classification = classifyApplyResult(bridgeSummary);
    }
    if (citationRefsToPersist.length > 0) {
      try {
        await persistCitationRefs(citationRefsToPersist, logMsg);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        logMsg(`[CITATIONS] persistCitationRefs failed (bridge path): ${msg}`);
      }
    }
    bridgeSummary.citationManifest = citationManifest;
    return bridgeSummary;
  }

  const summary = createApplyResultSummary(modifications);
  summary.citationManifest = citationManifest;
  const appliedSlideKeys = new Set<string>();
  const markSlideApplied = (slideKey: string) => {
    if (!appliedSlideKeys.has(slideKey)) {
      appliedSlideKeys.add(slideKey);
      summary.applied.slides += 1;
    }
  };

  for (const diagnostic of modifications.diagnostics || []) {
    recordSkipped(summary, diagnostic);
  }

  // First pass: handle slide-level actions (duplicate, delete)
  const slideIndexMap: Map<number, number> = new Map(); // old index -> new index

  // Handle missing or empty slides array
  if (!modifications.slides || modifications.slides.length === 0) {
    if ((modifications.diagnostics || []).length === 0) {
      recordSkipped(summary, {
        code: "NO_MODIFICATIONS_RETURNED",
        category: "no_op",
        message: "Model returned no slide operations to apply.",
      });
    }
    summary.classification = classifyApplyResult(summary);
    logMsg(`[APPLY] No slide modifications to apply`);
    logApplySummary(summary, logMsg);
    return summary;
  }

  logMsg(`[APPLY] Processing ${modifications.slides.length} slide modifications`);

  // Pre-fetch URL-backed image payloads only when URL fallback is enabled.
  const prefetchedImages: Map<string, string> = new Map();
  if (!OFFICEJS_DISABLE_IMAGE_URL_FALLBACK) {
    for (const slideMod of modifications.slides) {
      for (const shapeMod of slideMod.shapes || []) {
        const inlinePayload = getInlineImagePayload(shapeMod);
        if (
          shapeMod.action === "create" &&
          isImageShapeModification(shapeMod) &&
          !inlinePayload &&
          shapeMod.imageUrl &&
          !prefetchedImages.has(shapeMod.imageUrl)
        ) {
          logMsg(`🖼️ Pre-fetching image: ${shapeMod.imageUrl.slice(0, 80)}...`);
          const fetched = await imageUrlToBase64(shapeMod.imageUrl);
          if (fetched) {
            prefetchedImages.set(shapeMod.imageUrl, fetched.base64);
            logMsg(`🖼️ Got base64 (${(fetched.base64.length / 1024).toFixed(1)}KB)`);
          } else {
            logMsg(`⚠️ Image pre-fetch failed: ${shapeMod.imageUrl.slice(0, 80)}`);
          }
        }
      }
    }
  }
  if (prefetchedImages.size > 0) {
    logMsg(`[APPLY] Pre-fetched ${prefetchedImages.size} image(s)`);
  }

  for (const slideMod of modifications.slides) {
    console.log(`[APPLY] Slide ${slideMod.index}: action = ${slideMod.action || "modify"}`);
    if (slideMod.action === "duplicate") {
      try {
        console.log(`[APPLY] Duplicating slide ${slideMod.index}...`);
        const result = await duplicateSlide(slideMod.index, {
          log: logMsg,
          presentationRef: options?.presentationRef,
        });
        slideIndexMap.set(slideMod.index, result.newIndex);
        summary.applied.byAction.duplicate += 1;
        markSlideApplied(`duplicate:${result.newIndex}`);
        console.log(`[APPLY] Duplicate created at index ${result.newIndex}`);
      } catch (duplicateErr) {
        const errMsg = duplicateErr instanceof Error ? duplicateErr.message : String(duplicateErr);
        recordError(summary, {
          code: "SLIDE_DUPLICATE_FAILED",
          category: "apply_error",
          message: `Failed to duplicate slide ${slideMod.index}: ${errMsg}`,
          slideIndex: slideMod.index,
          slideId: slideMod.id,
          action: "duplicate",
        });
        logMsg(`[APPLY] Duplicate failed for slide ${slideMod.index}: ${errMsg}`);
      }
    }
  }

  // Second pass: apply shape modifications
  await guardedPowerPointRun(async (context) => {
    const presentation = context.presentation;
    const slides = presentation.slides;
    slides.load("items");
    await context.sync();

    const slideWidth = inferredSlideWidth || DEFAULT_SLIDE_WIDTH;
    const slideHeight = inferredSlideHeight || DEFAULT_SLIDE_HEIGHT;

    for (const slideMod of modifications.slides) {
      // Determine which slide to modify
      let targetIndex = slideMod.index;
      if (slideMod.action === "duplicate") {
        // Apply shape modifications to the duplicated slide
        targetIndex = slideIndexMap.get(slideMod.index) ?? slideMod.index;
      }

      if (targetIndex >= slides.items.length) {
        const skippedCount = Math.max(slideMod.shapes?.length || 0, 1);
        recordSkipped(
          summary,
          {
            code: "SLIDE_INDEX_OUT_OF_RANGE",
            category: "target_mismatch",
            message: `Slide index ${targetIndex} is out of range for current presentation.`,
            slideIndex: targetIndex,
            slideId: slideMod.id,
            action: slideMod.action === "duplicate" ? "duplicate" : "modify",
          },
          skippedCount
        );
        console.log(`Slide index ${targetIndex} out of range`);
        continue;
      }

      const slide = slides.items[targetIndex];
      slide.shapes.load("items");
      await context.sync();

      // Load shape IDs for matching
      for (const shape of slide.shapes.items) {
        shape.load("id,left,top,width,height");
      }
      await context.sync();

      const effectiveShapes = expandSnippetShapesForSlide(slideMod, slideWidth, slideHeight);

      for (const shapeMod of effectiveShapes) {
        let shapeApplied = false;
        try {
          const shapeType = isImageShapeModification(shapeMod)
            ? "image"
            : shapeMod.table
              ? "table"
              : shapeMod.chart
                ? "chart"
                : shapeMod.geometricType || "textBox";
          const imageSourceLog = shapeMod.imageUrl
            ? ` url=${shapeMod.imageUrl.slice(0, 60)}...`
            : shapeMod.imageDataUri
              ? " source=imageDataUri"
              : shapeMod.imageBase64
                ? " source=imageBase64"
                : "";
          logMsg(
            `  [SHAPE] ${shapeMod.action} id=${shapeMod.id} type=${shapeType}${imageSourceLog}`
          );

          // Normalize shape geometry values before apply.
          const boundedMod = applyBoundsToShape(shapeMod, slideWidth, slideHeight);

          // Copy bounded values back to shapeMod so change log shows actual applied values
          if (boundedMod.left !== undefined) shapeMod.left = boundedMod.left;
          if (boundedMod.top !== undefined) shapeMod.top = boundedMod.top;
          if (boundedMod.width !== undefined) shapeMod.width = boundedMod.width;
          if (boundedMod.height !== undefined) shapeMod.height = boundedMod.height;

          if (shapeMod.action === "create") {
            if (shapeMod.table) {
              recordUnsupportedCreateParagraphOps(
                summary,
                targetIndex,
                slideMod.id,
                shapeMod.id,
                "table",
                shapeMod.paragraphOps
              );
              const tableAction = getTableAction(shapeMod.table, "create");
              if (tableAction && tableAction !== "create" && tableAction !== "replace") {
                recordSkipped(summary, {
                  code: "MODEL_OUTPUT_INVALID_TABLE_OP",
                  category: "model_output_mismatch",
                  message: `Table action "${tableAction}" is invalid for create shape ${shapeMod.id}.`,
                  slideIndex: targetIndex,
                  slideId: slideMod.id,
                  shapeId: shapeMod.id,
                  action: "create",
                });
                continue;
              }
              // Insert table with bounded position
              try {
                await createTableWithFollowUp(slide, shapeMod.table, boundedMod, context, logMsg);
                logMsg(`✓ Added table with ${shapeMod.table.rows?.length || 0} rows`);
                shapeApplied = true;
              } catch (tableError) {
                const errMsg =
                  tableError instanceof Error ? tableError.message : String(tableError);
                logMsg(`❌ Table failed: ${errMsg}`);
                recordError(summary, {
                  code: "TABLE_INSERT_FAILED",
                  category: "apply_error",
                  message: `Failed to create table for shape ${shapeMod.id}: ${errMsg}`,
                  slideIndex: targetIndex,
                  slideId: slideMod.id,
                  shapeId: shapeMod.id,
                  action: "create",
                });
              }
            } else if (shapeMod.chart) {
              // No-op: chart shapes are expanded into inserted chart slides
              // BEFORE the iteration loop via expandInSlideChartsForOfficeJs.
              // If we reach this branch the translator failed; record an
              // error so it surfaces in the apply summary.
              logMsg(`⚠️ Chart shape ${shapeMod.id} not expanded by translator; skipping`);
              recordError(summary, {
                code: "CHART_INSERT_FAILED",
                category: "apply_error",
                message: `Chart shape ${shapeMod.id} reached slide iteration without expansion`,
                slideIndex: targetIndex,
                slideId: slideMod.id,
                shapeId: shapeMod.id,
                action: "create",
              });
            } else if (isImageShapeModification(shapeMod)) {
              recordUnsupportedCreateParagraphOps(
                summary,
                targetIndex,
                slideMod.id,
                shapeMod.id,
                "image",
                shapeMod.paragraphOps
              );
              try {
                const inlinePayload = getInlineImagePayload(shapeMod);
                const base64Data =
                  inlinePayload?.base64 ??
                  (!OFFICEJS_DISABLE_IMAGE_URL_FALLBACK && shapeMod.imageUrl
                    ? prefetchedImages.get(shapeMod.imageUrl) ?? null
                    : null);

                if (base64Data) {
                  if (inlinePayload) {
                    setInlineImagePayload(shapeMod, inlinePayload);
                  }
                  const left = boundedMod.left ?? 50;
                  const top = boundedMod.top ?? 80;
                  let width = boundedMod.width ?? 300;
                  let height = boundedMod.height ?? 200;

                  // Legacy logo.dev assets are square favicons; LogoIntern assets preserve their own aspect ratio.
                  const forcedSquareLogo = shouldForceSquareLogoImageUrl(shapeMod.imageUrl);
                  if (forcedSquareLogo && width !== height) {
                    const side = Math.min(width, height);
                    width = side;
                    height = side;
                  }

                  logMsg(
                    `🖼️ Inserting image (${(base64Data.length / 1024).toFixed(1)}KB) at (${left},${top}) ${width}×${height}${forcedSquareLogo ? " (square logo)" : ""}...`
                  );

                  const shapesAny = slide.shapes as any;
                  let inserted = false;

                  // Method 1: Rectangle with fill.setImage (most reliable)
                  try {
                    const rectShape = slide.shapes.addGeometricShape(
                      PowerPoint.GeometricShapeType.rectangle
                    );
                    rectShape.left = left;
                    rectShape.top = top;
                    rectShape.width = width;
                    rectShape.height = height;
                    // Remove outline so it looks like a plain image
                    const lineAny = rectShape.lineFormat as any;
                    if (lineAny && typeof lineAny.load === "function") {
                      lineAny.visible = false;
                    }
                    await context.sync();

                    const fill = rectShape.fill;
                    if (fill && typeof fill.setImage === "function") {
                      fill.setImage(base64Data);
                      await context.sync();
                      inserted = true;
                      logMsg(`🖼️ ✓ Inserted via rectangle.fill.setImage`);
                    } else {
                      logMsg(`🖼️ fill.setImage not available, trying addImage...`);
                    }
                  } catch (rectErr) {
                    logMsg(
                      `🖼️ Rectangle+fill failed: ${rectErr instanceof Error ? rectErr.message : String(rectErr)}`
                    );
                  }

                  // Method 2: addImage API (PowerPointApi 1.4+)
                  if (!inserted && typeof shapesAny.addImage === "function") {
                    try {
                      const imageShape = shapesAny.addImage(base64Data) as PowerPoint.Shape;
                      imageShape.left = left;
                      imageShape.top = top;
                      imageShape.width = width;
                      imageShape.height = height;
                      await context.sync();
                      inserted = true;
                      logMsg(`🖼️ ✓ Inserted via addImage`);
                    } catch (e) {
                      logMsg(`🖼️ addImage threw: ${e instanceof Error ? e.message : String(e)}`);
                    }
                  }

                  shapeMod.left = left;
                  shapeMod.top = top;
                  shapeMod.width = width;
                  shapeMod.height = height;

                  if (!inserted) {
                    logMsg(`⚠️ Could not insert image — no supported API available`);
                    recordSkipped(summary, {
                      code: "IMAGE_INSERT_UNSUPPORTED",
                      category: "apply_error",
                      message: `Image APIs were unavailable while inserting shape ${shapeMod.id}.`,
                      slideIndex: targetIndex,
                      slideId: slideMod.id,
                      shapeId: shapeMod.id,
                      action: "create",
                    });
                  } else {
                    shapeApplied = true;
                  }
                } else {
                  const imageSource =
                    shapeMod.imageUrl?.slice(0, 80) ||
                    (shapeMod.imageDataUri ? "imageDataUri" : "imageBase64");
                  logMsg(
                    `⚠️ Image not available (payload resolve failed): ${imageSource}`
                  );
                  recordSkipped(summary, {
                    code: "IMAGE_PREFETCH_FAILED",
                    category: "apply_error",
                    message: `Image payload could not be resolved before apply for shape ${shapeMod.id}.`,
                    slideIndex: targetIndex,
                    slideId: slideMod.id,
                    shapeId: shapeMod.id,
                    action: "create",
                  });
                }
              } catch (imgError) {
                const errMsg = imgError instanceof Error ? imgError.message : String(imgError);
                logMsg(`❌ Image insert failed: ${errMsg}`);
                recordError(summary, {
                  code: "SHAPE_CREATE_FAILED",
                  category: "apply_error",
                  message: `Failed to create image shape ${shapeMod.id}: ${errMsg}`,
                  slideIndex: targetIndex,
                  slideId: slideMod.id,
                  shapeId: shapeMod.id,
                  action: "create",
                });
              }
            } else if (shapeMod.geometricType) {
              const isLineType = ["line", "straightconnector"].includes(
                shapeMod.geometricType.toLowerCase()
              );
              if (isLineType) {
                try {
                  const shapesAny = slide.shapes as any;
                  if (typeof shapesAny.addLine === "function") {
                    const left = boundedMod.left ?? 50;
                    const top = boundedMod.top ?? 80;
                    const width = boundedMod.width ?? 200;
                    const height = boundedMod.height ?? 0;
                    const lineShape = shapesAny.addLine(
                      PowerPoint.ConnectorType.straight,
                      { x: left, y: top },
                      { x: left + width, y: top + height }
                    );
                    if (shapeMod.lineColor) lineShape.lineFormat.color = shapeMod.lineColor;
                    if (shapeMod.lineWidth !== undefined) lineShape.lineFormat.weight = shapeMod.lineWidth;
                    await context.sync();
                    logMsg(`✓ Added line from (${left},${top}) to (${left + width},${top + height})`);
                    shapeApplied = true;
                  } else {
                    logMsg(`⚠️ addLine API not available — creating thin rectangle as fallback`);
                    const geoShape = slide.shapes.addGeometricShape(PowerPoint.GeometricShapeType.rectangle);
                    geoShape.left = boundedMod.left ?? 50;
                    geoShape.top = boundedMod.top ?? 80;
                    geoShape.width = boundedMod.width ?? 200;
                    geoShape.height = boundedMod.height ?? 1;
                    if (shapeMod.fillColor) geoShape.fill.setSolidColor(shapeMod.fillColor);
                    else if (shapeMod.lineColor) geoShape.fill.setSolidColor(shapeMod.lineColor);
                    else geoShape.fill.setSolidColor("#000000");
                    await context.sync();
                    shapeApplied = true;
                  }
                } catch (lineError) {
                  const errMsg = lineError instanceof Error ? lineError.message : String(lineError);
                  logMsg(`❌ Line creation failed: ${errMsg}`);
                  recordError(summary, {
                    code: "SHAPE_CREATE_FAILED",
                    category: "apply_error",
                    message: `Failed to create line shape ${shapeMod.id}: ${errMsg}`,
                    slideIndex: targetIndex,
                    slideId: slideMod.id,
                    shapeId: shapeMod.id,
                    action: "create",
                  });
                }
              } else {
                // Create geometric shape (rectangle, ellipse, etc.)
                try {
                  const geoType = mapGeometricShapeType(shapeMod.geometricType);
                  const geoShape = slide.shapes.addGeometricShape(geoType);

                  const left = boundedMod.left ?? 50;
                  const top = boundedMod.top ?? 80;
                  const maxAvailWidth = inferredSlideWidth - left - MARGIN;
                  const maxAvailHeight = inferredSlideHeight - top - MARGIN;
                  const width = boundedMod.width ?? Math.min(200, maxAvailWidth);
                  const height = boundedMod.height ?? Math.min(100, maxAvailHeight);

                  geoShape.left = left;
                  geoShape.top = top;
                  geoShape.width = width;
                  geoShape.height = height;

                  shapeMod.left = left;
                  shapeMod.top = top;
                  shapeMod.width = width;
                  shapeMod.height = height;

                  if (shapeMod.fillColor) {
                    geoShape.fill.setSolidColor(shapeMod.fillColor);
                  } else {
                    try { geoShape.fill.clear(); } catch { /* fill.clear not available */ }
                  }
                  if (shapeMod.lineColor) {
                    geoShape.lineFormat.color = shapeMod.lineColor;
                  }
                  if (shapeMod.lineWidth !== undefined) {
                    geoShape.lineFormat.weight = shapeMod.lineWidth;
                  }

                  if (shapeMod.text !== undefined) {
                    geoShape.textFrame.textRange.text = shapeMod.text;
                  }

                  await applyParagraphOpsWithHandling(
                    geoShape,
                    shapeMod.paragraphOps,
                    context,
                    summary,
                    targetIndex,
                    slideMod.id,
                    shapeMod.id,
                    "create"
                  );

                  if (shapeMod.text !== undefined) {
                    const font = geoShape.textFrame.textRange.font;
                    if (shapeMod.textColor) font.color = shapeMod.textColor;
                    if (shapeMod.fontSize) font.size = shapeMod.fontSize;
                    if (shapeMod.fontFamily) font.name = shapeMod.fontFamily;
                    if (shapeMod.bold !== undefined) font.bold = shapeMod.bold;
                    if (shapeMod.italic !== undefined) font.italic = shapeMod.italic;
                    const vaEnum: Record<string, PowerPoint.TextVerticalAlignment> = {
                      top: PowerPoint.TextVerticalAlignment.top,
                      middle: PowerPoint.TextVerticalAlignment.middle,
                      bottom: PowerPoint.TextVerticalAlignment.bottom,
                    };
                    geoShape.textFrame.verticalAlignment = vaEnum[shapeMod.verticalAlignment || "middle"] || PowerPoint.TextVerticalAlignment.middle;
                    geoShape.textFrame.wordWrap = true;
                  }

                  await context.sync();
                  console.log(
                    `Added geometric shape: ${shapeMod.geometricType}${shapeMod.text ? ` with text` : ""}`
                  );
                  shapeApplied = true;
                } catch (geoError) {
                  console.error("Failed to add geometric shape:", geoError);
                  const errMsg = geoError instanceof Error ? geoError.message : String(geoError);
                  recordError(summary, {
                    code: "SHAPE_CREATE_FAILED",
                    category: "apply_error",
                    message: `Failed to create geometric shape ${shapeMod.id}: ${errMsg}`,
                    slideIndex: targetIndex,
                    slideId: slideMod.id,
                    shapeId: shapeMod.id,
                    action: "create",
                  });
                }
              }
            } else {
              // Create new text box
              const text = shapeMod.text || "";
              const newShape = slide.shapes.addTextBox(text);

              // Set position and size for new shapes with deterministic defaults.
              const left = boundedMod.left ?? 50;
              const top = boundedMod.top ?? 80;

              const width = boundedMod.width ?? 400;
              const height = boundedMod.height ?? 100;

              newShape.left = left;
              newShape.top = top;
              newShape.width = width;
              newShape.height = height;

              // CRITICAL: Enable word wrap to prevent horizontal overflow
              try {
                newShape.textFrame.wordWrap = true;
                if (shapeMod.verticalAlignment) {
                  const vaEnum: Record<string, PowerPoint.TextVerticalAlignment> = {
                    top: PowerPoint.TextVerticalAlignment.top,
                    middle: PowerPoint.TextVerticalAlignment.middle,
                    bottom: PowerPoint.TextVerticalAlignment.bottom,
                  };
                  newShape.textFrame.verticalAlignment = vaEnum[shapeMod.verticalAlignment] || PowerPoint.TextVerticalAlignment.top;
                }
              } catch (wrapErr) {
                console.log(`[TEXT BOX] Could not set wordWrap/alignment: ${wrapErr}`);
              }

              // Update shapeMod so change log shows actual values applied
              shapeMod.left = left;
              shapeMod.top = top;
              shapeMod.width = width;
              shapeMod.height = height;

              // Apply paragraph-level edits after text assignment and before whole-range font styles.
              await applyParagraphOpsWithHandling(
                newShape,
                shapeMod.paragraphOps,
                context,
                summary,
                targetIndex,
                slideMod.id,
                shapeMod.id,
                "create"
              );

              // Apply font properties to text box.
              const font = newShape.textFrame.textRange.font;
              if (shapeMod.textColor) font.color = shapeMod.textColor;
              if (shapeMod.fontSize) font.size = shapeMod.fontSize;
              if (shapeMod.fontFamily) font.name = shapeMod.fontFamily;
              if (shapeMod.bold !== undefined) font.bold = shapeMod.bold;
              if (shapeMod.italic !== undefined) font.italic = shapeMod.italic;

              await context.sync();
              console.log(`Created new text box: "${text.slice(0, 30)}..."`);
              shapeApplied = true;
            }
          } else if (shapeMod.action === "modify") {
            // Find existing shape by ID
            const existingShape = slide.shapes.items.find((s) => s.id === shapeMod.id);
            if (!existingShape) {
              console.log(`Shape ${shapeMod.id} not found for modification`);
              recordSkipped(summary, {
                code: "SHAPE_ID_NOT_FOUND",
                category: "target_mismatch",
                message: `Shape ${shapeMod.id} was not found for modification.`,
                slideIndex: targetIndex,
                slideId: slideMod.id,
                shapeId: shapeMod.id,
                action: "modify",
              });
              continue;
            }

            if (!hasModifyMutation(shapeMod, boundedMod)) {
              recordSkipped(summary, {
                code: "NO_MUTATION_FIELDS",
                category: "model_output_mismatch",
                message: `Modify action for shape ${shapeMod.id} did not include any supported fields.`,
                slideIndex: targetIndex,
                slideId: slideMod.id,
                shapeId: shapeMod.id,
                action: "modify",
              });
              continue;
            }

            let mutationApplied = false;
            let mutationTargetShape: PowerPoint.Shape | null = existingShape;

            // Update position/size if specified
            if (boundedMod.left !== undefined) {
              mutationTargetShape.left = boundedMod.left;
              mutationApplied = true;
            }
            if (boundedMod.top !== undefined) {
              mutationTargetShape.top = boundedMod.top;
              mutationApplied = true;
            }
            if (boundedMod.width !== undefined) {
              mutationTargetShape.width = boundedMod.width;
              mutationApplied = true;
            }
            if (boundedMod.height !== undefined) {
              mutationTargetShape.height = boundedMod.height;
              mutationApplied = true;
            }

            const normalizedTableData: TableData | undefined = shapeMod.table
              ? {
                  ...shapeMod.table,
                  textUpdates:
                    shapeMod.table.textUpdates && shapeMod.table.textUpdates.length > 0
                      ? shapeMod.table.textUpdates
                      : inferTableTextUpdates(shapeMod),
                }
              : shapeMod.cellUpdates && shapeMod.cellUpdates.length > 0
                ? {
                    action: "edit",
                    textUpdates: shapeMod.cellUpdates,
                  }
                : undefined;

            if (normalizedTableData) {
              if (!mutationTargetShape || typeof (mutationTargetShape as any).getTable !== "function") {
                recordSkipped(summary, {
                  code: "TABLE_TARGET_NOT_TABLE",
                  category: "target_mismatch",
                  message: `Shape ${shapeMod.id} is not a table target for table operations.`,
                  slideIndex: targetIndex,
                  slideId: slideMod.id,
                  shapeId: shapeMod.id,
                  action: "modify",
                });
              } else {
                const inferredAction = getTableAction(normalizedTableData, "modify");
                if (inferredAction) {
                  normalizedTableData.action = inferredAction;
                }
                const tableOpReplacesShape =
                  normalizedTableData.action === "replace" || normalizedTableData.action === "create";
                try {
                  const tableMutationResult = await applyTableOperationToExistingShape(
                    slide,
                    mutationTargetShape,
                    normalizedTableData,
                    boundedMod,
                    context
                  );
                  mutationApplied = mutationApplied || tableMutationResult.applied;
                  if (tableOpReplacesShape) {
                    // Replace/create table operations delete and recreate the shape.
                    mutationTargetShape = tableMutationResult.replacementShape || null;
                  }
                } catch (tableErr) {
                  const errMsg = tableErr instanceof Error ? tableErr.message : String(tableErr);
                  let tableErrorCode: ApplyReasonCode = "TABLE_OPERATION_FAILED";
                  if (normalizedTableData.action === "replace" || normalizedTableData.action === "create") {
                    tableErrorCode = "TABLE_REPLACE_FAILED";
                  } else if (normalizedTableData.action === "relayout") {
                    tableErrorCode = "TABLE_RELAYOUT_FAILED";
                  } else if (normalizedTableData.structureOps && normalizedTableData.structureOps.length > 0) {
                    tableErrorCode = "TABLE_STRUCTURE_OP_FAILED";
                  } else if (normalizedTableData.styleUpdates && normalizedTableData.styleUpdates.length > 0) {
                    tableErrorCode = "TABLE_STYLE_OP_FAILED";
                  } else if (normalizedTableData.clearOps && normalizedTableData.clearOps.length > 0) {
                    tableErrorCode = "TABLE_CLEAR_FAILED";
                  } else if (normalizedTableData.textUpdates && normalizedTableData.textUpdates.length > 0) {
                    tableErrorCode = "TABLE_EDIT_FAILED";
                  }
                  recordError(summary, {
                    code: tableErrorCode,
                    category: "apply_error",
                    message: `Failed table operation for shape ${shapeMod.id}: ${errMsg}`,
                    slideIndex: targetIndex,
                    slideId: slideMod.id,
                    shapeId: shapeMod.id,
                    action: "modify",
                  });
                  if (tableOpReplacesShape) {
                    mutationTargetShape = null;
                  }
                }
              }
            }

            // Apply text replacements (preserves formatting)
            if (mutationTargetShape && shapeMod.textReplacements && shapeMod.textReplacements.length > 0) {
              try {
                await applyTextReplacements(mutationTargetShape, shapeMod.textReplacements, context);
                console.log(`Applied ${shapeMod.textReplacements.length} text replacements`);
                mutationApplied = true;
              } catch (replaceError) {
                console.log("Could not apply text replacements:", replaceError);
                const errMsg =
                  replaceError instanceof Error ? replaceError.message : String(replaceError);
                recordError(summary, {
                  code: "SHAPE_MODIFY_FAILED",
                  category: "apply_error",
                  message: `Failed text replacements for shape ${shapeMod.id}: ${errMsg}`,
                  slideIndex: targetIndex,
                  slideId: slideMod.id,
                  shapeId: shapeMod.id,
                  action: "modify",
                });
              }
            }
            // Full text replacement (loses formatting) - only if no textReplacements
            else if (mutationTargetShape && shapeMod.text !== undefined) {
              try {
                mutationTargetShape.textFrame.textRange.text = shapeMod.text;
                mutationApplied = true;
              } catch {
                console.log("Could not update text for shape:", shapeMod.id);
                recordError(summary, {
                  code: "SHAPE_MODIFY_FAILED",
                  category: "apply_error",
                  message: `Text could not be updated for shape ${shapeMod.id}.`,
                  slideIndex: targetIndex,
                  slideId: slideMod.id,
                  shapeId: shapeMod.id,
                  action: "modify",
                });
              }
            }

            // Paragraph-level edits are applied after text mutations so targeting remains stable.
            if (
              mutationTargetShape &&
              await applyParagraphOpsWithHandling(
                mutationTargetShape,
                shapeMod.paragraphOps,
                context,
                summary,
                targetIndex,
                slideMod.id,
                shapeMod.id,
                "modify"
              )
            ) {
              mutationApplied = true;
            }

            // Apply fill/line styles for modify actions
            if (
              mutationTargetShape &&
              (shapeMod.fillColor || shapeMod.lineColor || shapeMod.lineWidth !== undefined)
            ) {
              try {
                if (shapeMod.fillColor) {
                  mutationTargetShape.fill.setSolidColor(shapeMod.fillColor);
                }
                if (shapeMod.lineColor) {
                  mutationTargetShape.lineFormat.color = shapeMod.lineColor;
                }
                if (shapeMod.lineWidth !== undefined) {
                  mutationTargetShape.lineFormat.weight = shapeMod.lineWidth;
                }
                mutationApplied = true;
              } catch (styleErr) {
                const errMsg = styleErr instanceof Error ? styleErr.message : String(styleErr);
                recordError(summary, {
                  code: "SHAPE_MODIFY_FAILED",
                  category: "apply_error",
                  message: `Failed to update fill/line styles for shape ${shapeMod.id}: ${errMsg}`,
                  slideIndex: targetIndex,
                  slideId: slideMod.id,
                  shapeId: shapeMod.id,
                  action: "modify",
                });
              }
            }

            // Apply font properties to existing shape
            if (
              mutationTargetShape &&
              (
                shapeMod.textColor ||
                shapeMod.fontSize ||
                shapeMod.fontFamily ||
                shapeMod.bold !== undefined ||
                shapeMod.italic !== undefined
              )
            ) {
              try {
                const font = mutationTargetShape.textFrame.textRange.font;
                if (shapeMod.textColor) font.color = shapeMod.textColor;
                if (shapeMod.fontSize) font.size = shapeMod.fontSize;
                if (shapeMod.fontFamily) font.name = shapeMod.fontFamily;
                if (shapeMod.bold !== undefined) font.bold = shapeMod.bold;
                if (shapeMod.italic !== undefined) font.italic = shapeMod.italic;
                mutationApplied = true;
              } catch {
                console.log("Could not apply font properties to shape:", shapeMod.id);
                recordError(summary, {
                  code: "SHAPE_MODIFY_FAILED",
                  category: "apply_error",
                  message: `Font properties could not be updated for shape ${shapeMod.id}.`,
                  slideIndex: targetIndex,
                  slideId: slideMod.id,
                  shapeId: shapeMod.id,
                  action: "modify",
                });
              }
            }

            if (!mutationApplied) {
              recordSkipped(summary, {
                code: "NO_MUTATION_FIELDS",
                category: "model_output_mismatch",
                message: `No supported modifications were successfully applied to shape ${shapeMod.id}.`,
                slideIndex: targetIndex,
                slideId: slideMod.id,
                shapeId: shapeMod.id,
                action: "modify",
              });
              continue;
            }

            await context.sync();
            console.log(`Modified shape: ${shapeMod.id}`);
            shapeApplied = true;
          } else if (shapeMod.action === "delete") {
            // Find and delete shape
            const shapeToDelete = slide.shapes.items.find((s) => s.id === shapeMod.id);
            if (shapeToDelete) {
              shapeToDelete.delete();
              await context.sync();
              console.log(`Deleted shape: ${shapeMod.id}`);
              shapeApplied = true;
            } else {
              console.log(`Shape ${shapeMod.id} not found for deletion`);
              recordSkipped(summary, {
                code: "SHAPE_ID_NOT_FOUND",
                category: "target_mismatch",
                message: `Shape ${shapeMod.id} was not found for deletion.`,
                slideIndex: targetIndex,
                slideId: slideMod.id,
                shapeId: shapeMod.id,
                action: "delete",
              });
            }
          }
        } catch (modError) {
          console.error(`Error applying modification to shape:`, modError);
          const errMsg = modError instanceof Error ? modError.message : String(modError);
          recordError(summary, {
            code: shapeActionErrorCode[shapeMod.action],
            category: "apply_error",
            message: `Unhandled ${shapeMod.action} error for shape ${shapeMod.id}: ${errMsg}`,
            slideIndex: targetIndex,
            slideId: slideMod.id,
            shapeId: shapeMod.id,
            action: shapeMod.action,
          });
        }

        if (shapeApplied) {
          summary.applied.shapes += 1;
          summary.applied.byAction[shapeMod.action] += 1;
          markSlideApplied(`slide:${targetIndex}`);
        }
      }
    }
  });

  summary.classification = classifyApplyResult(summary);
  logApplySummary(summary, logMsg);

  if (citationRefsToPersist.length > 0) {
    try {
      await persistCitationRefs(citationRefsToPersist, logMsg);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      logMsg(`[CITATIONS] persistCitationRefs failed (Office.js path): ${msg}`);
    }
  }

  return summary;
}

/**
 * Simple helper to insert text on the current slide
 */
export async function insertText(text: string): Promise<void> {
  if (shouldUseFactSetBridge()) {
    const normalizedText = String(text ?? "");
    let targetSlideRef = await getSelectedSlideRefViaIntents();
    if (!targetSlideRef.id) {
      const slides = (await listSlidesViaIntents(undefined, { forceRefresh: true }))
        .slice()
        .sort((left, right) => left.index - right.index);
      const fallback = slides[0];
      if (!fallback) {
        throw new Error("No slides in presentation");
      }
      targetSlideRef = { id: fallback.slideId, index: fallback.index };
    }

    const applySummary = await applyModificationsViaIntents({
      slides: [
        {
          id: targetSlideRef.id,
          index: targetSlideRef.index,
          action: "modify",
          shapes: [
            {
              id: `insert_text_${Date.now()}`,
              action: "create",
              text: normalizedText,
              left: 100,
              top: 100,
              width: 400,
              height: 50,
            },
          ],
        },
      ],
    });
    if (applySummary.errors.count > 0) {
      throw new Error(applySummary.errors.issues[0]?.message || "FactSet insertText failed.");
    }
    if (applySummary.applied.shapes <= 0) {
      throw new Error("FactSet insertText did not apply a text shape.");
    }
    return;
  }

  return guardedPowerPointRun(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items");
    await context.sync();

    if (slides.items.length === 0) {
      throw new Error("No slides in presentation");
    }

    // Use first slide if no selection
    const slide = slides.items[0];
    const textBox = slide.shapes.addTextBox(text);
    textBox.left = 100;
    textBox.top = 100;
    textBox.width = 400;
    textBox.height = 50;
    await context.sync();
  });
}

/**
 * Export the selected slide as a PNG image (base64)
 * Much more efficient than exporting entire presentation as PDF
 * Uses PowerPointApi 1.8 slide.getImageAsBase64()
 */
export async function exportSlideAsImage(
  slideIndex?: number,
  options?: { height?: number; presentationRef?: FactSetPresentationRef | null; targetSlideId?: string }
): Promise<string> {
  if (shouldUseFactSetBridge()) {
    return exportSlideAsImageViaIntents(slideIndex, options);
  }

  if (isOfficeJsBypassed()) {
    console.log("[OfficeJS NOP] exportSlideAsImage bypassed");
    return "";
  }

  return guardedPowerPointRun(async (context) => {
    let slide: PowerPoint.Slide;

    if (slideIndex !== undefined) {
      // Get specific slide by index
      const slides = context.presentation.slides;
      slides.load("items");
      await context.sync();

      if (slideIndex >= slides.items.length) {
        throw new Error(`Slide index ${slideIndex} out of range`);
      }
      slide = slides.items[slideIndex];
    } else {
      // Get currently selected slide
      slide = context.presentation.getSelectedSlides().getItemAt(0);
    }

    // Default to high-quality PNG; callers can request smaller previews.
    const height = options?.height ?? 1080;
    const slideAny = slide as any;
    const imageResult = slideAny.getImageAsBase64({ height });
    await context.sync();

    const base64 = imageResult.value;
    console.log(`Exported slide image: ${(base64.length / 1024).toFixed(1)}KB`);

    return base64;
  }, "");
}

export interface StreamedSlideThumbnail {
  index: number;
  slideNumber?: number;
  shapeCount?: number;
  thumbnail: string;
}

export interface StreamSlideThumbnailsResult {
  totalSlides: number;
  emittedSlides: number;
}

/**
 * Stream slide thumbnails using a single PowerPoint context for speed.
 * This avoids repeated PowerPoint.run setup per slide.
 */
export async function streamSlideThumbnails(
  onThumbnail: (slide: StreamedSlideThumbnail) => void,
  options?: { height?: number; maxSlides?: number; log?: Logger }
): Promise<StreamSlideThumbnailsResult> {
  if (shouldUseFactSetBridge()) {
    const log = options?.log ?? defaultLogger;
    const streamSequentially = async (): Promise<StreamSlideThumbnailsResult> => {
      const activePresentationRef = await getActivePresentationRefViaIntents(log);
      const candidateSlides = (await listSlidesViaIntents(log, {
        forceRefresh: true,
        presentationRef: activePresentationRef,
      }))
        .slice()
        .sort((left, right) => left.index - right.index);
      const maxSlides =
        typeof options?.maxSlides === "number" ? Math.max(0, Math.trunc(options.maxSlides)) : undefined;
      const targetSlides =
        typeof maxSlides === "number" ? candidateSlides.slice(0, maxSlides) : candidateSlides;

      let emittedSlides = 0;
      for (const slide of targetSlides) {
        try {
          const thumbnail = await exportSlideAsImageViaIntents(slide.index, {
            height: options?.height,
            log,
            presentationRef: activePresentationRef,
            targetSlideId: slide.slideId,
          });
          if (!thumbnail || thumbnail.length <= 100) {
            continue;
          }
          onThumbnail({
            index: slide.index,
            ...(typeof slide.slideNumber === "number" ? { slideNumber: slide.slideNumber } : {}),
            thumbnail,
          });
          emittedSlides += 1;
        } catch (fallbackError) {
          log(
            `[FACTSET] Sequential thumbnail fallback failed for slide index ${slide.index}: ${
              fallbackError instanceof Error ? fallbackError.message : String(fallbackError)
            }`
          );
        }
      }

      return { totalSlides: targetSlides.length, emittedSlides };
    };

    try {
      const streamed = await streamSlideThumbnailsViaIntents(onThumbnail, options);
      if (streamed.totalSlides === 0 || streamed.emittedSlides > 0) {
        return streamed;
      }

      log(
        "[FACTSET] Thumbnail stream returned 0 images; retrying sequentially via per-slide GetSlideImage."
      );
      return streamSequentially();
    } catch (intentError) {
      const details = intentError instanceof Error ? intentError.message : String(intentError);
      console.error("[FACTSET] streamSlideThumbnails intent failed:", intentError);
      log(`[FACTSET] Thumbnail stream failed (${details}); retrying sequentially.`);
      return streamSequentially();
    }
  }

  if (isOfficeJsBypassed()) {
    console.log("[OfficeJS NOP] streamSlideThumbnails bypassed");
    return { totalSlides: 0, emittedSlides: 0 };
  }

  let emittedSlides = 0;
  let totalSlides = 0;
  await guardedPowerPointRun(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items");
    await context.sync();

    const height = options?.height ?? 220;
    const total = options?.maxSlides
      ? Math.min(options.maxSlides, slides.items.length)
      : slides.items.length;
    totalSlides = total;

    for (let i = 0; i < total; i++) {
      const slide = slides.items[i];
      slide.shapes.load("items");
      const slideAny = slide as any;
      const imageResult = slideAny.getImageAsBase64({ height });
      await context.sync();

      const thumbnail = imageResult.value;
      if (!thumbnail || thumbnail.length <= 100) {
        continue;
      }

      onThumbnail({
        index: i,
        slideNumber: i + 1,
        shapeCount: slide.shapes.items.length,
        thumbnail,
      });
      emittedSlides += 1;
    }
  });
  return { totalSlides, emittedSlides };
}

function looksLikePdfBase64(payload: string): boolean {
  const trimmed = payload.trim();
  if (!trimmed) {
    return false;
  }
  if (/^data:application\/pdf;base64,/i.test(trimmed)) {
    return true;
  }
  const rawBase64 = trimmed.startsWith("data:")
    ? (() => {
        const separatorIdx = trimmed.indexOf(",");
        return separatorIdx >= 0 ? trimmed.slice(separatorIdx + 1).trim() : "";
      })()
    : trimmed;
  return rawBase64.startsWith("JVBERi0");
}

/**
 * Export the current presentation as a compressed PPTX payload (base64).
 * Useful for native deck snapshots and slide duplication workflows.
 */
export async function exportPresentationAsBase64Compressed(): Promise<string> {
  if (isOfficeJsBypassed()) {
    console.log("[OfficeJS NOP] exportPresentationAsBase64Compressed bypassed");
    return "";
  }
  return new Promise<string>((resolve, reject) => {
    Office.context.document.getFileAsync(
      Office.FileType.Compressed,
      { sliceSize: 4 * 1024 * 1024 },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error("getFileAsync failed"));
          return;
        }
        const file = result.value;
        const sliceCount = file.sliceCount;
        const sliceArrays: Uint8Array[] = new Array(sliceCount);
        let completed = 0;

        for (let si = 0; si < sliceCount; si++) {
          file.getSliceAsync(si, (sliceResult) => {
            if (sliceResult.status === Office.AsyncResultStatus.Succeeded) {
              sliceArrays[si] = new Uint8Array(sliceResult.value.data);
            }
            completed++;
            if (completed === sliceCount) {
              file.closeAsync();
              const totalLen = sliceArrays.reduce((sum, s) => sum + (s?.length || 0), 0);
              const combined = new Uint8Array(totalLen);
              let offset = 0;
              for (const slice of sliceArrays) {
                if (slice) {
                  combined.set(slice, offset);
                  offset += slice.length;
                }
              }
              let binary = "";
              const chunkSize = 8192;
              for (let j = 0; j < combined.length; j += chunkSize) {
                const chunk = combined.subarray(j, j + chunkSize);
                binary += String.fromCharCode.apply(null, Array.from(chunk));
              }
              resolve(btoa(binary));
            }
          });
        }
      }
    );
  });
}

/**
 * Export the current presentation to PDF as base64
 * Returns the PDF data that can be sent to vision models
 * @deprecated Use exportSlideAsImage() for single slides - much more efficient
 */
export async function exportPresentationToPdf(): Promise<string> {
  if (shouldUseFactSetBridge()) {
    try {
      const exportedBase64 = await exportPresentationAsBase64ViaIntents();
      if (!looksLikePdfBase64(exportedBase64)) {
        throw new Error(
          "FactSet ExportPresentation returned non-PDF payload; intent bridge PDF export is not supported."
        );
      }
      return exportedBase64;
    } catch (intentError) {
      const details = intentError instanceof Error ? intentError.message : String(intentError);
      console.error("[FACTSET] exportPresentationToPdf intent export failed:", intentError);
      throw new Error(`FactSet presentation export failed: ${details}`);
    }
  }

  if (isOfficeJsBypassed()) {
    console.log("[OfficeJS NOP] exportPresentationToPdf bypassed");
    return "";
  }
  return new Promise((resolve, reject) => {
    Office.context.document.getFileAsync(
      Office.FileType.Pdf,
      { sliceSize: 4194304 }, // 4MB slices
      async (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(result.error.message));
          return;
        }

        const file = result.value;
        const sliceCount = file.sliceCount;
        const slices: Uint8Array[] = [];

        // Read all slices
        for (let i = 0; i < sliceCount; i++) {
          const sliceData = await new Promise<Uint8Array>((resolveSlice, rejectSlice) => {
            file.getSliceAsync(i, (sliceResult) => {
              if (sliceResult.status === Office.AsyncResultStatus.Succeeded) {
                resolveSlice(new Uint8Array(sliceResult.value.data));
              } else {
                rejectSlice(new Error(sliceResult.error.message));
              }
            });
          });
          slices.push(sliceData);
        }

        // Close the file
        file.closeAsync();

        // Combine slices into single array
        const totalLength = slices.reduce((sum, slice) => sum + slice.length, 0);
        const combined = new Uint8Array(totalLength);
        let offset = 0;
        for (const slice of slices) {
          combined.set(slice, offset);
          offset += slice.length;
        }

        // Convert to base64
        let binary = "";
        const bytes = combined;
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        const base64 = btoa(binary);

        console.log(`Exported PDF: ${(base64.length / 1024).toFixed(1)}KB`);
        resolve(base64);
      }
    );
  });
}

/**
 * Insert multiple images as new slides (one image per slide)
 * Images are centered and sized to fit the slide while maintaining aspect ratio
 */
export async function insertImagesAsSlides(
  images: { base64_data: string; format: string; width: number; height: number }[],
  log?: (message: string) => void,
  signal?: AbortSignal
): Promise<void> {
  const logMsg = (msg: string) => {
    if (signal?.aborted) return;
    console.log("[PDF→SLIDES]", msg);
    if (log) log(msg);
  };

  if (signal?.aborted) return;
  if (shouldUseFactSetBridge()) {
    logMsg(`Starting insertion of ${images.length} images via FactSet intent bridge...`);

    // Pull slide dimensions via the cheap bounds resolver instead of a full
    // RunOfficeExtractor trip — the inserted-image flow only needs width/height
    // for placement, not shapes or paragraph text. `getResolvedSlideBoundsViaIntents`
    // sources from GetActivePresentationContext (and falls back to the extractor
    // only if the host can't answer).
    let slideWidth = inferredSlideWidth || DEFAULT_SLIDE_WIDTH;
    let slideHeight = inferredSlideHeight || DEFAULT_SLIDE_HEIGHT;
    try {
      const bounds = await getResolvedSlideBoundsViaIntents(logMsg);
      const refreshedWidth = bounds.slideWidth;
      const refreshedHeight = bounds.slideHeight;
      if (typeof refreshedWidth === "number" && refreshedWidth > 0) {
        slideWidth = refreshedWidth;
      }
      if (typeof refreshedHeight === "number" && refreshedHeight > 0) {
        slideHeight = refreshedHeight;
      }
      inferredSlideWidth = slideWidth;
      inferredSlideHeight = slideHeight;
    } catch (dimensionError) {
      logMsg(
        `⚠️ Could not refresh slide dimensions before FactSet image insertion: ${
          dimensionError instanceof Error ? dimensionError.message : String(dimensionError)
        }`
      );
    }
    if (signal?.aborted) return;

    let insertedCount = 0;
    for (let i = 0; i < images.length; i++) {
      if (signal?.aborted) return;
      const image = images[i];
      try {
        const createdSlideId = await createBlankSlideAtEndViaIntents(logMsg);
        if (signal?.aborted) return;
        if (!createdSlideId) {
          throw new Error("FactSet bridge did not return the created slide ID");
        }
        const createdSlideIndex = await resolveSlideIndexByIdViaIntents(createdSlideId, logMsg);
        if (signal?.aborted) return;
        if (createdSlideIndex === null) {
          throw new Error(`FactSet bridge could not resolve inserted slide index for ${createdSlideId}`);
        }

        const safeImageWidth = image.width > 0 ? image.width : 1;
        const safeImageHeight = image.height > 0 ? image.height : 1;
        const imgAspectRatio = safeImageWidth / safeImageHeight;
        const padding = 40;
        const maxWidth = Math.max(20, slideWidth - padding * 2);
        const maxHeight = Math.max(20, slideHeight - padding * 2);
        const slideAspectRatio = maxWidth / maxHeight;

        let finalWidth: number;
        let finalHeight: number;
        if (imgAspectRatio > slideAspectRatio) {
          finalWidth = maxWidth;
          finalHeight = maxWidth / imgAspectRatio;
        } else {
          finalHeight = maxHeight;
          finalWidth = maxHeight * imgAspectRatio;
        }

        const left = (slideWidth - finalWidth) / 2;
        const top = (slideHeight - finalHeight) / 2;

        const normalizedFormat = (image.format || "png")
          .toLowerCase()
          .replace(/^image\//, "")
          .trim();
        const mimeType =
          normalizedFormat === "jpg" || normalizedFormat === "jpeg"
            ? "jpeg"
            : normalizedFormat || "png";
        const rawPayload = (image.base64_data || "").trim();
        const dataUri =
          rawPayload.startsWith("data:") ? rawPayload : `data:image/${mimeType};base64,${rawPayload}`;

        const applySummary = await applyModifications(
          {
            slides: [
              {
                id: createdSlideId,
                index: createdSlideIndex,
                action: "modify",
                shapes: [
                  {
                    id: `pdf_image_${Date.now()}_${i + 1}`,
                    action: "create",
                    imageDataUri: dataUri,
                    imageMimeType: `image/${mimeType}`,
                    left,
                    top,
                    width: finalWidth,
                    height: finalHeight,
                  },
                ],
              },
            ],
          },
          logMsg
        );
        if (signal?.aborted) return;

        if (applySummary.errors.count > 0 || applySummary.applied.shapes === 0) {
          const issue = applySummary.errors.issues[0]?.message || "No image shape was applied";
          throw new Error(issue);
        }

        try {
          await addSlideTagsViaIntents({
            slideId: createdSlideId,
            tags: {
              "finster.source": "image-import",
              "finster.imageIndex": String(i),
              "finster.imageFormat": mimeType,
              "finster.tagVersion": "1",
            },
            log: logMsg,
          });
        } catch (tagError) {
          logMsg(
            `⚠️ AddSlideTags failed for imported image slide (continuing): ${
              tagError instanceof Error ? tagError.message : String(tagError)
            }`
          );
        }

        insertedCount++;
        logMsg(
          `✓ Inserted image ${insertedCount}/${images.length} on slide ${createdSlideIndex + 1} (${Math.round(finalWidth)}×${Math.round(finalHeight)}pt)`
        );
      } catch (e) {
        if (signal?.aborted) return;
        const errorMsg = e instanceof Error ? e.message : String(e);
        logMsg(`⚠️ Failed to insert image ${i + 1}: ${errorMsg}`);
      }
    }

    logMsg(`✓ Created ${insertedCount} new slides with images`);
    return;
  }

  if (isOfficeJsBypassed()) {
    console.log("[OfficeJS NOP] insertImagesAsSlides bypassed");
    return;
  }

  logMsg(`Starting insertion of ${images.length} images...`);

  // Insert each image in its own PowerPoint.run to avoid sync issues
  let insertedCount = 0;

  for (let i = 0; i < images.length; i++) {
    if (signal?.aborted) return;
    const image = images[i];

    try {
      await guardedPowerPointRun(async (context) => {
        if (signal?.aborted) return;
        const presentation = context.presentation;
        const slides = presentation.slides;
        slides.load("items");
        await context.sync();
        if (signal?.aborted) return;

        const slideWidth = inferredSlideWidth || DEFAULT_SLIDE_WIDTH;
        const slideHeight = inferredSlideHeight || DEFAULT_SLIDE_HEIGHT;

        const padding = 40;
        const maxWidth = slideWidth - padding * 2;
        const maxHeight = slideHeight - padding * 2;

        const newSlideIndex = slides.items.length;
        logMsg(`Adding slide at index ${newSlideIndex}...`);

        const slidesAny = slides as any;
        slidesAny.add({ index: newSlideIndex });
        await context.sync();
        if (signal?.aborted) return;

        // Reload slides to get the new slide
        slides.load("items");
        await context.sync();
        if (signal?.aborted) return;

        const newSlide = slides.items[newSlideIndex];
        if (!newSlide) {
          throw new Error(`Could not get slide at index ${newSlideIndex}`);
        }

        // Calculate size to fit slide while maintaining aspect ratio
        const imgAspectRatio = image.width / image.height;
        const slideAspectRatio = maxWidth / maxHeight;

        let finalWidth: number;
        let finalHeight: number;

        if (imgAspectRatio > slideAspectRatio) {
          finalWidth = maxWidth;
          finalHeight = maxWidth / imgAspectRatio;
        } else {
          finalHeight = maxHeight;
          finalWidth = maxHeight * imgAspectRatio;
        }

        // Center on slide
        const left = (slideWidth - finalWidth) / 2;
        const top = (slideHeight - finalHeight) / 2;

        // Insert image - addImage expects raw base64 string
        logMsg(`Inserting image (${image.width}×${image.height}, format: ${image.format})...`);

        const shapesAny = newSlide.shapes as any;

        // Try addImage first (PowerPointApi 1.4+)
        if (typeof shapesAny.addImage === "function") {
          const imageShape = shapesAny.addImage(image.base64_data);
          imageShape.left = left;
          imageShape.top = top;
          imageShape.width = finalWidth;
          imageShape.height = finalHeight;
        } else {
          // Fallback: Try using a rectangle shape with fill.setImage()
          logMsg("addImage not available, trying shape.fill.setImage() fallback...");

          // Create a rectangle shape
          const rectShape = newSlide.shapes.addGeometricShape(
            PowerPoint.GeometricShapeType.rectangle
          );
          rectShape.left = left;
          rectShape.top = top;
          rectShape.width = finalWidth;
          rectShape.height = finalHeight;

          // Remove line/border
          try {
            rectShape.lineFormat.visible = false;
          } catch {
            // Line formatting might not be available
          }

          // Set the fill to be the image
          const fill = rectShape.fill;
          if (fill && typeof fill.setImage === "function") {
            fill.setImage(image.base64_data);
            logMsg("✓ Used fill.setImage() fallback");
          } else {
            // Last resort: just show a placeholder
            logMsg("fill.setImage() also not available - showing placeholder");
            rectShape.delete();

            const textBox = newSlide.shapes.addTextBox(
              `📷 Image ${i + 1} from PDF\n\n` +
              `Size: ${image.width}×${image.height}px\n\n` +
              `⚠️ Please update PowerPoint to insert images.`
            );
            textBox.left = 50;
            textBox.top = 50;
            textBox.width = 400;
            textBox.height = 150;
          }
        }

        await context.sync();
        insertedCount++;
        logMsg(
          `✓ Inserted image ${insertedCount}/${images.length} (${Math.round(finalWidth)}×${Math.round(finalHeight)}pt)`
        );
      });
    } catch (e) {
      if (signal?.aborted) return;
      const errorMsg = e instanceof Error ? e.message : String(e);
      logMsg(`⚠️ Failed to insert image ${i + 1}: ${errorMsg}`);
      console.error("[PDF→SLIDES] Error details:", e);
    }
  }

  logMsg(`✓ Created ${insertedCount} new slides with images`);
}

/**
 * Insert a single image to the currently selected slide.
 *
 * getSelectedSlides is read in an isolated context so that a failure
 * never poisons the main context. pageSetup uses cached values.
 */
export async function insertImageToCurrentSlide(
  image: { base64_data: string; format: string; width: number; height: number },
  log?: (message: string) => void
): Promise<void> {
  const logMsg = (msg: string) => {
    console.log("[INSERT IMAGE]", msg);
    if (log) log(msg);
  };

  const slideWidth = inferredSlideWidth || DEFAULT_SLIDE_WIDTH;
  const slideHeight = inferredSlideHeight || DEFAULT_SLIDE_HEIGHT;
  const maxWidth = slideWidth * 0.6;
  const maxHeight = slideHeight * 0.6;
  const safeImageWidth = image.width > 0 ? image.width : 1;
  const safeImageHeight = image.height > 0 ? image.height : 1;
  const imgAspectRatio = safeImageWidth / safeImageHeight;

  let finalWidth: number;
  let finalHeight: number;

  if (imgAspectRatio > maxWidth / maxHeight) {
    finalWidth = maxWidth;
    finalHeight = maxWidth / imgAspectRatio;
  } else {
    finalHeight = maxHeight;
    finalWidth = maxHeight * imgAspectRatio;
  }

  const left = (slideWidth - finalWidth) / 2;
  const top = (slideHeight - finalHeight) / 2;

  if (shouldUseFactSetBridge()) {
    const normalizedFormat = (image.format || "png")
      .toLowerCase()
      .replace(/^image\//, "")
      .trim();
    const mimeType =
      normalizedFormat === "jpg" || normalizedFormat === "jpeg"
        ? "jpeg"
        : normalizedFormat || "png";
    const extension = mimeType === "jpeg" ? "jpg" : mimeType;
    const rawPayload = (image.base64_data || "").trim();
    const dataUri =
      rawPayload.startsWith("data:") ? rawPayload : `data:image/${mimeType};base64,${rawPayload}`;

    logMsg(`Inserting ${safeImageWidth}x${safeImageHeight} image via FactSet intent...`);
    await addImageFromBase64ViaIntents({
      name: `finster-image-${Date.now()}.${extension || "png"}`,
      dataUri,
      insertOptions: {
        left,
        top,
        width: finalWidth,
        height: finalHeight,
      },
    });
    logMsg(`✓ Image inserted (${Math.round(finalWidth)}x${Math.round(finalHeight)}pt)`);
    return;
  }

  if (isOfficeJsBypassed()) {
    console.log("[OfficeJS NOP] insertImageToCurrentSlide bypassed");
    return;
  }

  const selectedId = await readSelectedSlideIdInIsolatedContext(
    "[INSERT IMAGE] Could not get selected slide"
  );

  return guardedPowerPointRun(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items/id");
    await context.sync();

    if (slides.items.length === 0) {
      throw new Error("No slides in presentation");
    }

    let slide: PowerPoint.Slide | undefined;
    if (selectedId) {
      slide = slides.items.find((s) => s.id === selectedId);
    }
    if (!slide) {
      slide = slides.items[0];
    }

    logMsg(`Inserting ${safeImageWidth}x${safeImageHeight} image...`);

    const shapesAny = slide.shapes as any;

    if (typeof shapesAny.addImage === "function") {
      const imageShape = shapesAny.addImage(image.base64_data);
      imageShape.left = left;
      imageShape.top = top;
      imageShape.width = finalWidth;
      imageShape.height = finalHeight;
    } else {
      logMsg("Using fill.setImage fallback...");
      const rectShape = slide.shapes.addGeometricShape(PowerPoint.GeometricShapeType.rectangle);
      rectShape.left = left;
      rectShape.top = top;
      rectShape.width = finalWidth;
      rectShape.height = finalHeight;

      try {
        rectShape.lineFormat.visible = false;
      } catch {
        // Ignore
      }

      const fill = rectShape.fill;
      if (fill && typeof fill.setImage === "function") {
        fill.setImage(image.base64_data);
      } else {
        throw new Error("Image insertion not supported in this PowerPoint version");
      }
    }

    await context.sync();
    logMsg(`✓ Image inserted (${Math.round(finalWidth)}x${Math.round(finalHeight)}pt)`);
  });
}

/**
 * Get selected slide reference (stable ID + current index).
 *
 * getSelectedSlides is read in an isolated context so that a failure
 * never poisons the main context that reads allSlides.items.
 */
export async function getSelectedSlideRef(): Promise<SlideRef> {
  if (shouldUseFactSetBridge()) {
    try {
      const selectedRef = await getSelectedSlideRefViaIntents();
      if (selectedRef.id) {
        lastKnownSelectedSlideId = selectedRef.id;
      }
      return selectedRef;
    } catch (error) {
      console.warn("[FACTSET] getSelectedSlideRef via intent bridge failed:", error);
      throw error instanceof Error
        ? error
        : new Error(`FactSet intent selected-slide lookup failed: ${String(error)}`);
    }
  }

  if (isOfficeJsBypassed()) {
    console.log("[OfficeJS NOP] getSelectedSlideRef bypassed");
    const selectedSlideIndex = Math.max(0, Math.trunc(lastKnownPresentationContent.selectedSlideIndex ?? 0));
    const selectedSlide =
      lastKnownPresentationContent.slides.find((slide) => slide.index === selectedSlideIndex) ??
      lastKnownPresentationContent.slides[selectedSlideIndex];
    const fallbackId = selectedSlide?.id ?? lastKnownSelectedSlideId ?? "";
    if (fallbackId) {
      lastKnownSelectedSlideId = fallbackId;
    }
    return {
      id: fallbackId,
      index: selectedSlide?.index ?? selectedSlideIndex,
      slideNumber: selectedSlide?.slideNumber ?? (selectedSlide?.index ?? selectedSlideIndex) + 1,
    };
  }

  const selectedId = await readSelectedSlideIdInIsolatedContext(
    "Could not get selected slide, defaulting to first slide"
  );

  return guardedPowerPointRun(async (context) => {
    const allSlides = context.presentation.slides;
    allSlides.load("items/id");
    await context.sync();

    if (allSlides.items.length === 0) {
      return { id: "", index: 0 };
    }

    if (selectedId) {
      const index = allSlides.items.findIndex((s) => s.id === selectedId);
      if (index >= 0) {
        return { id: selectedId, index, slideNumber: index + 1 };
      }
    }

    return { id: allSlides.items[0].id, index: 0, slideNumber: 1 };
  }, { id: "", index: 0 });
}

/**
 * Get selected slide index (0-based)
 */
export async function getSelectedSlideIndex(): Promise<number> {
  const slideRef = await getSelectedSlideRef();
  return slideRef.index;
}

/**
 * Cheap "what slides exist?" lookup. Returns just stable IDs and indices —
 * no shapes, no text, no tables. Use this for tab reconciliation, navigation,
 * and any other UX that only needs to know which slides exist and their
 * order. Reserve `extractPresentationContent` for cases where shape/text
 * content is actually needed (LLM context, deck creation, vision review).
 *
 * In the FactSet bridge this hits `factset.Presentation.GetSlideList`
 * (lightweight) instead of `factset.Presentation.RunOfficeExtractor`
 * (returns the entire deck). The slide-list result is also cached for 10s
 * inside the bridge.
 */
export async function listSlides(options?: {
  presentationRef?: FactSetPresentationRef | null;
  forceRefresh?: boolean;
}): Promise<SlideRef[]> {
  if (shouldUseFactSetBridge()) {
    try {
      const slides = await listSlidesViaIntents(undefined, {
        forceRefresh: options?.forceRefresh,
        presentationRef: options?.presentationRef,
      });
      return slides.map((slide) => ({
        id: slide.slideId,
        index: slide.index,
        ...(typeof slide.slideNumber === "number" ? { slideNumber: slide.slideNumber } : {}),
      }));
    } catch (error) {
      console.warn("[FACTSET] listSlides via intent bridge failed:", error);
      throw error instanceof Error
        ? error
        : new Error(`FactSet intent slide-list lookup failed: ${String(error)}`);
    }
  }

  if (isOfficeJsBypassed()) {
    console.log("[OfficeJS NOP] listSlides bypassed");
    return lastKnownPresentationContent.slides.map((slide) => ({
      id: slide.id,
      index: slide.index,
      slideNumber: slide.slideNumber ?? slide.index + 1,
    }));
  }

  return guardedPowerPointRun(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items/id");
    await context.sync();
    return slides.items.map((slide, index) => ({ id: slide.id, index, slideNumber: index + 1 }));
  }, []);
}

/**
 * Navigate to a specific slide by stable slide ID.
 * Optional index fallback can be enabled explicitly for legacy flows.
 */
export async function goToSlideById(
  slideId: string,
  fallbackIndex?: number,
  options?: { allowIndexFallback?: boolean }
): Promise<void> {
  return guardedPowerPointRun(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items/id");
    await context.sync();

    if (slides.items.length === 0) {
      return;
    }

    const allowIndexFallback = options?.allowIndexFallback ?? false;
    let targetSlide = slides.items.find((s) => s.id === slideId);
    if (
      !targetSlide &&
      allowIndexFallback &&
      fallbackIndex !== undefined &&
      fallbackIndex >= 0 &&
      fallbackIndex < slides.items.length
    ) {
      targetSlide = slides.items[fallbackIndex];
    }
    if (!targetSlide) {
      return;
    }

    targetSlide.load("id");
    await context.sync();

    // Use setSelectedSlides to navigate to the slide
    const presentationAny = context.presentation as any;
    if (presentationAny.setSelectedSlides) {
      presentationAny.setSelectedSlides([targetSlide.id]);
      await context.sync();
      return;
    }

    // Fallback: Use Office.context.document.goToByIdAsync
    const Office = (globalThis as any).Office;
    if (Office?.context?.document?.goToByIdAsync) {
      await new Promise<void>((resolve) => {
        Office.context.document.goToByIdAsync(
          targetSlide!.id,
          Office.GoToType.Slide,
          (result: { status: string; error?: { message: string } }) => {
            if (result.status === "failed") {
              console.warn("[goToSlideById] goToByIdAsync failed:", result.error?.message);
            }
            resolve();
          }
        );
      });
    }
  });
}

/**
 * Navigate to a specific slide by index (0-based)
 */
export async function goToSlide(slideIndex: number): Promise<void> {
  const targetSlideId = await guardedPowerPointRun(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items/id");
    await context.sync();
    if (slideIndex >= 0 && slideIndex < slides.items.length) {
      return slides.items[slideIndex].id;
    }
    return "";
  }, "");

  if (!targetSlideId) {
    return;
  }

  await goToSlideById(targetSlideId);
}

/**
 * Create a new deck from a plan
 * Creates slides with titles and bullet points
 */
export interface SlideOutline {
  index: number;
  title: string;
  bullets: string[];
}

export interface DeckPlan {
  topic: string;
  slideCount: number;
  slides: SlideOutline[];
  summary: string;
}

function buildLegacyDeckSlideShapes(
  slideOutline: SlideOutline,
  slideIndex: number,
  totalSlides: number,
  slideWidth: number,
  slideHeight: number
): ShapeModification[] {
  const isFirstSlide = slideIndex === 0;
  const _isLastSlide = slideIndex === totalSlides - 1; // Reserved for future use (e.g., conclusion styling)
  void _isLastSlide;

  const shapes: ShapeModification[] = [
    {
      id: `legacy_deck_title_${slideIndex}`,
      action: "create",
      text: slideOutline.title,
      left: isFirstSlide ? slideWidth * 0.1 : 50,
      top: isFirstSlide ? slideHeight * 0.35 : 30,
      width: isFirstSlide ? slideWidth * 0.8 : slideWidth - 100,
      height: isFirstSlide ? 80 : 60,
      fontSize: isFirstSlide ? 44 : 32,
      bold: true,
    },
  ];

  if (slideOutline.bullets.length > 0 && !isFirstSlide) {
    const bulletText = slideOutline.bullets.map((bullet) => `• ${bullet}`).join("\n");
    shapes.push({
      id: `legacy_deck_body_${slideIndex}`,
      action: "create",
      text: bulletText,
      left: 50,
      top: 110,
      width: slideWidth - 100,
      height: slideHeight - 150,
      fontSize: 20,
    });
  }

  return shapes;
}

async function createDeckFromPlanViaIntentBridge(
  plan: DeckPlan,
  logMsg: (message: string) => void
): Promise<void> {
  const slideWidth = inferredSlideWidth || DEFAULT_SLIDE_WIDTH;
  const slideHeight = inferredSlideHeight || DEFAULT_SLIDE_HEIGHT;

  for (let i = 0; i < plan.slides.length; i++) {
    const slideOutline = plan.slides[i];

    try {
      const createdSlideId = await createBlankSlideAtEndViaIntents(logMsg);
      if (!createdSlideId) {
        throw new Error("FactSet bridge did not return the created slide ID");
      }

      const createdSlideIndex = await resolveSlideIndexByIdViaIntents(createdSlideId, logMsg);
      if (createdSlideIndex === null) {
        throw new Error(`FactSet bridge could not resolve inserted slide index for ${createdSlideId}`);
      }

      const applySummary = await applyModifications(
        {
          slides: [
            {
              id: createdSlideId,
              index: createdSlideIndex,
              action: "modify",
              shapes: buildLegacyDeckSlideShapes(
                slideOutline,
                i,
                plan.slides.length,
                slideWidth,
                slideHeight
              ),
            },
          ],
        },
        logMsg
      );

      if (applySummary.errors.count > 0) {
        const firstIssue = applySummary.errors.issues[0]?.message ?? "Unknown apply error";
        throw new Error(firstIssue);
      }

      logMsg(
        `✓ Created slide ${i + 1}/${plan.slides.length}: ${slideOutline.title.substring(0, 40)}...`
      );
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : String(e);
      logMsg(`⚠️ Error creating slide ${i + 1}: ${errorMsg}`);
    }
  }
}

export async function createDeckFromPlan(
  plan: DeckPlan,
  log?: (message: string) => void
): Promise<void> {
  const logMsg = (msg: string) => {
    console.log("[DECK CREATE]", msg);
    if (log) log(msg);
  };

  logMsg(`Creating deck: "${plan.topic}" with ${plan.slides.length} slides`);

  if (shouldUseFactSetBridge()) {
    await createDeckFromPlanViaIntentBridge(plan, logMsg);
    logMsg(`✓ Deck created with ${plan.slides.length} slides`);
    return;
  }

  if (isOfficeJsBypassed()) {
    console.log("[OfficeJS NOP] createDeckFromPlan bypassed");
    return;
  }

  // Create each slide
  for (let i = 0; i < plan.slides.length; i++) {
    const slideOutline = plan.slides[i];

    try {
      await guardedPowerPointRun(async (context) => {
        const presentation = context.presentation;
        const slides = presentation.slides;
        slides.load("items");
        await context.sync();

        // Add a new slide at the end
        const newSlideIndex = slides.items.length;

        const slidesAny = slides as any;
        slidesAny.add({ index: newSlideIndex });
        await context.sync();

        // Reload slides to get the new one
        slides.load("items");
        await context.sync();

        const newSlide = slides.items[newSlideIndex];

        const slideWidth = inferredSlideWidth || DEFAULT_SLIDE_WIDTH;
        const slideHeight = inferredSlideHeight || DEFAULT_SLIDE_HEIGHT;

        const isFirstSlide = i === 0;
        const _isLastSlide = i === plan.slides.length - 1; // Reserved for future use (e.g., conclusion styling)

        // Add title
        const titleBox = newSlide.shapes.addTextBox(slideOutline.title);

        if (isFirstSlide) {
          // Title slide - centered, larger
          titleBox.left = slideWidth * 0.1;
          titleBox.top = slideHeight * 0.35;
          titleBox.width = slideWidth * 0.8;
          titleBox.height = 80;
        } else {
          // Content slide - top area
          titleBox.left = 50;
          titleBox.top = 30;
          titleBox.width = slideWidth - 100;
          titleBox.height = 60;
        }

        // Style the title
        try {
          const titleTextFrame = titleBox.textFrame;
          const titleParagraph = titleTextFrame.textRange;
          titleParagraph.font.size = isFirstSlide ? 44 : 32;
          titleParagraph.font.bold = true;
        } catch {
          // Font styling might not be available
        }

        // Add bullets (for non-title slides)
        if (slideOutline.bullets.length > 0 && !isFirstSlide) {
          const bulletText = slideOutline.bullets.map((b) => `• ${b}`).join("\n");
          const contentBox = newSlide.shapes.addTextBox(bulletText);

          contentBox.left = 50;
          contentBox.top = 110;
          contentBox.width = slideWidth - 100;
          contentBox.height = slideHeight - 150;

          // Style the content
          try {
            const contentTextFrame = contentBox.textFrame;
            const contentParagraph = contentTextFrame.textRange;
            contentParagraph.font.size = 20;
          } catch {
            // Font styling might not be available
          }
        }

        await context.sync();
        logMsg(
          `✓ Created slide ${i + 1}/${plan.slides.length}: ${slideOutline.title.substring(0, 40)}...`
        );
      });
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : String(e);
      logMsg(`⚠️ Error creating slide ${i + 1}: ${errorMsg}`);
    }
  }

  logMsg(`✓ Deck created with ${plan.slides.length} slides`);
}

/**
 * Test function: extracts all slides, then for each slide creates a new blank
 * slide and replays the extracted shapes onto it via applyModifications.
 * This validates whether our JSON extraction captures enough to reproduce slides.
 */
export async function testExtractAndReplicate(
  log: Logger = defaultLogger
): Promise<void> {
  log(`🧪 Test: Extracting current presentation...`);
  const content = await extractPresentationContent(log, { forceRefresh: true });
  const slideWidth = content.slideWidth ?? 960;
  const slideHeight = content.slideHeight ?? 540;
  log(`🧪 Found ${content.slides.length} slides (${slideWidth}x${slideHeight})`);

  for (let si = 0; si < content.slides.length; si++) {
    const slide = content.slides[si];
    log(`🧪 Replicating slide ${si + 1} (${slide.shapes.length} shapes)...`);

    let newSlideId: string | undefined;
    let newSlideIndex: number | undefined;

    if (shouldUseFactSetBridge()) {
      const createdSlideId = await createBlankSlideAtEndViaIntents(log);
      if (!createdSlideId) {
        log(`⚠️ Failed to create blank slide for replication ${si + 1}`);
        continue;
      }
      newSlideId = createdSlideId;
      const resolvedIndex = await resolveSlideIndexByIdViaIntents(createdSlideId, log);
      if (resolvedIndex === null) {
        log(`⚠️ Could not resolve new slide index`);
        continue;
      }
      newSlideIndex = resolvedIndex;
    } else {
      await guardedPowerPointRun(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items/id");
        await context.sync();
        const beforeIds = new Set(slides.items.map((s) => s.id));
        (slides as any).add({ index: slides.items.length });
        await context.sync();
        slides.load("items/id");
        await context.sync();
        const afterItem = slides.items.find((s) => !beforeIds.has(s.id));
        newSlideId = afterItem?.id;
      });

      if (!newSlideId) {
        log(`⚠️ Failed to create blank slide for replication ${si + 1}`);
        continue;
      }

      await guardedPowerPointRun(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items/id");
        await context.sync();
        newSlideIndex = slides.items.findIndex((s) => s.id === newSlideId);
      });
    }

    if (newSlideIndex === undefined || newSlideIndex < 0) {
      log(`⚠️ Could not find new slide index`);
      continue;
    }

    const shapes: ShapeModification[] = slide.shapes
      .filter((s) => s.type === "textBox" || s.type === "geometricShape" || s.type === "table" || s.type === "line")
      .map((s, i) => {
        const mod: ShapeModification = {
          id: `rep_${si}_${i}`,
          action: "create",
          left: s.left,
          top: s.top,
          width: s.width,
          height: s.height,
        };

        if (s.type === "table" && s.tableData) {
          const snap = s.tableData.styleSnapshot;
          let specificCells: TableCellProperties[][] | undefined;
          if (snap?.cellOverrides?.length && s.tableData.rowCount && s.tableData.colCount) {
            specificCells = Array.from({ length: s.tableData.rowCount }, () =>
              Array.from({ length: s.tableData.colCount }, () => ({}))
            );
            for (const ov of snap.cellOverrides) {
              if (ov.set && ov.row < s.tableData.rowCount && ov.col < s.tableData.colCount) {
                specificCells[ov.row][ov.col] = ov.set;
              }
            }
          }
          mod.table = {
            action: "create",
            rows: s.tableData.rows,
            columnWidths: s.tableData.columnWidths,
            rowHeights: s.tableData.rowHeights,
            tableStyle: snap?.tableStyle,
            styleSettings: snap?.styleSettings,
            uniformCellProperties: snap?.uniformCellProperties,
            specificCellProperties: specificCells,
            mergedAreas: s.tableData.mergedAreas,
          };
        } else {
          // If shape has a fill or border but no geometricType, promote to rectangle
          // (plain text boxes don't support fill colors or outlines)
          if ((s.fillColor || s.lineColor) && !s.geometricType) {
            mod.geometricType = "rectangle";
          } else if (s.geometricType) {
            mod.geometricType = s.geometricType;
          }
          if (s.fillColor) mod.fillColor = s.fillColor;
          if (s.lineColor) mod.lineColor = s.lineColor;
          if (s.lineWidth) mod.lineWidth = s.lineWidth;
          if (s.text) mod.text = s.text;
          if (s.textColor) mod.textColor = s.textColor;
          if (s.fontSize) mod.fontSize = s.fontSize;
          if (s.fontFamily) mod.fontFamily = s.fontFamily;
          if (s.bold !== undefined) mod.bold = s.bold;
          if (s.italic !== undefined) mod.italic = s.italic;
          if (s.verticalAlignment) {
            const vaMap: Record<string, ShapeModification["verticalAlignment"]> = {
              Top: "top", Middle: "middle", Bottom: "bottom",
              top: "top", middle: "middle", bottom: "bottom",
            };
            mod.verticalAlignment = vaMap[s.verticalAlignment] || undefined;
          }

          // Replay per-paragraph formatting: alignment, bullets, and fonts
          const paraOps: ParagraphOp[] = [];
          if (s.horizontalAlignment) {
            paraOps.push({
              target: { type: "all" as const },
              set: { horizontalAlignment: s.horizontalAlignment as any },
            });
          }
          const mapBulletType = (raw?: string): "Numbered" | "Unnumbered" | undefined => {
            if (!raw) return "Unnumbered";
            const lower = raw.toLowerCase();
            if (lower === "numbered" || lower === "alphanumeric") return "Numbered";
            if (lower === "unnumbered" || lower === "character" || lower === "picture") return "Unnumbered";
            return "Unnumbered";
          };
          if (s.paragraphs) {
            for (const p of s.paragraphs) {
              const set: ParagraphSet = {};
              if (p.bulletVisible) {
                set.bullet = {
                  visible: true,
                  type: mapBulletType(p.bulletType),
                  style: p.bulletStyle as any,
                };
              }
              const hasFont = p.fontSize || p.fontFamily || p.textColor || p.bold !== undefined || p.italic !== undefined;
              if (hasFont) {
                set.font = {};
                if (p.fontSize) set.font.size = p.fontSize;
                if (p.fontFamily) set.font.family = p.fontFamily;
                if (p.textColor) set.font.color = p.textColor;
                if (p.bold !== undefined) set.font.bold = p.bold;
                if (p.italic !== undefined) set.font.italic = p.italic;
              }
              if (Object.keys(set).length > 0) {
                paraOps.push({
                  target: { type: "indices" as const, indices: [p.index] },
                  set,
                });
              }
            }
          }
          if (paraOps.length > 0) mod.paragraphOps = paraOps;
        }

        return mod;
      });

    if (shapes.length === 0) {
      log(`🧪 Slide ${si + 1}: no replicable shapes, skipping`);
      continue;
    }

    const modification: PresentationModification = {
      slides: [
        {
          index: newSlideIndex,
          id: newSlideId,
          shapes,
        },
      ],
    };

    await applyModifications(modification, log);
    log(`✅ Slide ${si + 1} replicated with ${shapes.length} shapes`);
  }

  log(`🧪 Test complete! ${content.slides.length} slides replicated.`);
}

const COMPOSITE_DEMO_DATA: CompositeLayout[] = [
  {
    type: "numberedRows",
    title: "Investment Thesis — Key Pillars",
    items: [
      { label: "Platform Scalability", description: "Cloud-native architecture supports 10x user growth without proportional cost increase" },
      { label: "Recurring Revenue", description: "95% subscription-based revenue with 120%+ net retention rate" },
      { label: "Market Position", description: "Top-3 market share in core verticals with expanding TAM" },
      { label: "M&A Optionality", description: "Strong balance sheet enables accretive tuck-in acquisitions" },
    ],
  },
  {
    type: "iconTextRows",
    title: "Executive Summary — Strategic Priorities",
    items: [
      { label: "Growth", bullets: ["Expand enterprise segment by 30%", "Launch 2 new product lines in H2"] },
      { label: "Efficiency", bullets: ["Reduce OpEx ratio from 68% to 62%", "Automate 40% of manual workflows"] },
      { label: "Risk", bullets: ["Diversify revenue across 3+ geographies", "Strengthen cybersecurity posture"] },
    ],
  },
  {
    type: "statGrid",
    title: "Q4 Financial Snapshot",
    items: [
      { label: "Revenue", description: "$12.4M" },
      { label: "EBITDA Margin", description: "24.3%" },
      { label: "Net New Clients", description: "47" },
      { label: "Employee NPS", description: "72" },
      { label: "Churn Rate", description: "2.1%" },
      { label: "Pipeline", description: "$38M" },
    ],
  },
  {
    type: "sectionBlock",
    title: "Business Overview",
    items: [
      { label: "Company Profile", bullets: ["Global enterprise SaaS platform", "Founded 2012, IPO 2019", "3,200 employees across 12 offices"] },
      { label: "Products & Services", bullets: ["Core platform (65% of revenue)", "Analytics suite (25%)", "Professional services (10%)"] },
      { label: "Key Differentiators", bullets: ["AI-embedded workflows", "99.99% uptime SLA", "SOC 2 Type II certified"] },
    ],
  },
  {
    type: "columnCards",
    title: "Current vs Target Operating Model",
    items: [
      { label: "Current State", bullets: ["Siloed teams", "Manual reporting", "Reactive risk mgmt", "Legacy systems"] },
      { label: "Transition", bullets: ["Cross-functional pods", "Dashboard rollout", "Risk framework design", "Cloud migration"] },
      { label: "Target State", bullets: ["Integrated platform", "Real-time analytics", "Proactive risk sensing", "API-first architecture"] },
      { label: "Success Metrics", bullets: ["95% uptime SLA", "40% cost reduction", "Real-time dashboards", "Zero manual handoffs"] },
    ],
  },
  {
    type: "quadrantGrid",
    title: "Strategic Priority Matrix",
    axisLabels: { x: "Short-term ←→ Long-term", y: "Growth ←→ Efficiency" },
    items: [
      { label: "Quick Wins", bullets: ["Cost reduction", "Process automation", "Vendor consolidation"] },
      { label: "Strategic Bets", bullets: ["New market entry", "M&A pipeline", "Platform investment"] },
      { label: "Operational Fixes", bullets: ["Legacy migration", "Compliance remediation"] },
      { label: "Foundational", bullets: ["Data infrastructure", "Talent development", "Culture change"] },
    ],
  },
  {
    type: "processFlow",
    title: "Investment Process Flow",
    items: [
      { label: "Screening", description: "Identify targets" },
      { label: "Due Diligence", description: "Financial & legal review" },
      { label: "Valuation", description: "DCF & comparables" },
      { label: "Execution", description: "Negotiate & close" },
    ],
  },
  {
    type: "kpiDashboard",
    title: "Q4 Performance Dashboard",
    items: [
      { label: "Revenue", description: "$12.4M" },
      { label: "EBITDA Margin", description: "24.3%" },
      { label: "Net New Clients", description: "47" },
      { label: "Employee NPS", description: "72" },
      { label: "Churn Rate", description: "2.1%" },
      { label: "Pipeline", description: "$38M" },
    ],
  },
  {
    type: "orgChart",
    title: "Leadership Team",
    items: [
      { label: "CEO", description: "Jane Smith" },
      { label: "CFO", description: "John Doe" },
      { label: "CTO", description: "Alice Chen" },
      { label: "COO", description: "Bob Park" },
      { label: "CMO", description: "Sarah Lee" },
    ],
  },
  {
    type: "bulletBox",
    title: "Key Takeaways",
    items: [
      { label: "Summary" },
      { label: "Institutional Buyside remains the core ASV base at $1,193M." },
      { label: "Wealth is the fastest-growing disclosed firm type at +10%." },
      { label: "Dealmakers and Market Infrastructure each grew +8%, adding growth beyond the core buyside base." },
    ],
  },
  {
    type: "splitContent",
    title: "Research Analyst Perspectives",
    items: [
      {
        label: "Selected Analyst Ratings",
        bullets: ["94% Buy consensus in latest quarter", "Target prices range $607–$650", "10 brokers covering"],
      },
      {
        label: "Selected Recommendations",
        bullets: ["Median target: $630.00", "Mean target: $630.57", "Premium to current: 22.9%"],
      },
      {
        label: "Guidance Update",
        bullets: [
          "Share price increased following news of planned separation",
          "Significant debt burden of $35 billion remains a key challenge",
          "Earnings conference call scheduled before equity market opens",
        ],
      },
    ],
  },
];

export async function testCompositeLayouts(log: Logger = defaultLogger): Promise<void> {
  let slideWidth = inferredSlideWidth || DEFAULT_SLIDE_WIDTH;
  let slideHeight = inferredSlideHeight || DEFAULT_SLIDE_HEIGHT;
  if (shouldUseFactSetBridge()) {
    const bounds = await getResolvedSlideBoundsViaIntents(log);
    slideWidth = bounds.slideWidth;
    slideHeight = bounds.slideHeight;
  }

  for (const demo of COMPOSITE_DEMO_DATA) {
    log(`🎨 Creating ${demo.type} slide...`);

    let newSlideId: string | undefined;
    let newSlideIndex = -1;

    if (shouldUseFactSetBridge()) {
      const createdSlideId = await createBlankSlideAtEndViaIntents(log);
      if (!createdSlideId) {
        log(`⚠️ Could not create slide for ${demo.type}`);
        continue;
      }
      newSlideId = createdSlideId;
      const resolvedIndex = await resolveSlideIndexByIdViaIntents(createdSlideId, log);
      if (resolvedIndex === null) {
        log(`⚠️ Could not resolve slide index for ${demo.type}`);
        continue;
      }
      newSlideIndex = resolvedIndex;
    } else {
      await guardedPowerPointRun(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items/id");
        await context.sync();
        const beforeCount = slides.items.length;
        slides.add();
        await context.sync();
        slides.load("items/id");
        await context.sync();
        const newSlide = slides.items[beforeCount];
        newSlideId = newSlide?.id;
        newSlideIndex = beforeCount;
      });
    }

    if (!newSlideId || newSlideIndex < 0) {
      log(`⚠️ Could not create slide for ${demo.type}`);
      continue;
    }

    const shapes = generateCompositeLayoutShapes(demo, slideWidth, slideHeight, undefined, {
      useTextBoxesForStatGrid: shouldUseFactSetBridge(),
    });

    const modification: PresentationModification = {
      slides: [{ index: newSlideIndex, id: newSlideId, shapes }],
    };

    await applyModifications(modification, log);
    log(`✅ ${demo.type} slide created with ${shapes.length} shapes`);
  }

  log(`🎨 All ${COMPOSITE_DEMO_DATA.length} composite layouts generated!`);

  // Multi-chart dashboard demo (native charts)
  log(`📊 Creating multi-chart dashboard demo...`);
  await testMultiChartDashboard(log);
}

async function testMultiChartDashboard(log: Logger): Promise<void> {
  const slideWidth = inferredSlideWidth || DEFAULT_SLIDE_WIDTH;
  const slideHeight = inferredSlideHeight || DEFAULT_SLIDE_HEIGHT;

  let newSlideId: string | undefined;
  let newSlideIndex = -1;

  if (shouldUseFactSetBridge()) {
    const createdSlideId = await createBlankSlideAtEndViaIntents(log);
    if (!createdSlideId) {
      log(`⚠️ Could not create slide for multi-chart dashboard`);
      return;
    }
    newSlideId = createdSlideId;
    const resolvedIndex = await resolveSlideIndexByIdViaIntents(createdSlideId, log);
    if (resolvedIndex === null) {
      log(`⚠️ Could not resolve slide index for multi-chart dashboard`);
      return;
    }
    newSlideIndex = resolvedIndex;
  } else {
    await guardedPowerPointRun(async (context) => {
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();
      const beforeCount = slides.items.length;
      slides.add();
      await context.sync();
      slides.load("items/id");
      await context.sync();
      const newSlide = slides.items[beforeCount];
      newSlideId = newSlide?.id;
      newSlideIndex = beforeCount;
    });
  }

  if (!newSlideId || newSlideIndex < 0) {
    log(`⚠️ Could not create slide for multi-chart dashboard`);
    return;
  }

  // Layout constants in points — use fixed proportions that work for 720×540 or 960×540
  const margin = 30;
  const gap = 10;
  const titleAreaH = 80;
  const contentW = slideWidth - margin * 2;
  const contentH = slideHeight - titleAreaH - margin;
  const cellW = Math.round((contentW - gap) / 2);
  const cellH = Math.round((contentH - gap) / 2);
  const col1X = margin;
  const col2X = margin + cellW + gap;
  const row1Y = titleAreaH;
  const row2Y = titleAreaH + cellH + gap;

  log(`📊 Chart dashboard layout: slide=${slideWidth}×${slideHeight}, cell=${cellW}×${cellH}, col2X=${col2X}, row2Y=${row2Y}`);

  const shapes: ShapeModification[] = [
    // Title
    {
      id: "chart_demo_title",
      action: "create",
      text: "Gearing Over Time",
      left: margin,
      top: 20,
      width: contentW,
      height: 28,
      fontSize: 22,
      bold: true,
      fontFamily: "Calibri",
    },
    // Subtitle
    {
      id: "chart_demo_subtitle",
      action: "create",
      text: "Leverage levels have averaged ~0.9x over the last ~4 years with strong capacity to service debt",
      left: margin,
      top: 48,
      width: contentW,
      height: 24,
      fontSize: 11,
      textColor: "#555555",
      fontFamily: "Calibri",
    },
    // Commentary panel header (top-left)
    {
      id: "chart_demo_commentary_header",
      action: "create",
      geometricType: "rectangle",
      left: col1X,
      top: row1Y,
      width: cellW,
      height: 20,
      fillColor: "#1B365D",
      text: "Commentary",
      fontSize: 9,
      bold: true,
      textColor: "#FFFFFF",
      fontFamily: "Calibri",
      verticalAlignment: "middle",
    },
    // Commentary panel body (top-left)
    {
      id: "chart_demo_commentary_body",
      action: "create",
      geometricType: "rectangle",
      left: col1X,
      top: row1Y + 20,
      width: cellW,
      height: cellH - 20,
      fillColor: "#F8F8F8",
      lineColor: "#DDDDDD",
      lineWidth: 0.5,
      text: "\u2022 Gearing on a pre-AASB16 basis results in FY23F levels at ~0.9x and FY24F at ~0.6x\n\n\u2022 Facility with Westpac is fairly competitive and initially secured in a competitive process\n\n\u2022 Strong earnings generation has resulted in a strong ability to service debt and meet covenant requirements",
      fontSize: 9,
      textColor: "#333333",
      fontFamily: "Calibri",
      verticalAlignment: "top",
    },
    // Net Debt / EBITDA chart (top-right)
    {
      id: "chart_demo_net_debt",
      action: "create",
      left: col2X,
      top: row1Y,
      width: cellW,
      height: cellH,
      chart: {
        type: "Chart",
        chartType: "xlColumnClustered",
        title: { hasTitle: true, text: "Net Debt / EBITDA", font: { size: 11 } },
        series: [
          {
            index: 0,
            name: "Net Debt / EBITDA",
            categories: "[\"FY19A\",\"FY20A\",\"FY21A\",\"FY22A\",\"FY23F\",\"FY24F\"]",
            values: "[0.3,1.0,1.1,1.1,1.1,0.5]",
            fill: { colorRgb: "1B365D" },
            hasDataLabels: true,
            dataLabels: { showValue: true, position: "xlLabelPositionOutsideEnd", font: { size: 8 } },
          },
        ],
        axes: [
          { axisType: "xlValue", axisGroup: "xlPrimary", enabled: false, hasMajorGridlines: false, hasMinorGridlines: false, axisLine: { isVisible: false }, tickLabelFont: { colorRgb: "FFFFFF", size: 1 } },
          { axisType: "xlCategory", axisGroup: "xlPrimary", hasTitle: false, hasMajorGridlines: false, hasMinorGridlines: false, tickLabelFont: { size: 8 }, axisLine: { isVisible: false } },
        ],
        position: { left: col2X, top: row1Y, width: cellW, height: cellH },
      },
    },
    // Interest Coverage chart (bottom-left)
    {
      id: "chart_demo_interest_cov",
      action: "create",
      left: col1X,
      top: row2Y,
      width: cellW,
      height: cellH,
      chart: {
        type: "Chart",
        chartType: "xlColumnClustered",
        title: { hasTitle: true, text: "Interest Coverage - EBIT / Interest Expense", font: { size: 11 } },
        series: [
          {
            index: 0,
            name: "Interest Coverage",
            categories: "[\"FY19A\",\"FY20A\",\"FY21A\",\"FY22A\",\"FY23F\",\"FY24F\"]",
            values: "[8.5,3.9,4.0,6.6,8.0,7.8]",
            fill: { colorRgb: "1B365D" },
            hasDataLabels: true,
            dataLabels: { showValue: true, position: "xlLabelPositionOutsideEnd", font: { size: 8 } },
          },
        ],
        axes: [
          { axisType: "xlValue", axisGroup: "xlPrimary", enabled: false, hasMajorGridlines: false, hasMinorGridlines: false, axisLine: { isVisible: false }, tickLabelFont: { colorRgb: "FFFFFF", size: 1 } },
          { axisType: "xlCategory", axisGroup: "xlPrimary", hasTitle: false, hasMajorGridlines: false, hasMinorGridlines: false, tickLabelFont: { size: 8 }, axisLine: { isVisible: false } },
        ],
        position: { left: col1X, top: row2Y, width: cellW, height: cellH },
      },
    },
    // Covenant Testing chart (bottom-right)
    {
      id: "chart_demo_covenant",
      action: "create",
      left: col2X,
      top: row2Y,
      width: cellW,
      height: cellH,
      chart: {
        type: "Chart",
        chartType: "xlColumnClustered",
        title: { hasTitle: true, text: "Financial Debt / EBITDA", font: { size: 11 } },
        series: [
          {
            index: 0,
            name: "Debt/EBITDA",
            categories: "[\"FY21A\",\"FY22A\",\"FY23F\",\"FY24F\"]",
            values: "[1.2,1.1,1.2,0.6]",
            fill: { colorRgb: "5B9BD5" },
            hasDataLabels: true,
            dataLabels: { showValue: true, position: "xlLabelPositionOutsideEnd", font: { size: 8 } },
          },
        ],
        axes: [
          { axisType: "xlValue", axisGroup: "xlPrimary", enabled: false, hasMajorGridlines: false, hasMinorGridlines: false, axisLine: { isVisible: false }, tickLabelFont: { colorRgb: "FFFFFF", size: 1 } },
          { axisType: "xlCategory", axisGroup: "xlPrimary", hasTitle: false, hasMajorGridlines: false, hasMinorGridlines: false, tickLabelFont: { size: 8 }, axisLine: { isVisible: false } },
        ],
        position: { left: col2X, top: row2Y, width: cellW, height: cellH },
      },
    },
  ];

  const modification: PresentationModification = {
    slides: [{ index: newSlideIndex, id: newSlideId, shapes }],
  };

  await applyModifications(modification, log);
  log(`✅ Multi-chart dashboard created with ${shapes.length} shapes (3 native charts)`);
}

/**
 * Render the temporarily-inserted reference slides to base64 PNGs (best-effort,
 * before they are stripped). Gives the deck-build agent a visual of each
 * reference slide to pair with its serialized shape JSON.
 */
async function renderReferenceSlideImages(
  refSlides: Array<{ id: string; index: number }>,
  options: {
    imageHeight?: number;
    maxImages?: number;
    imageSlideIndexes?: number[];
    presentationRef?: FactSetPresentationRef | null;
  },
  log: Logger
): Promise<Array<string | undefined>> {
  const max = options.maxImages ?? refSlides.length;
  const height = options.imageHeight ?? 1080;
  const images = new Array<string | undefined>(refSlides.length).fill(undefined);
  // An explicit index list renders exactly those slides (curated brand
  // references); otherwise render the first `maxImages` in deck order.
  const targets = options.imageSlideIndexes
    ? options.imageSlideIndexes.filter((index) => index >= 0 && index < refSlides.length)
    : Array.from({ length: Math.min(refSlides.length, Math.max(0, max)) }, (_, i) => i);
  for (const index of targets) {
    const slide = refSlides[index];
    try {
      const image = await exportSlideAsImage(slide.index, {
        targetSlideId: slide.id,
        height,
        presentationRef: options.presentationRef,
      });
      if (image) images[index] = image;
    } catch (error) {
      log(
        `⚠️ Could not render reference slide image ${slide.id}: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }
  return images;
}

async function snapshotSelectedSlideIdViaIntents(
  log: Logger,
  presentationRef?: FactSetPresentationRef | null
): Promise<string | null> {
  try {
    const selected = await getSelectedSlideRefViaIntents(log, {
      ...(presentationRef ? { presentationRef } : {}),
    });
    const slideId = typeof selected?.id === "string" ? selected.id.trim() : "";
    return slideId || null;
  } catch (error) {
    log(
      `⚠️ Could not snapshot selected slide before reference insert: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
    return null;
  }
}

async function restoreSelectedSlideIdViaIntents(
  slideId: string | null,
  log: Logger,
  presentationRef?: FactSetPresentationRef | null
): Promise<void> {
  if (!slideId) return;
  try {
    await setSelectedSlidesViaIntents({
      slideIds: [slideId],
      log,
      ...(presentationRef ? { presentationRef } : {}),
    });
  } catch (error) {
    log(
      `⚠️ Could not restore selected slide ${slideId} after reference cleanup: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }
}

async function deleteTempReferenceSlidesViaIntents(
  slideIds: Iterable<string>,
  log: Logger,
  presentationRef?: FactSetPresentationRef | null
): Promise<void> {
  const tempSlideIds = Array.from(new Set(slideIds)).filter((id) => id.trim().length > 0);
  if (tempSlideIds.length === 0) return;
  log(`🧹 Removing temporary slides...`);
  try {
    await deleteSlidesViaIntents(tempSlideIds, {
      log,
      ...(presentationRef ? { presentationRef } : {}),
    });
  } catch {
    for (const slideId of tempSlideIds) {
      log(`⚠️ Could not remove temp slide ${slideId}`);
    }
  }
}

async function deleteOfficeJsSlidesById(slideIds: readonly string[], log: Logger): Promise<void> {
  for (const slideId of slideIds) {
    try {
      await guardedPowerPointRun(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items/id");
        await context.sync();
        const target = slides.items.find((slide) => slide.id === slideId);
        if (target) {
          target.delete();
          await context.sync();
        }
      });
    } catch {
      log(`⚠️ Could not remove temp slide ${slideId}`);
    }
  }
}

/**
 * Parses a .pptx given as base64 by temporarily inserting its slides into the
 * target presentation, extracting all shape data, then cleaning up. Temps are
 * inserted after the last existing slide (or at the host default if the deck
 * is empty) and deleted by inserted id even if extract or render throws. Host
 * selection is snapshotted before insert and restored after cleanup so callers
 * that key off the selected slide (Create New Slides `insertAfterCurrent`)
 * still see the user's slide. When `options.renderImages` is set, each
 * reference slide is also rendered to a base64 PNG (before stripping) and
 * returned as `referenceImages`, aligned by index to the returned slides.
 */
export async function parsePptxBase64AsReference(
  base64: string,
  log: Logger = defaultLogger,
  options?: {
    renderImages?: boolean;
    imageHeight?: number;
    maxImages?: number;
    /** 0-based reference-slide indexes to render, overriding `maxImages`. */
    imageSlideIndexes?: number[];
    presentationRef?: FactSetPresentationRef | null;
  }
): Promise<PresentationContent & { referenceImages?: Array<string | undefined> }> {
  if (shouldUseFactSetBridge()) {
    const presentationRef = options?.presentationRef ?? undefined;
    const existingSlides = await listSlidesViaIntents(log, {
      forceRefresh: true,
      ...(presentationRef ? { presentationRef } : {}),
    });
    const existingSlideIds = new Set(existingSlides.map((slide) => slide.slideId));
    const lastExistingSlideId = existingSlides[existingSlides.length - 1]?.slideId;
    const originalSelectedSlideId = await snapshotSelectedSlideIdViaIntents(log, presentationRef);
    const tempSlideIds = new Set<string>();

    try {
      log(`📥 Inserting reference slides temporarily...`);
      const insertedSlides = await insertSlidesFromBase64ViaIntents({
        base64Slides: base64,
        log,
        ...(lastExistingSlideId ? { targetSlideId: lastExistingSlideId } : {}),
        ...(presentationRef ? { presentationRef } : {}),
      });
      for (const slide of insertedSlides) {
        if (slide.insertedSlideId) tempSlideIds.add(slide.insertedSlideId);
      }

      // Wave 2 site 15: ask the host to scope the extract to just the slides
      // we inserted (rather than the legacy full-deck extract + JS-side
      // filter). Full per-shape detail is preserved — reference decks need
      // it for downstream parsing. The defensive `.filter` below catches a
      // hypothetical host regression where the response includes slides we
      // did not request.
      const fullContent = await extractPresentationContent(log, {
        forceRefresh: true,
        ...(presentationRef ? { presentationRef } : {}),
        filters: {
          slides: { ids: Array.from(tempSlideIds) },
          pageSetup: true,
        },
      });

      const refSlides = fullContent.slides.filter(
        (slide) => tempSlideIds.has(slide.id) || !existingSlideIds.has(slide.id)
      );
      for (const slide of refSlides) {
        tempSlideIds.add(slide.id);
      }
      log(`📋 Extracted ${refSlides.length} reference slides`);

      const referenceImages = options?.renderImages
        ? await renderReferenceSlideImages(refSlides, options, log)
        : undefined;

      return {
        slides: refSlides.map((s, i) => ({ ...s, index: i })),
        selectedSlideIndex: 0,
        slideWidth: fullContent.slideWidth,
        slideHeight: fullContent.slideHeight,
        ...(referenceImages ? { referenceImages } : {}),
      };
    } finally {
      await deleteTempReferenceSlidesViaIntents(tempSlideIds, log, presentationRef);
      await restoreSelectedSlideIdViaIntents(originalSelectedSlideId, log, presentationRef);
    }
  }

  // getSelectedSlides is read in an isolated context so a SharePoint /
  // PowerPoint Online failure never poisons the run that lists slides.items.
  const originalSelectedSlideId = await readSelectedSlideIdInIsolatedContext(
    "Could not snapshot selected slide before reference insert"
  );
  let existingSlideIds: string[] = [];
  await guardedPowerPointRun(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items/id");
    await context.sync();
    existingSlideIds = slides.items.map((s) => s.id);
  });

  const existingSlideIdSet = new Set(existingSlideIds);
  let officeJsInserted = false;
  const officeJsTempIds = new Set<string>();

  try {
    log(`📥 Inserting reference slides temporarily...`);
    await guardedPowerPointRun(async (context) => {
      context.presentation.insertSlidesFromBase64(base64, {
        formatting: "KeepSourceFormatting" as PowerPoint.InsertSlideFormatting,
      });
      await context.sync();
    });
    officeJsInserted = true;

    const fullContent = await extractPresentationContent(log, { forceRefresh: true });

    const refSlides = fullContent.slides.filter(
      (slide) => !existingSlideIdSet.has(slide.id)
    );
    for (const slide of refSlides) {
      officeJsTempIds.add(slide.id);
    }
    log(`📋 Extracted ${refSlides.length} reference slides`);

    const referenceImages = options?.renderImages
      ? await renderReferenceSlideImages(refSlides, options, log)
      : undefined;

    return {
      slides: refSlides.map((s, i) => ({ ...s, index: i })),
      selectedSlideIndex: 0,
      slideWidth: fullContent.slideWidth,
      slideHeight: fullContent.slideHeight,
      ...(referenceImages ? { referenceImages } : {}),
    };
  } finally {
    if (officeJsInserted) {
      let idsToDelete = Array.from(officeJsTempIds);
      if (idsToDelete.length === 0) {
        try {
          idsToDelete = await guardedPowerPointRun(async (context) => {
            const slides = context.presentation.slides;
            slides.load("items/id");
            await context.sync();
            return slides.items
              .map((slide) => slide.id)
              .filter((id) => !existingSlideIdSet.has(id));
          }, [] as string[]);
        } catch {
          idsToDelete = [];
        }
      }
      if (idsToDelete.length > 0) {
        log(`🧹 Removing temporary slides...`);
        await deleteOfficeJsSlidesById(idsToDelete, log);
      }
    }
    if (originalSelectedSlideId) {
      try {
        await goToSlideById(originalSelectedSlideId);
      } catch (error) {
        log(
          `⚠️ Could not restore selected slide ${originalSelectedSlideId} after reference cleanup: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }
    }
  }
}

/**
 * Parses an uploaded .pptx File by reading it as base64 first.
 */
export async function parsePptxFileAsReference(
  file: File,
  log: Logger = defaultLogger
): Promise<PresentationContent> {
  log(`📄 Reading reference file: ${file.name} (${(file.size / 1024).toFixed(0)} KB)`);

  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.split(",")[1]);
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

  return parsePptxBase64AsReference(base64, log);
}

/**
 * Fetch the bundled Citi.pptx template, insert its slides (which imports the
 * Citi slide master + layouts into the active presentation), then delete the
 * inserted content slides.  The master persists after deletion.
 *
 * Returns true if the import succeeded.
 */
export async function importCitiTemplateMaster(
  log: Logger = defaultLogger
): Promise<boolean> {
  if (!shouldUseFactSetBridge()) {
    log("⚠️ importCitiTemplateMaster requires the FactSet COM bridge");
    return false;
  }
  try {
    log("📄 Creating blank Citi-branded slide...");
    const slideId = await createBlankSlideAtEndViaIntents(log);
    if (!slideId) {
      log("⚠️ Could not create blank slide");
      return false;
    }
    const slideIndex = await resolveSlideIndexByIdViaIntents(slideId, log);
    if (slideIndex === null) {
      log("⚠️ Could not resolve slide index");
      return false;
    }

    // Stamp Citi master elements onto the blank slide using centralized positions
    const citiMasterShapes: ShapeModification[] = [
      {
        id: "citi_accent_bar",
        action: "create",
        geometricType: "rectangle",
        ...CITI_POSITIONS.accentBar,
        fillColor: `#${CITI_COLORS.citiBlue}`,
        fontSize: 8, fontFamily: CITI_THEME_COLORS.minorFont,
      },
      {
        id: "citi_title_ph",
        action: "create",
        text: "",
        ...CITI_POSITIONS.title,
        fontSize: 22, fontFamily: CITI_THEME_COLORS.majorFont,
        textColor: `#${CITI_COLORS.citiBlue}`,
        verticalAlignment: "bottom",
      },
      {
        id: "citi_subtitle_ph",
        action: "create",
        text: "",
        ...CITI_POSITIONS.pageMessage,
        fontSize: 14, fontFamily: CITI_THEME_COLORS.minorFont,
        textColor: `#${CITI_COLORS.citiInk}`, bold: true,
      },
      {
        id: "citi_footer_ph",
        action: "create",
        text: "",
        ...CITI_POSITIONS.footer,
        fontSize: 8, fontFamily: "Citi Sans Condensed",
        textColor: `#${CITI_COLORS.citiInk}`,
        verticalAlignment: "bottom",
      },
    ];

    log("🎨 Applying Citi brand elements to slide...");
    await applyModificationsViaIntents(
      {
        slides: [{
          id: slideId,
          index: slideIndex,
          action: "modify",
          shapes: citiMasterShapes,
        }],
      },
      log
    );

    await goToSlideById(slideId);
    log("✅ Citi-branded blank slide created");
    return true;
  } catch (err) {
    log(`⚠️ importCitiTemplateMaster failed: ${err instanceof Error ? err.message : String(err)}`);
    return false;
  }
}

/**
 * Dev test: create one slide per Citi snippet type to visually verify rendering.
 */
export async function testCitiSnippets(
  log: Logger = defaultLogger
): Promise<void> {
  let slideWidth = inferredSlideWidth || DEFAULT_SLIDE_WIDTH;
  let slideHeight = inferredSlideHeight || DEFAULT_SLIDE_HEIGHT;
  if (shouldUseFactSetBridge()) {
    const bounds = await getResolvedSlideBoundsViaIntents(log);
    slideWidth = bounds.slideWidth;
    slideHeight = bounds.slideHeight;
  }

  const citiDemos: Snippet[] = [
    {
      type: "citiTextTable",
      title: "Peer comparison — key financial metrics",
      fontSize: 11,
      items: [
        { label: "Metric", description: "Company A|Company B|Company C|Company D" },
        { label: "Revenue (US$bn)", description: "12.4|8.9|21.1|5.6" },
        { label: "EBITDA margin", description: "32%|28%|35%|22%" },
        { label: "Net income (US$bn)", description: "3.1|1.8|5.4|0.9" },
        { label: "P/E ratio", description: "18.2x|22.1x|15.8x|25.4x", bold: true },
        { label: "Dividend yield", description: "2.1%|1.5%|2.8%|0.8%" },
        { label: "ROE", description: "15%|12%|19%|8%" },
      ],
    },
    {
      type: "citiQuadrant",
      title: "Market positioning overview",
      items: [
        { label: "Revenue breakdown", bullets: ["Americas: 45% of total", "EMEA: 30% of total", "APAC: 25% of total"] },
        { label: "Competitive landscape", bullets: ["#2 market share globally", "Gaining vs. top competitor", "Strong pipeline in growth segments"] },
        { label: "Growth drivers", bullets: ["Digital transformation spend", "Cross-sell into existing base", "New market entry (India, SE Asia)"] },
        { label: "Key metrics", bullets: ["LTM Revenue: $12.4B", "EBITDA margin: 32%", "Net debt / EBITDA: 1.8x"] },
      ],
    },
    {
      type: "citiSplitContent",
      title: "Valuation methodology",
      items: [
        { label: "Approach", color: "#EAE0D3", bullets: ["DCF with 10-year explicit forecast", "WACC of 8.5% based on CAPM", "Terminal growth rate of 2.5%", "Cross-checked with trading comps", "Precedent transactions for control premium"] },
        { label: "Sum-of-the-parts", description: "US$ millions", bullets: ["Market value (US$)", "Asset value (US$)"] },
        { label: "Core banking", description: "4,200|5,100" },
        { label: "Wealth management", description: "2,800|3,400" },
        { label: "Capital markets", description: "1,500|1,900" },
        { label: "Corporate / other", description: "(350)|(350)" },
        { label: "Enterprise value", description: "8,150|10,050", bold: true },
      ],
    },
    {
      type: "citiTombstone",
      title: "Selected M&A credentials",
      items: [
        { label: "Acme Corp\nAcquisition of Beta Inc.", description: "$2.4B|Jan 2024", color: "#255BE3", bold: true },
        { label: "Delta Holdings\nSale to Gamma Group", description: "$1.8B|Mar 2024", color: "#255BE3", bold: true },
        { label: "Omega Systems\nIPO", description: "$950M|Jun 2024", color: "#E6EBED" },
        { label: "Sigma Partners\nLeveraged buyout", description: "$3.1B|Sep 2023", color: "#EAE0D3" },
        { label: "Kappa Industries\nMerger with Lambda", description: "$4.5B|Nov 2023" },
        { label: "Zeta Corp\nMinority stake sale", description: "$620M|Dec 2023" },
        { label: "Alpha Finance\nDebt restructuring", description: "$1.2B|Feb 2024" },
        { label: "Theta Group\nJoint venture", description: "$800M|Apr 2024" },
        { label: "Iota Solutions\nStrategic investment", description: "$450M|May 2024" },
        { label: "Nu Technologies\nSeries D", description: "$300M|Jul 2024" },
        { label: "Pi Holdings\nDivestiture", description: "$1.6B|Aug 2024" },
        { label: "Rho Capital\nSecondary offering", description: "$2.0B|Oct 2024" },
      ],
    },
    {
      type: "citiMatrix",
      title: "Competitive positioning matrix",
      items: [
        { label: "", description: "Market share|Revenue growth|Margin profile" },
        { label: "Company A", description: "Leading position (#1)\n• 35% market share\n• Growing in all regions|Strong growth\n• 18% YoY\n• Accelerating|Expanding\n• 32% EBITDA\n• +200bps vs prior year" },
        { label: "Company B", description: "Challenger (#2)\n• 22% market share\n• Stable|Moderate growth\n• 12% YoY\n• Decelerating|Stable\n• 28% EBITDA\n• Flat vs prior year", bold: true },
        { label: "Company C", description: "Niche player (#5)\n• 8% market share\n• Declining|Slow growth\n• 5% YoY\n• Below industry|Compressing\n• 18% EBITDA\n• -150bps vs prior year" },
      ],
    },
  ];

  for (const demo of citiDemos) {
    log(`🏦 Creating ${demo.type} slide...`);

    let newSlideId: string | undefined;
    let newSlideIndex = -1;

    if (shouldUseFactSetBridge()) {
      const createdSlideId = await createBlankSlideAtEndViaIntents(log);
      if (!createdSlideId) {
        log(`⚠️ Could not create slide for ${demo.type}`);
        continue;
      }
      newSlideId = createdSlideId;
      const resolvedIndex = await resolveSlideIndexByIdViaIntents(createdSlideId, log);
      if (resolvedIndex === null) {
        log(`⚠️ Could not resolve slide index for ${demo.type}`);
        continue;
      }
      newSlideIndex = resolvedIndex;
    } else {
      await guardedPowerPointRun(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items/id");
        await context.sync();
        const beforeCount = slides.items.length;
        slides.add();
        await context.sync();
        slides.load("items/id");
        await context.sync();
        const newSlide = slides.items[beforeCount];
        newSlideId = newSlide?.id;
        newSlideIndex = beforeCount;
      });
    }

    if (!newSlideId || newSlideIndex < 0) {
      log(`⚠️ Could not create slide for ${demo.type}`);
      continue;
    }

    const shapes = generateCompositeLayoutShapes(demo, slideWidth, slideHeight, "#255BE3", {
      useTextBoxesForStatGrid: shouldUseFactSetBridge(),
      styleProfile: CITI_SNIPPET_STYLE_PROFILE,
    });

    const modification: PresentationModification = {
      slides: [{ index: newSlideIndex, id: newSlideId, shapes }],
    };

    await applyModifications(modification, log);
    log(`✅ ${demo.type} slide created with ${shapes.length} shapes`);
  }

  log(`🏦 All ${citiDemos.length} Citi snippet demos created!`);
}

/**
 * Extract the currently selected slide's shapes as a JSON string suitable for
 * copy-pasting to build new snippets.
 */
export async function extractCurrentSlideJson(
  log: Logger = defaultLogger
): Promise<string> {
  log("🔍 Extracting current slide content...");
  const content = await extractPresentationContent(log, { forceRefresh: true });
  const slide = content.slides[content.selectedSlideIndex];
  if (!slide) {
    throw new Error(`No slide found at index ${content.selectedSlideIndex}`);
  }

  const result = {
    slideIndex: slide.index,
    slideId: slide.id,
    layout: slide.layout ?? null,
    slideWidth: content.slideWidth ?? null,
    slideHeight: content.slideHeight ?? null,
    shapeCount: slide.shapes.length,
    shapes: slide.shapes.map((s) => {
      const obj: Record<string, unknown> = {
        id: s.id,
        name: s.name,
        type: s.type,
        left: s.left,
        top: s.top,
        width: s.width,
        height: s.height,
      };
      if (s.placeholderType) obj.placeholderType = s.placeholderType;
      if (s.geometricType) obj.geometricType = s.geometricType;
      if (s.text != null) obj.text = s.text;
      if (s.fontSize) obj.fontSize = s.fontSize;
      if (s.fontFamily) obj.fontFamily = s.fontFamily;
      if (s.textColor) obj.textColor = s.textColor;
      if (s.fillColor) obj.fillColor = s.fillColor;
      if (s.lineColor) obj.lineColor = s.lineColor;
      if (s.lineWidth) obj.lineWidth = s.lineWidth;
      if (s.bold) obj.bold = s.bold;
      if (s.italic) obj.italic = s.italic;
      if (s.horizontalAlignment) obj.horizontalAlignment = s.horizontalAlignment;
      if (s.verticalAlignment) obj.verticalAlignment = s.verticalAlignment;
      if (s.paragraphs) obj.paragraphs = s.paragraphs;
      if (s.tableData) {
        obj.tableData = {
          rowCount: s.tableData.rowCount,
          colCount: s.tableData.colCount,
          rows: s.tableData.rows,
          columnWidths: s.tableData.columnWidths,
          rowHeights: s.tableData.rowHeights,
          mergedAreas: s.tableData.mergedAreas,
          styleSnapshot: s.tableData.styleSnapshot,
          semanticHints: s.tableData.semanticHints,
        };
      }
      return obj;
    }),
  };

  log(`✅ Extracted slide ${slide.index + 1}: ${result.shapeCount} shapes`);
  return JSON.stringify(result, null, 2);
}

/**
 * Extract the raw design/master metadata (color scheme, font scheme,
 * custom layouts with placeholder shapes) as JSON for inspection.
 */
export async function extractDesignJson(
  log: Logger = defaultLogger
): Promise<string> {
  log("🔍 Extracting design/master metadata...");
  if (!shouldUseFactSetBridge()) {
    throw new Error("Design inspector requires the FactSet COM bridge");
  }
  const json = await extractRawDesignJsonViaIntents(log);
  log("✅ Design metadata extracted");
  return json;
}

/**
 * Extract background information from the current slide + master/layout
 * background flags and shapes. Combines the current slide's background-like
 * shapes with the design inspector's layout background metadata.
 */
export async function extractBackgroundJson(
  log: Logger = defaultLogger
): Promise<string> {
  log("🔍 Extracting background information...");

  const content = await extractPresentationContent(log, { forceRefresh: true });
  const slide = content.slides[content.selectedSlideIndex];
  if (!slide) {
    throw new Error(`No slide found at index ${content.selectedSlideIndex}`);
  }

  const slideW = content.slideWidth ?? 960;
  const slideH = content.slideHeight ?? 540;

  const bgShapes = slide.shapes.filter((s) => {
    const coverageW = s.width / slideW;
    const coverageH = s.height / slideH;
    const isLarge = coverageW > 0.7 && coverageH > 0.5;
    const isNamedBg =
      (s.name ?? "").toLowerCase().includes("background") ||
      (s.name ?? "").toLowerCase().includes("bg");
    return isLarge || isNamedBg;
  });

  const currentSlide: Record<string, unknown> = {
    slideIndex: slide.index,
    slideId: slide.id,
    layout: slide.layout ?? null,
    slideWidth: slideW,
    slideHeight: slideH,
    backgroundShapes: bgShapes.map((s) => ({
      id: s.id,
      name: s.name,
      type: s.type,
      left: s.left,
      top: s.top,
      width: s.width,
      height: s.height,
      fillColor: s.fillColor ?? null,
      lineColor: s.lineColor ?? null,
      geometricType: s.geometricType ?? null,
      text: s.text ?? null,
      coveragePercent: Math.round((s.width * s.height) / (slideW * slideH) * 100),
    })),
  };

  let masterBackgrounds: unknown = null;
  if (shouldUseFactSetBridge()) {
    try {
      const bgJson = await extractSlideBackgroundsViaIntents(log);
      masterBackgrounds = JSON.parse(bgJson);
    } catch (err) {
      log(`⚠️ Could not extract master backgrounds: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  const result = {
    currentSlide,
    masterBackgrounds,
  };

  log(`✅ Background info extracted (${bgShapes.length} background-like shapes on current slide)`);
  return JSON.stringify(result, null, 2);
}

/**
 * Open a brand "blank" template (e.g. assets/templates/Citi_blank.pptx) as a
 * brand new presentation via the FactSet `openPresentationFromBase64` intent.
 * This gives us the full brand master (backgrounds, all layouts) as the
 * starting point for deck creation.
 */
export async function openBrandBlankPresentation(
  assetPath: string,
  log: Logger = defaultLogger
): Promise<boolean> {
  if (!shouldUseFactSetBridge()) {
    log("⚠️ openBrandBlankPresentation requires the FactSet COM bridge");
    return false;
  }
  const fileName = assetPath.split("/").pop() || assetPath;
  try {
    log(`📄 Fetching ${fileName} template...`);
    const resp = await fetch(assetPath);
    if (!resp.ok) {
      log(`⚠️ Could not fetch ${fileName}: ${resp.status}`);
      return false;
    }
    const buf = await resp.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    const base64 = btoa(binary);

    log(`📂 Opening ${fileName} as new presentation...`);
    await openPresentationFromBase64ViaIntents({ pptxBase64: base64, log });

    await new Promise((r) => setTimeout(r, 2000));
    log(`✅ ${fileName} opened with full master`);
    return true;
  } catch (err) {
    log(`⚠️ openBrandBlankPresentation failed: ${err instanceof Error ? err.message : String(err)}`);
    return false;
  }
}

/**
 * Backward-compatible Citi-only wrapper. Prefer `openBrandBlankPresentation`.
 */
export async function openCitiBlankPresentation(
  log: Logger = defaultLogger
): Promise<boolean> {
  return openBrandBlankPresentation("assets/templates/Citi_blank.pptx", log);
}
